<template>
    <div
        class="workforce-plan-page"
        :style="displayColorVariables"
        :class="{
            'workforce-plan-page--filtered': hasActivePlanFilters,
            'workforce-plan-page--mobile-filters-open': mobileFiltersOpen
        }"
    >
        <header class="workforce-plan-header">
            <div>
                <div class="workforce-plan-title-row">
                    <h1>План сотрудников</h1>
                    <span
                        class="workforce-plan-sync"
                        :class="`workforce-plan-sync--${syncStatus}`"
                    >
                        <i class="fas fa-circle" aria-hidden="true"></i>
                        {{ syncStatusLabel }}
                    </span>
                </div>
                <p>Один план для календаря и будущего среза доски</p>
            </div>
            <div class="workforce-plan-header__actions">
                <collaboration-participants scope="plan" :ready="syncStatus === 'connected' && permissions.can_view && !accessDenied"
                                           :followable-user-ids="collaborationUserIds" @select="followCollaborator" />
                <b-button
                    v-if="permissions.can_edit && !accessDenied"
                    size="sm"
                    variant="outline-secondary"
                    :disabled="loading"
                    @click="openPlanningNames()"
                >
                    <i class="fas fa-sliders-h mr-1" aria-hidden="true"></i>
                    Цвета и названия
                </b-button>
                <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="openPlannedObjectsManager"
                >
                    <i class="fas fa-map-signs mr-1" aria-hidden="true"></i>
                    Плановые объекты
                </b-button>
                <b-button-group size="sm" aria-label="Вид планирования">
                    <b-button
                        variant="outline-primary"
                        :to="boardRoute"
                    >
                        <i class="fas fa-columns mr-1" aria-hidden="true"></i>
                        Доска
                    </b-button>
                    <b-button variant="primary" active>
                        <i class="far fa-calendar-alt mr-1" aria-hidden="true"></i>
                        План
                    </b-button>
                </b-button-group>
                <span class="workforce-create-employee-slot">
                    <b-button
                        v-if="permissions.can_manage_employees"
                        size="sm"
                        variant="success"
                        class="w-100"
                        @click="createEmployeeVisible = true"
                    >
                        <i class="fas fa-user-plus mr-1" aria-hidden="true"></i>
                        Создать сотрудника
                    </b-button>
                </span>
            </div>
        </header>

        <nav class="workforce-plan-period-toolbar" aria-label="Период плана">
            <b-button-group size="sm" aria-label="Режим периода">
                <b-button :variant="periodMode === 'rolling' ? 'primary' : 'outline-primary'"
                          :aria-pressed="periodMode === 'rolling' ? 'true' : 'false'"
                          :disabled="periodNavigationDisabled" @click="setPeriodMode('rolling')">Скользящий</b-button>
                <b-button :variant="periodMode === 'year' ? 'primary' : 'outline-primary'"
                          :aria-pressed="periodMode === 'year' ? 'true' : 'false'"
                          :disabled="periodNavigationDisabled" @click="setPeriodMode('year')">По годам</b-button>
            </b-button-group>
            <div v-if="periodMode === 'rolling'" class="workforce-plan-period-range">
                <b-button size="sm" variant="outline-secondary" :disabled="periodNavigationDisabled"
                          title="На 13 недель назад" aria-label="На 13 недель назад" @click="shiftPlanPeriod(-13)">
                    <i class="fas fa-chevron-left" aria-hidden="true"></i> 13 нед.
                </b-button>
                <span>{{ periodRangeLabel }}</span>
                <b-button size="sm" variant="outline-secondary" :disabled="periodNavigationDisabled"
                          title="На 13 недель вперёд" aria-label="На 13 недель вперёд" @click="shiftPlanPeriod(13)">
                    13 нед. <i class="fas fa-chevron-right" aria-hidden="true"></i>
                </b-button>
            </div>
            <b-form-select v-else :value="year" size="sm" :options="yearOptions"
                           class="workforce-plan-year" aria-label="Год плана"
                           :disabled="periodNavigationDisabled" @change="setPlanYear" />
        </nav>

        <section class="workforce-plan-toolbar" aria-label="Настройки плана">
            <div class="workforce-plan-mobile-tools">
                <button
                    type="button"
                    class="workforce-plan-mobile-tool"
                    :class="{'is-active': mobileFiltersOpen || hasActivePlanFilters}"
                    :aria-expanded="mobileFiltersOpen ? 'true' : 'false'"
                    @click="mobileFiltersOpen = !mobileFiltersOpen"
                >
                    <i class="fas fa-filter" aria-hidden="true"></i>
                    Фильтры
                    <b v-if="activePlanFilterChips.length">{{ activePlanFilterChips.length }}</b>
                </button>
                <span>{{ filteredEmployees.length }} из {{ employees.length }}</span>
                <button
                    type="button"
                    class="workforce-plan-mobile-tool"
                    :disabled="periodNavigationDisabled" @click="goToToday"
                >
                    <i class="far fa-calendar-alt" aria-hidden="true"></i>
                    Сегодня
                </button>
            </div>
            <b-form-input
                v-model.trim="query"
                size="sm"
                type="search"
                class="workforce-plan-search"
                placeholder="Найти сотрудника"
                aria-label="Найти сотрудника"
            ></b-form-input>
            <b-form-select
                v-model="citizenshipFilter"
                size="sm"
                :options="citizenshipFilterOptions"
                class="workforce-plan-citizenship"
                aria-label="Фильтр по гражданству"
            ></b-form-select>
            <b-form-select
                v-model="hiringFormFilter"
                size="sm"
                :options="hiringFormOptions"
                class="workforce-plan-citizenship"
                aria-label="Фильтр по форме найма"
            ></b-form-select>
            <employee-multi-filter :skills.sync="skillFilter" :positions.sync="positionFilter"
                                   :itr.sync="itrFilter"
                                   :skill-options="skillFilterOptions" :position-options="positionFilterOptions"
                                   :skill-descriptions="skillDescriptions"
                                   class="workforce-plan-multi-filter" />
            <div class="workforce-plan-assignment-filter">
                <v-select
                    v-model="assignmentFilter"
                    :options="assignmentFilterOptions"
                    label="text"
                    :reduce="option => option.value"
                    :selectable="option => !option.disabled"
                    :clearable="false"
                    :searchable="true"
                    class="workforce-plan-assignment-select"
                    aria-label="Фильтр по назначению"
                    :title="assignmentFilterPeriodTitle"
                    @keydown.alt.left.capture.stop.prevent="assignmentFilterScope = 'all'"
                    @keydown.alt.right.capture.stop.prevent="assignmentFilterScope = 'future'"
                >
                    <template #list-header>
                        <li role="presentation" class="workforce-plan-assignment-scope-header">
                            <div role="group" aria-label="Период отбора назначений" class="workforce-plan-assignment-scope">
                                <button type="button" :aria-pressed="assignmentFilterScope === 'all'"
                                        aria-keyshortcuts="Alt+ArrowLeft" tabindex="-1"
                                        @click.stop.prevent="assignmentFilterScope = 'all'">
                                    {{ periodMode === 'year' ? 'Весь год' : 'Весь период' }}
                                </button>
                                <button type="button" :aria-pressed="assignmentFilterScope === 'future'"
                                        aria-keyshortcuts="Alt+ArrowRight" tabindex="-1"
                                        @click.stop.prevent="assignmentFilterScope = 'future'">
                                    С текущей недели
                                </button>
                            </div>
                        </li>
                    </template>
                </v-select>
                <b-button
                    v-if="assignmentFilter !== 'all'"
                    size="sm"
                    variant="outline-secondary"
                    title="Сбросить фильтр назначения"
                    aria-label="Сбросить фильтр назначения"
                    @click="assignmentFilter = 'all'"
                >
                    <i class="fas fa-times" aria-hidden="true"></i>
                </b-button>
            </div>
            <employee-workforce-display-controls
                class="workforce-plan-zoom"
                :fit-label="periodMode === 'year' ? 'Весь год' : 'Весь период'"
                :fit-title="periodMode === 'year' ? 'Уместить год по ширине' : 'Уместить период по ширине'"
                :zoom="zoom" :numeric-zoom="numericZoom" :contrast="contrast" :user="currentUser"
                :ready="displayReady && !loading && !accessDenied && !errorMessage"
                :visible="!isMobileViewport || mobileFiltersOpen"
                :blocked="Boolean(inspectorEmployee) || quickEditorOpen || currentFilterOpen || rowCopyHintVisible
                    || mobileContextHintVisible || planningNamesVisible || plannedObjectDraftVisible
                    || plannedObjectsManagerVisible || createEmployeeVisible"
                @zoom="setPlanZoom" @fit="toggleFitZoom" @contrast="setPlanContrast"
            />
            <b-button
                size="sm"
                variant="outline-secondary"
                class="workforce-plan-today"
                title="Перейти к текущей неделе"
                :disabled="periodNavigationDisabled"
                @click="goToToday"
            >
                Сегодня
            </b-button>
            <employee-name-format-switcher
                class="workforce-plan-name-switcher"
                :value="employeeNameFormatState.value"
                copy-actions
                :copy-with-details.sync="copyNamesWithDetails"
                :selected-copy-count="selectedFullRowEmployees.length"
                :visible-copy-count="filteredEmployees.length"
                @input="setEmployeeNameFormat"
                @copy-selected="copySelectedEmployeeNames"
                @copy-visible="copyVisibleEmployeeNames"
            ></employee-name-format-switcher>
            <span class="workforce-plan-count">
                {{ filteredEmployees.length }} из {{ employees.length }}
            </span>
        </section>

        <section
            v-if="hasActivePlanFilters"
            class="workforce-plan-active-filters"
            aria-label="Активные фильтры плана"
        >
            <span class="workforce-plan-active-filters__label">
                <i class="fas fa-filter" aria-hidden="true"></i>
                Включены фильтры:
            </span>
            <button
                v-for="chip in activePlanFilterChips"
                :key="chip.key"
                type="button"
                class="workforce-plan-active-filter"
                :class="`workforce-plan-active-filter--${chip.variant}`"
                :title="chip.title || `Снять фильтр: ${chip.text}`"
                @click="clearPlanFilterChip(chip.key)"
            >
                <i class="fas fa-check" aria-hidden="true"></i>
                <span>{{ chip.text }}</span>
                <i class="fas fa-times" aria-hidden="true"></i>
            </button>
            <button
                type="button"
                class="workforce-plan-active-filters__reset"
                @click="clearAllPlanFilters"
            >
                Сбросить все
            </button>
        </section>

        <b-alert v-if="accessDenied" show variant="warning">
            У вас нет глобального права просмотра плана сотрудников.
        </b-alert>
        <b-alert v-else-if="errorMessage" show variant="danger" dismissible>
            {{ errorMessage }}
            <b-button v-if="!planWindowReady" size="sm" variant="outline-danger"
                      :disabled="loading" @click="retryPlanPeriod">Повторить загрузку</b-button>
        </b-alert>
        <b-modal
            v-model="plannedObjectDraftVisible"
            :title="plannedObjectDraftTitle"
            hide-footer
            centered
        >
            <b-form-group label="Код в плане">
                <b-form-input
                    v-model.trim="plannedObjectDraft.planning_code"
                    maxlength="16"
                    autocomplete="off"
                    @input="updatePlannedObjectDraftConflict"
                ></b-form-input>
            </b-form-group>
            <b-form-group label="Название">
                <b-form-input
                    v-model.trim="plannedObjectDraft.name"
                    maxlength="255"
                    autofocus
                ></b-form-input>
            </b-form-group>
            <b-form-group label="Комментарий">
                <AppTextarea
                    v-model.trim="plannedObjectDraft.comment"
                    maxlength="2000"
                    placeholder="Например: контракт ещё не заключён"
                ></AppTextarea>
            </b-form-group>
            <b-alert v-if="plannedObjectDraft.conflict_owner" show variant="warning">
                Код уже занят: <strong>{{ plannedObjectDraft.conflict_owner.name }}</strong>.
                <template v-if="plannedObjectDraftCanReassignCode">
                    Чтобы освободить код, переименуйте прежнюю цель.
                </template>
                <template v-else>
                    Выберите другой код. Переименовать прежнюю цель может пользователь
                    с правом настройки доски сотрудников.
                </template>
            </b-alert>
            <b-form-group
                v-if="plannedObjectDraft.conflict_owner && plannedObjectDraftCanReassignCode"
                label="Новый код прежней цели"
            >
                <b-form-input
                    v-model.trim="plannedObjectDraft.rename_conflict_to"
                    maxlength="16"
                ></b-form-input>
            </b-form-group>
            <b-alert v-if="plannedObjectDraftError" show variant="danger" class="py-2">
                {{ plannedObjectDraftError }}
            </b-alert>
            <div class="d-flex justify-content-end">
                <b-button
                    variant="outline-secondary"
                    class="mr-2"
                    :disabled="plannedObjectDraftSaving"
                    @click="plannedObjectDraftVisible = false"
                >
                    Отмена
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="plannedObjectDraftSaving || !plannedObjectDraftValid"
                    @click="acceptPlannedObjectDraft"
                >
                    <b-spinner v-if="plannedObjectDraftSaving" small class="mr-1"></b-spinner>
                    {{ plannedObjectDraftActionLabel }}
                </b-button>
            </div>
        </b-modal>

        <aside
            v-if="quickEditorOpen && hasSelection && (canEdit || arrivalActionAvailable || selectedArrivalConfirmation || selectedBaselinePresence || hasSelectedFullRows)"
            ref="quickEditor"
            class="workforce-plan-quick-editor"
            :class="{'workforce-plan-quick-editor--dragging': quickEditorDragging}"
            :style="quickEditorStyle"
            aria-label="Изменение выбранных ячеек"
            @mousedown.stop
            @click.stop
        >
            <header
                class="workforce-plan-quick-editor__header"
                title="Потяните, чтобы переместить окно"
                @mousedown.prevent="startQuickEditorDrag"
            >
                <div>
                    <span class="workforce-plan-quick-editor__drag-label">
                        <i class="fas fa-grip-lines" aria-hidden="true"></i>
                        <strong class="workforce-plan-quick-editor__selection-title">{{ selectionEmployeeRecords.length === 1
                            ? employeeFullName(selectionEmployeeRecords[0]) : selectionSummaryLabel }}</strong>
                    </span>
                    <small v-if="selectionEmployeeRecords.length === 1">{{ selectionSummaryLabel }}</small>
                    <small>{{ selectionPeriodLabel }}</small>
                </div>
                <button
                    type="button"
                    title="Закрыть без сохранения"
                    aria-label="Закрыть без сохранения"
                    @mousedown.stop
                    @click="closeQuickEditorFromButton"
                >
                    <i class="fas fa-times" aria-hidden="true"></i>
                </button>
            </header>

            <section
                v-if="hasSelectedFullRows"
                class="workforce-plan-row-copy"
            >
                <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    @click="copySelectedEmployeeNames"
                >
                    <i class="far fa-copy mr-1" aria-hidden="true"></i>
                    Скопировать ФИО — {{ selectedFullRowEmployees.length }}
                </button>
                <b-form-checkbox v-model="copyNamesWithDetails" size="sm">
                    Добавлять гражданство и ГПХ
                </b-form-checkbox>
                <div
                    v-if="rowCopyHintVisible"
                    class="workforce-plan-row-copy__hint"
                    role="status"
                >
                    <button
                        type="button"
                        aria-label="Больше не показывать подсказку"
                        @click="hideRowCopyHint(true)"
                    >×</button>
                    <template v-if="isMobileViewport">
                        Нажимайте номера строк, чтобы добавить или убрать сотрудников.
                    </template>
                    <template v-else>
                        <kbd>Ctrl</kbd> + клик — добавить строку.
                        <kbd>Ctrl</kbd> + <kbd>C</kbd> — скопировать ФИО.
                    </template>
                </div>
            </section>

            <div class="workforce-plan-quick-editor__current">
                Сейчас: <strong>{{ selectedCurrentStateLabel }}</strong>
            </div>

            <section
                v-if="arrivalActionAvailable || selectedArrivalConfirmation || selectedBaselinePresence"
                class="workforce-plan-arrival"
            >
                <template v-if="!arrivalDraft.open">
                    <button
                        v-if="selectedArrivalConfirmation || selectedBaselinePresence"
                        type="button"
                        class="workforce-plan-arrival__summary"
                        :aria-expanded="arrivalDetailsExpanded ? 'true' : 'false'"
                        aria-controls="workforce-arrival-details"
                        @click="toggleArrivalDetails"
                    >
                        <span v-if="selectedArrivalConfirmation">
                            <i class="fas fa-check-circle" aria-hidden="true"></i>
                            Прибытие: {{ formatDate(selectedArrivalConfirmation.arrival_date) }}
                        </span>
                        <span v-else>
                            <i class="fas fa-table" aria-hidden="true"></i>
                            Присутствие принято по табелю
                        </span>
                        <i :class="arrivalDetailsExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
                    </button>
                    <div v-show="arrivalDetailsExpanded" id="workforce-arrival-details">
                    <div
                        v-if="selectedArrivalConfirmation"
                        class="workforce-plan-arrival__confirmed"
                    >
                        <span>
                            <small v-if="selectedArrivalConfirmation.source === 'timesheet_hours'">Через ввод часов</small>
                            <small class="d-block workforce-plan-arrival__author">
                                Подтвердил: {{ selectedArrivalConfirmation.created_by_name || (selectedArrivalConfirmation.created_by ? 'Пользователь №' + selectedArrivalConfirmation.created_by : 'Автор не указан') }}
                            </small>
                            <small v-if="selectedArrivalConfirmation.created_at" class="d-block workforce-plan-arrival__author">
                                {{ formatArrivalTimestamp(selectedArrivalConfirmation.created_at) }}
                            </small>
                        </span>
                        <div v-if="arrivalActionAvailable">
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-primary"
                                @click="openArrivalDraft('correct')"
                            >
                                Исправить дату
                            </button>
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-danger"
                                :disabled="arrivalDraft.cancelling"
                                @click="cancelArrivalConfirmation"
                            >
                                <b-spinner
                                    v-if="arrivalDraft.cancelling"
                                    small
                                    class="mr-1"
                                ></b-spinner>
                                Отменить
                            </button>
                        </div>
                    </div>
                    <div
                        v-if="selectedBaselinePresence"
                        class="workforce-plan-arrival__confirmed"
                        role="status"
                    >
                        <span>
                            <small v-if="selectedArrivalConfirmation" class="d-block workforce-plan-arrival__author">Также принято по табелю</small>
                            <small class="d-block workforce-plan-arrival__author">
                                Часы за {{ formatDate(selectedBaselinePresence.starts_on) }} внёс/изменил:
                                {{ selectedBaselinePresence.source_author_name || (selectedBaselinePresence.source_author_id ? 'Пользователь №' + selectedBaselinePresence.source_author_id : 'Автор не указан') }}
                            </small>
                            <small v-if="selectedBaselinePresence.source_updated_at" class="d-block workforce-plan-arrival__author">
                                {{ formatArrivalTimestamp(selectedBaselinePresence.source_updated_at) }}
                            </small>
                            <small class="d-block workforce-plan-arrival__author">Снимок от {{ formatDate(selectedBaselinePresence.snapshot_date) }}. Точная дата прибытия по табелю не устанавливалась.</small>
                            <router-link
                                v-if="selectedBaselinePresence.timesheet_id"
                                class="d-block mt-1"
                                :to="{path: '/timesheets/' + selectedBaselinePresence.timesheet_id, query: {month: selectedBaselinePresence.starts_on.slice(0, 7)}}"
                                target="_blank"
                                rel="noopener"
                            >Открыть исходный табель</router-link>
                        </span>
                    </div>
                    </div>
                    <button
                        v-if="!selectedArrivalConfirmation && !selectedBaselinePresence && arrivalActionAvailable"
                        type="button"
                        class="workforce-plan-arrival__open"
                        @click="openArrivalDraft('create')"
                    >
                        <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                        Подтвердить прибытие на объект
                    </button>
                </template>
                <template v-else>
                    <div class="workforce-plan-arrival__title">
                        <strong>{{ arrivalTargetLabel }}</strong>
                        <button
                            type="button"
                            title="Закрыть подтверждение"
                            @click="resetArrivalDraft"
                        >
                            <i class="fas fa-times" aria-hidden="true"></i>
                        </button>
                    </div>
                    <small>Выберите точную дату фактического прибытия.</small>
                    <div class="workforce-plan-arrival__days">
                        <button
                            v-for="day in arrivalDateOptions"
                            :key="day.date"
                            type="button"
                            :class="{'is-active': arrivalDraft.arrival_date === day.date}"
                            :disabled="day.disabled || arrivalDraft.pending"
                            @click="selectArrivalDate(day.date)"
                        >
                            <span>{{ day.label }}</span>
                            <strong>{{ day.short_date }}</strong>
                        </button>
                    </div>
                    <button
                        type="button"
                        class="workforce-plan-arrival__other-toggle"
                        @click="arrivalDraft.other_date_open = !arrivalDraft.other_date_open"
                    >
                        <i class="far fa-calendar-alt" aria-hidden="true"></i>
                        Выбрать другую дату
                    </button>
                    <input
                        v-if="arrivalDraft.other_date_open"
                        v-model="arrivalDraft.arrival_date"
                        type="date"
                        :max="observedToday"
                        class="workforce-plan-arrival__other-date"
                        @change="clearArrivalWarnings"
                    >
                    <div
                        v-if="arrivalDraft.warning"
                        class="workforce-plan-arrival__warning"
                    >
                        <strong>Есть расхождения — проверьте перед записью.</strong>
                        <p v-if="arrivalDraft.warning.plan_mismatch">
                            На выбранную дату в плане:
                            <b>{{ arrivalDraft.warning.plan_mismatch.description }}</b>.
                        </p>
                        <p v-if="arrivalDraft.warning.fact_mismatch">
                            Фактически сотрудник числится:
                            <b>{{ arrivalDraft.warning.fact_mismatch.description }}</b>
                            с {{ formatDate(arrivalDraft.warning.fact_mismatch.effective_date) }}.
                        </p>
                        <p
                            v-if="arrivalDraft.warning.fact_mismatch && arrivalDraft.warning.fact_mismatch.next_event"
                        >
                            Следующее фактическое изменение:
                            <b>{{ arrivalDraft.warning.fact_mismatch.next_event.description }}</b>
                            с {{ formatDate(arrivalDraft.warning.fact_mismatch.next_event.effective_date) }}.
                        </p>
                    </div>
                    <div
                        v-if="arrivalDraft.planned_moves.length"
                        class="workforce-plan-arrival__moves"
                    >
                        <strong>Какое запланированное перемещение подтвердить?</strong>
                        <label
                            v-for="move in arrivalDraft.planned_moves"
                            :key="move.id"
                        >
                            <input
                                v-model.number="arrivalDraft.linked_planned_move_id"
                                type="radio"
                                :value="Number(move.id)"
                            >
                            {{ formatDate(move.planned_for) }}
                            <span v-if="move.note">— {{ move.note }}</span>
                        </label>
                    </div>
                    <div v-if="arrivalDraft.discrepancies.length" class="workforce-plan-arrival__warning">
                        <strong>Есть отметки раньше даты прибытия.</strong>
                        <div v-for="item in arrivalDraft.discrepancies" :key="item.key">
                            <div v-for="entry in item.affected_entries" :key="`${entry.line_id}:${entry.work_date}`">
                                {{ formatDate(entry.work_date) }} — {{ timesheetArrivalLabel(entry) }}
                            </div>
                        </div>
                        <label>Объяснение (обязательно)
                            <AppTextarea v-model="arrivalDraft.reason" maxlength="2000"></AppTextarea>
                        </label>
                        <label><input type="checkbox" v-model="arrivalDraft.accept_discrepancy"> Подтверждаю несмотря на несоответствие</label>
                    </div>
                    <div v-if="arrivalDraft.error" class="workforce-plan-arrival__error" role="alert">
                        {{ arrivalDraft.error }}
                    </div>
                    <button
                        type="button"
                        class="btn btn-sm btn-success workforce-plan-arrival__submit"
                        :disabled="!arrivalDraft.arrival_date || arrivalDraft.pending || (arrivalDraft.planned_moves.length > 0 && !arrivalDraft.linked_planned_move_id)"
                        @click="submitArrivalConfirmation"
                    >
                        <b-spinner v-if="arrivalDraft.pending" small class="mr-1"></b-spinner>
                        {{ arrivalSubmitLabel }}
                    </button>
                </template>
            </section>

            <template v-if="canEdit">
            <label class="workforce-plan-command-search">
                Состояние, объект или группа
                <input
                    ref="quickEditorSearch"
                    v-model="editorQuery"
                    type="search"
                    autocomplete="off"
                    placeholder="Начните печатать код или название"
                    @input="editorCommandIndex = 0"
                    @focus="editorSearchFocused = true"
                    @blur="deferEditorSearchClose"
                    @keydown="handleEditorSearchKeydown"
                >
            </label>
            <div
                v-if="editorQuery || editorSearchFocused"
                class="workforce-plan-command-results"
                role="listbox"
            >
                <button
                    v-for="(option, index) in filteredCommandOptions"
                    :key="option.value"
                    type="button"
                    :class="{'is-active': index === editorCommandIndex}"
                    @mouseenter="editorCommandIndex = index"
                    @click="chooseCommandOption(option)"
                >
                    <strong>{{ option.label }}</strong>
                    <small v-if="option.hint">{{ option.hint }}</small>
                </button>
                <small v-if="!filteredCommandOptions.length">
                    Подходящих вариантов нет. Произвольный текст записать нельзя.
                </small>
            </div>

            <button
                v-if="canCreatePlannedObjects"
                type="button"
                class="workforce-plan-create-planned-action"
                @click="preparePlannedObjectCreation('selection')"
            >
                <i class="fas fa-plus-circle" aria-hidden="true"></i>
                Создать плановый объект
            </button>

            <div class="workforce-plan-quick-states">
                <button
                    v-for="option in quickEditorStateOptions"
                    :key="option.value"
                    type="button"
                    :class="{'is-active': editor.state_type === option.value && editorDirty}"
                    @click="chooseCellState(option.value, false)"
                >
                    {{ option.text }}
                </button>
            </div>

            <div v-if="editorDirty" class="workforce-plan-selected-action">
                Выбрано: <strong>{{ editorSelectionLabel }}</strong>
            </div>
            <small v-if="editorDirty && editor.state_type === 'requires_distribution'" class="d-block text-muted mb-2">
                Продлится до конца года или ближайшей записи справа. Новое значение или очистка обрежет автозаполнение.
            </small>
            <div
                v-if="selectionImpact.replaced > 0 && editorDirty"
                class="workforce-plan-selection-warning"
            >
                Будут заменены существующие значения: {{ selectionImpact.replaced }}.
                Без изменений: {{ selectionImpact.unchanged }}.
            </div>

            <button
                type="button"
                class="workforce-plan-quick-editor__details-toggle"
                @click="toggleEditorDetails"
            >
                <i
                    class="fas mr-1"
                    :class="editorDetailsOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
                    aria-hidden="true"
                ></i>
                Точный период и комментарий
            </button>
            <div v-if="editorDetailsOpen" class="workforce-plan-quick-editor__details">
                <template v-if="exactPeriodAvailable">
                    <label>
                        С
                        <input
                            v-model="exactStartsOn"
                            type="date"
                            :min="window.from"
                            :max="exactEndsOnInclusive"
                            @change="markEditorDirty"
                        >
                    </label>
                    <label>
                        По
                        <input
                            v-model="exactEndsOnInclusive"
                            type="date"
                            :min="exactStartsOn"
                            :max="lastYearDate"
                            @change="markEditorDirty"
                        >
                    </label>
                </template>
                <small v-else class="workforce-plan-exact-period-disabled">
                    У Ctrl-диапазонов разные периоды — используются границы ячеек.
                </small>
                <label>
                    Комментарий
                    <select v-model="editor.note_mode" @change="markEditorDirty">
                        <option value="preserve">Не изменять</option>
                        <option value="set">Записать общий</option>
                        <option value="clear">Очистить</option>
                    </select>
                </label>
                <AppTextarea
                    v-if="editor.note_mode === 'set'"
                    v-model.trim="editor.note"
                    maxlength="2000"
                    placeholder="Один комментарий для всех выбранных ячеек"
                    @input="markEditorDirty"
                ></AppTextarea>
                <template v-if="editor.state_type === 'loupe'">
                    <label>
                        Причина выбытия
                        <select v-model="editor.loupe_reason_mode" @change="markEditorDirty">
                            <option value="preserve">Не изменять</option>
                            <option value="set">Записать общую</option>
                            <option value="clear">Очистить</option>
                        </select>
                    </label>
                    <AppTextarea
                        v-if="editor.loupe_reason_mode === 'set'"
                        v-model.trim="editor.loupe_reason"
                        maxlength="100"
                        placeholder="Например: не выходит на связь"
                        @input="markEditorDirty"
                     />
                </template>
            </div>

            <div aria-live="polite" class="small mb-2">
                <div v-if="loupePreviewPending">Рассчитываем продолжение…</div>
                <div v-for="message in loupePreviewMessages" :key="message">{{ message }}</div>
                <div v-if="loupePreviewError || errorMessage" role="alert" class="text-danger">{{ loupePreviewError || errorMessage }}</div>
                <button v-if="loupePreviewError" type="button" class="btn btn-sm btn-outline-secondary" @click="retryLoupePreview">Повторить расчёт</button>
            </div>
            <footer class="workforce-plan-quick-editor__actions">
                <button
                    type="button"
                    class="btn btn-sm"
                    :class="editor.state_type === 'clear' ? 'btn-danger' : 'btn-primary'"
                    :disabled="!saveAllowed || saving || loupePreviewPending || Boolean(loupePreviewError)"
                    @click="saveSelectedRanges"
                >
                    <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                    {{ editorApplyLabel }}
                </button>
                <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="saving"
                    @click="prepareClearAction"
                >
                    Очистить план
                </button>
            </footer>
            <small class="workforce-plan-quick-editor__hint">
                Enter — выбрать вариант · Ctrl+Enter — применить · Esc — закрыть
            </small>
            </template>
        </aside>

        <div v-if="loading" class="workforce-plan-loading">
            <b-spinner small></b-spinner>
            Загружаем план…
        </div>

        <div
            v-if="planWindowReady && !loading && !accessDenied && zoom !== 'fit'"
            ref="topScrollbar"
            class="workforce-plan-horizontal-scroll"
            aria-label="Горизонтальная прокрутка плана"
            @scroll="syncPlanScroll('top')"
        >
            <div :style="{width: gridContentWidth}"></div>
        </div>

        <nav
            v-if="planWindowReady && !loading && !accessDenied && zoom !== 'fit'"
            class="workforce-plan-mobile-week-nav"
            aria-label="Навигация по неделям"
        >
            <button type="button" @click="scrollPlanByWeeks(-4)">
                <i class="fas fa-chevron-left" aria-hidden="true"></i>
                4 нед.
            </button>
            <button type="button" class="is-current" :disabled="periodNavigationDisabled" @click="goToToday">
                <small>К текущей</small>
                <strong>{{ currentStatusWeek ? `${currentStatusWeek.number}Н` : 'Сегодня' }}</strong>
            </button>
            <button type="button" @click="scrollPlanByWeeks(4)">
                4 нед.
                <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
        </nav>

        <button
            v-if="planWindowReady && !loading && !accessDenied && zoom !== 'fit' && !quickEditorOpen"
            type="button"
            class="workforce-plan-context-fab"
            :class="{
                'is-active': mobileContextPinned,
                'is-previewing': mobileContextPreviewing
            }"
            :aria-label="mobileContextPinned
                ? 'ФИО закреплены. Нажмите, чтобы отпустить; удерживайте для прозрачного просмотра'
                : 'Нажмите, чтобы закрепить; удерживайте, чтобы временно показать прозрачные ФИО'"
            :aria-pressed="mobileContextPinned ? 'true' : 'false'"
            :title="mobileContextPinned
                ? 'Нажмите, чтобы отпустить; удерживайте для прозрачного просмотра'
                : 'Нажмите, чтобы закрепить; удерживайте, чтобы временно показать прозрачные ФИО'"
            @pointerdown="startMobileContextPreview"
            @pointerup="finishMobileContextPreview"
            @pointercancel="finishMobileContextPreview"
            @contextmenu.prevent
            @click="toggleMobileContext"
        >
            <i class="fas fa-eye" aria-hidden="true"></i>
            <i
                v-if="mobileContextPinned && !mobileContextPreviewing"
                class="fas fa-thumbtack workforce-plan-context-fab__pin"
                aria-hidden="true"
            ></i>
        </button>
        <aside
            v-if="mobileContextHintVisible
                && planWindowReady
                && !loading
                && !accessDenied
                && zoom !== 'fit'
                && !quickEditorOpen"
            class="workforce-plan-context-hint"
            role="status"
        >
            <button
                type="button"
                aria-label="Больше не показывать эту подсказку"
                @click="hideMobileContextHint(true)"
            >×</button>
            <strong>ФИО и текущее место</strong>
            <span>
                Нажмите на глаз — закрепить или отпустить.
                Удерживайте — временно показать ФИО полупрозрачно.
            </span>
        </aside>

        <div v-if="routeFocusMessage" class="alert alert-info" role="status">{{ routeFocusMessage }}</div>
        <section
            v-if="planWindowReady && !loading && !accessDenied"
            ref="viewport"
            class="workforce-plan-viewport"
            :class="{
                'workforce-plan-viewport--fit': zoom === 'fit',
                'workforce-plan-viewport--mobile-context-pinned': mobileContextPinned
                    || mobileContextPreviewing,
                'workforce-plan-viewport--mobile-context-previewing': mobileContextPreviewing
            }"
            :style="gridStyle"
            @scroll="handleViewportScroll"
        >
            <div class="workforce-plan-grid" role="grid"
                 :aria-label="periodMode === 'year' ? 'Годовой план сотрудников' : 'Скользящий план сотрудников'">
                <div class="workforce-plan-corner" role="columnheader">
                    <strong>{{ isMobileViewport ? 'ФИО' : 'Сотрудник' }}</strong>
                    <span v-if="!isMobileViewport">{{ periodMode === 'year' ? `План на ${year} год` : periodRangeLabel }}</span>
                    <button
                        type="button"
                        class="workforce-plan-summary-toggle"
                        :title="summaryCollapsed ? 'Показать сводку по объектам' : 'Скрыть сводку по объектам'"
                        :aria-label="summaryCollapsed ? 'Показать сводку по объектам' : 'Скрыть сводку по объектам'"
                        @click.stop="toggleSummary"
                    >
                        <i
                            class="fas"
                            :class="summaryCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
                            aria-hidden="true"
                        ></i>
                        Сводка
                    </button>
                </div>
                <button
                    id="workforce-plan-current-filter"
                    type="button"
                    class="workforce-plan-current-head"
                    :class="{
                        'workforce-plan-current-head--filtered': currentAssignmentFilter.length > 0
                    }"
                    role="columnheader"
                    :title="currentStatusTitle"
                    aria-label="Фильтр по текущему состоянию сотрудника"
                    @click="currentFilterOpen = !currentFilterOpen"
                >
                    <small>{{ isMobileViewport ? shortDate(currentStatusDate) : formatDate(currentStatusDate) }}</small>
                    <strong>{{ currentStatusWeek ? `${currentStatusWeek.number}Н` : '—' }}</strong>
                    <span class="workforce-plan-current-head__filter">
                        <i class="fas fa-filter" aria-hidden="true"></i>
                        <b>{{ currentAssignmentFilterShortLabel }}</b>
                        <i class="fas fa-caret-down" aria-hidden="true"></i>
                    </span>
                </button>
                <div
                    v-for="band in monthBands"
                    :key="band.key"
                    class="workforce-plan-month-head"
                    :class="{
                        'workforce-plan-month-head--current': band.is_current,
                        'workforce-plan-month-head--dated': periodMode === 'rolling'
                    }"
                    :style="{gridColumn: `${band.start + 4} / span ${band.count}`}"
                    :title="band.title"
                    role="columnheader"
                >
                    <span class="workforce-plan-month-name">{{ band.label }}</span>
                    <small v-if="band.yearLabel" class="workforce-plan-month-year">{{ band.yearLabel }}</small>
                </div>
                <button
                    v-for="(week, weekIndex) in weeks"
                    :key="`head:${week.key}`"
                    type="button"
                    class="workforce-plan-week-head"
                    :class="{
                        'workforce-plan-week-head--month': week.is_month_start,
                        'workforce-plan-week-head--today': week.is_current,
                        'workforce-plan-week-head--filter': week.is_filter
                    }"
                    :style="{gridColumn: weekIndex + 4, gridRow: 2}"
                    :title="weekTitle(week)"
                    role="columnheader"
                    @click="selectWeekForAll(week)"
                >
                    <strong>{{ week.number }}Н</strong>
                    <small v-if="zoom !== 'fit' && Number(zoom) >= 125">
                        {{ shortWeekRange(week) }}
                    </small>
                </button>

                <template v-for="summary in summaryPresentations">
                    <div
                        :key="`summary-label:${summary.row.key}`"
                        class="workforce-plan-summary-label"
                        :class="summaryRowClasses(summary.row)"
                        :style="summaryLabelStyle(summary.row)"
                        :title="summary.row.description || summary.row.label"
                        role="rowheader"
                    >
                        <strong class="workforce-plan-summary-label__text">
                            {{ summary.row.label }}
                        </strong>
                        <button
                            v-if="summary.row.description"
                            v-b-tooltip.hover.focus
                            type="button"
                            class="workforce-plan-summary-help"
                            :title="`${summary.row.description} Сверху — рабочие, снизу — ИТР.`"
                            :aria-label="`Что означает «${summary.row.label}»`"
                        >
                            <span aria-hidden="true">?</span>
                        </button>
                        <button
                            v-if="planningNameTargetKeys.has(summary.row.key)"
                            type="button"
                            class="workforce-plan-settings-button"
                            :title="`Цвет и название в плане: ${summary.row.label}`"
                            :aria-label="`Цвет и название в плане: ${summary.row.label}`"
                            @click.stop="openPlanningNames(summary.row)"
                        >
                            <i class="fas fa-cog" aria-hidden="true"></i>
                        </button>
                        <button
                            v-if="linkablePlannedObjectKeys.has(summary.row.key)"
                            type="button"
                            class="workforce-plan-link-button"
                            :title="`Связать с объектом в Аргусе: ${summary.row.label}`"
                            :aria-label="`Связать с объектом в Аргусе: ${summary.row.label}`"
                            :disabled="plannedObjectsLoading"
                            @click.stop="openPlannedObjectLink(summary.row)"
                        >
                            <i class="fas fa-link" aria-hidden="true"></i>
                        </button>
                    </div>
                    <div
                        :key="`summary-current:${summary.row.key}`"
                        v-b-tooltip.hover.html
                        class="workforce-plan-summary-cell workforce-plan-current-cell"
                        :class="[
                            summaryRowClasses(summary.row),
                            {'workforce-plan-summary-cell--filled': summary.current.breakdown.total > 0}
                        ]"
                        :style="summary.current.style"
                        :title="summary.current.title"
                        role="gridcell"
                    >
                        <strong
                            v-if="summary.current.breakdown.total > 0"
                            class="workforce-plan-summary-count"
                            :aria-label="`Рабочие: ${summary.current.breakdown.workers}; ИТР: ${summary.current.breakdown.itr}`"
                        >
                            <span class="workforce-plan-summary-count__workers">
                                {{ summary.current.breakdown.workers }}
                            </span>
                            <span class="workforce-plan-summary-count__divider">/</span>
                            <span class="workforce-plan-summary-count__itr">
                                {{ summary.current.breakdown.itr }}
                            </span>
                        </strong>
                    </div>
                    <div
                        v-for="cell in summary.weeks"
                        :key="`summary:${summary.row.key}:${cell.week.key}`"
                        v-b-tooltip.hover.html
                        class="workforce-plan-summary-cell"
                        :class="[
                            summaryRowClasses(summary.row),
                            {
                                'workforce-plan-summary-cell--filled': cell.breakdown.total > 0,
                                'workforce-plan-summary-cell--month': cell.week.is_month_start,
                                'workforce-plan-summary-cell--today': cell.week.is_current
                            }
                        ]"
                        :style="cell.style"
                        :title="cell.title"
                        role="gridcell"
                    >
                        <strong
                            v-if="cell.breakdown.total > 0"
                            class="workforce-plan-summary-count"
                            :aria-label="`Рабочие: ${cell.breakdown.workers}; ИТР: ${cell.breakdown.itr}`"
                        >
                            <span class="workforce-plan-summary-count__workers">
                                {{ cell.breakdown.workers }}
                            </span>
                            <span class="workforce-plan-summary-count__divider">/</span>
                            <span class="workforce-plan-summary-count__itr">
                                {{ cell.breakdown.itr }}
                            </span>
                        </strong>
                    </div>
                </template>

                <div
                    class="workforce-plan-summary-label workforce-plan-summary-label--section"
                    role="rowheader"
                >
                    <strong>Сотрудники</strong>
                </div>
                <div
                    class="workforce-plan-summary-cell workforce-plan-summary-cell--section workforce-plan-current-cell"
                    role="presentation"
                ></div>
                <div
                    v-for="week in weeks"
                    :key="`employee-section:${week.key}`"
                    class="workforce-plan-summary-cell workforce-plan-summary-cell--section"
                    :class="{
                        'workforce-plan-summary-cell--month': week.is_month_start,
                        'workforce-plan-summary-cell--today': week.is_current
                    }"
                    role="presentation"
                ></div>

                <employee-workforce-plan-rows
                    ref="employeeRows"
                    :api="gridRowApi"
                    :employees="filteredEmployees"
                    :inspected-employee-id="inspectedEmployeeId"
                    :focused-employee-id="focusedEmployeeId"
                    :mobile="isMobileViewport"
                    :selected-employee-ids="selectedEmployeeIds"
                    :selected-full-row-employee-ids="selectedFullRowEmployeeIds"
                    :weeks="weeks"
                    :current-status-week="currentStatusWeek"
                ></employee-workforce-plan-rows>
            </div>
            <div v-if="!filteredEmployees.length" class="workforce-plan-empty">
                <template v-if="assignmentFilter !== 'all' && assignmentFilterScope === 'future' && !assignmentFilterRange">
                    В открытом периоде нет текущей и будущих недель.
                    <b-button size="sm" variant="outline-primary" :disabled="periodNavigationDisabled" @click="goToToday">Сегодня</b-button>
                </template>
                <template v-else>Сотрудники не найдены</template>
            </div>
        </section>

        <employee-workforce-history v-if="permissions.can_view && !accessDenied" inline
                                    @open-employee="$router.push({name: 'employee-details', params: {id: $event.employee_id}})" />

        <b-popover
            v-if="!loading && !accessDenied"
            target="workforce-plan-current-filter"
            triggers="manual"
            :show.sync="currentFilterOpen"
            placement="bottom"
            boundary="viewport"
            custom-class="workforce-plan-current-filter-popover"
            @hidden="currentFilterOpen = false"
        >
            <template #title>
                <div class="workforce-plan-current-filter-title" @keydown.esc.capture.stop.prevent="closeCurrentAssignmentFilter()">
                    <span>Кто находится здесь сейчас</span>
                    <b-button variant="link" class="workforce-plan-current-filter-close"
                              aria-label="Закрыть фильтр" title="Закрыть фильтр"
                              @click="closeCurrentAssignmentFilter()">×</b-button>
                </div>
            </template>
            <div class="workforce-plan-current-filter-menu" @click.stop
                 @keydown.esc.capture.stop.prevent="closeCurrentAssignmentFilter()">
                <v-select
                    ref="currentAssignmentSelect"
                    class="employee-checklist-select"
                    :value="currentAssignmentFilter.length ? currentAssignmentFilter : ['all']"
                    :options="currentAssignmentFilterOptions"
                    label="text"
                    :reduce="option => option.value"
                    :selectable="option => !option.disabled"
                    multiple
                    :close-on-select="false"
                    deselect-from-dropdown
                    :searchable="true"
                    aria-label="Состояние сотрудника на выбранную дату"
                    @open="sizeCurrentAssignmentDropdown"
                    @input="setCurrentAssignmentFilter"
                >
                    <template #selected-option-container="{option}">
                        <span v-if="option.value === (currentAssignmentFilter[0] || 'all')"
                              class="workforce-plan-current-filter-selection">
                            {{ currentAssignmentFilter.length > 1 ? `Выбрано: ${currentAssignmentFilter.length}` : option.text }}
                        </span>
                    </template>
                    <template #option="option">
                        <employee-filter-option :label="option.label" :count="option.count" :title="option.label"
                                                :heading="!!option.disabled"
                                                :selected="currentAssignmentOptionSelected(option.value)" />
                    </template>
                </v-select>
                <small>
                    Состояние строго на {{ formatDate(currentStatusDate) }}.
                    Верхний фильтр ищет упоминания {{ periodMode === 'year' ? 'за весь год' : 'за весь период' }}.
                    Счётчики не включают скрытых в настройках доски и плана.
                </small>
                <b-button variant="primary" size="sm" block class="mt-3"
                          @click="closeCurrentAssignmentFilter()">Готово</b-button>
            </div>
        </b-popover>

        <employee-inspector
            :employee="inspectorEmployee"
            :loading="inspectorLoading"
            :can-view-plan="permissions.can_view === true"
            :board-date="currentStatusDate"
            :can-move="false"
            :can-manage-characteristics="inspectorCanManage('characteristics')"
            :can-manage-absences="inspectorCanManage('absences')"
            @close="closeEmployeeInspector"
            @characteristic-pin-changed="refreshEmployeeInspector"
            @characteristic-added="refreshEmployeeInspector"
            @characteristic-updated="refreshEmployeeInspector"
            @absence-added="refreshEmployeeInspector"
            @reserve-returned="refreshEmployeeInspector"
        ></employee-inspector>

        <workforce-planning-target-modal
            v-if="planningNamesVisible"
            :targets="planningNameTargets"
            :initial-target-key="planningNameTargetKey"
            :save-name="savePlanningName"
            :save-color="savePlanningTargetColor"
            :color-palette="planningColorPalette"
            :color-text="planningColorText"
            @saved="onPlanningTargetSettingsSaved"
            @close="planningNamesVisible = false"
        ></workforce-planning-target-modal>

        <employee-create-modal
            v-model="createEmployeeVisible"
            @created="onEmployeeCreated"
        ></employee-create-modal>

        <b-modal
            v-model="plannedObjectsManagerVisible"
            size="lg"
            :title="plannedObjectAction.item
                ? (plannedObjectAction.mode === 'link' ? 'Связать с Аргусом' : 'Отменить плановый объект')
                : 'Плановые объекты'"
            :hide-footer="!plannedObjectAction.item"
            :hide-header-close="plannedObjectActionSaving"
            scrollable
            @hide="preventPlannedObjectsHideWhileSaving"
            @hidden="clearPlannedObjectAction"
        >
            <div v-if="plannedObjectsLoading" class="text-center py-4">
                <b-spinner></b-spinner>
            </div>
            <template v-else-if="!plannedObjectAction.item">
                <b-alert v-if="plannedObjectsError" show variant="danger">
                    {{ plannedObjectsError }}
                    <b-button size="sm" variant="outline-danger" @click="openPlannedObjectsManager">
                        Повторить загрузку
                    </b-button>
                </b-alert>
                <div class="workforce-planned-object-manager-toolbar">
                    <small>
                        Здесь создаются объекты для будущих контрактов, которых ещё нет в Аргусе.
                    </small>
                    <b-button
                        v-if="plannedObjectsCanCreate"
                        size="sm"
                        variant="primary"
                        @click="preparePlannedObjectCreation('manager')"
                    >
                        <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                        Создать плановый объект
                    </b-button>
                </div>
                <div v-if="!plannedObjectsError && !managedPlannedObjects.length" class="text-muted py-3">
                    Плановых объектов пока нет.
                </div>
                <div
                    v-for="item in managedPlannedObjects"
                    :key="item.planned_object_id"
                    class="workforce-planned-object-row"
                >
                    <div>
                        <strong>{{ item.planning_code }} — {{ item.name }}</strong>
                        <small>{{ plannedObjectStatusLabel(item) }}</small>
                        <small v-if="item.comment">{{ item.comment }}</small>
                        <small v-if="item.status === 'planned' && item.future_usage">
                            В будущем плане: {{ item.future_usage.employee_count }} сотрудников,
                            {{ item.future_usage.interval_count }} периодов
                        </small>
                    </div>
                    <b-button-group
                        v-if="item.status === 'planned' && plannedObjectsCanManage"
                        size="sm"
                    >
                        <b-button
                            variant="outline-success"
                            @click="preparePlannedObjectAction(item, 'link')"
                        >
                            Связать
                        </b-button>
                        <b-button
                            variant="outline-danger"
                            @click="preparePlannedObjectAction(item, 'cancel')"
                        >
                            Отменить
                        </b-button>
                    </b-button-group>
                </div>
            </template>
            <div v-else class="workforce-planned-object-action">
                <p class="font-weight-bold">
                    {{ plannedObjectAction.item.planning_code }} — {{ plannedObjectAction.item.name }}
                </p>
                <b-form-group
                    :label="plannedObjectAction.mode === 'link' ? 'Объект или группа в Аргусе' : 'Чем заменить будущий план'"
                    label-for="planned-object-action-target"
                >
                    <v-select
                        v-model="plannedObjectAction.target"
                        input-id="planned-object-action-target"
                        :options="plannedObjectReplacementOptions"
                        label="label"
                        placeholder="Найдите объект или группу по названию"
                        :disabled="plannedObjectActionSaving"
                        :clearable="false"
                        :searchable="true"
                        append-to-body
                        :calculate-position="positionVueSelectDropdown"
                    ></v-select>
                </b-form-group>
                <b-form-group
                    label="Неделя начала изменений"
                    label-for="planned-object-action-week"
                    description="Выберите любой день — изменения начнутся с понедельника этой недели."
                >
                    <b-form-input
                        id="planned-object-action-week"
                        :value="plannedObjectAction.selected_date"
                        :disabled="plannedObjectActionSaving"
                        type="date"
                        @input="changePlannedObjectWeek"
                    ></b-form-input>
                </b-form-group>
                <p v-if="plannedObjectAction.effective_from" class="workforce-planned-object-week">
                    Изменения с понедельника, <strong>{{ formatDate(plannedObjectAction.effective_from) }}</strong>.
                </p>
                <b-alert v-if="plannedObjectAction.impact_error" show variant="danger">
                    {{ plannedObjectAction.impact_error }}
                    <b-button
                        size="sm"
                        variant="outline-danger"
                        :disabled="plannedObjectActionSaving"
                        @click="loadPlannedObjectImpact"
                    >
                        Повторить расчёт
                    </b-button>
                </b-alert>
                <b-alert
                    v-else-if="plannedObjectAction.impact_loading || plannedObjectAction.impact"
                    show
                    variant="info"
                    class="py-2"
                    aria-live="polite"
                >
                    <template v-if="plannedObjectAction.impact_loading">
                        <b-spinner small class="mr-1"></b-spinner>
                        Рассчитываем изменения в плане…
                    </template>
                    <template v-else>
                        {{ plannedObjectAction.mode === 'link' ? 'На выбранный объект или группу перейдут' : 'Будет изменён план' }}:
                        <strong>
                            {{ plannedObjectAction.impact.employee_count }} сотрудников,
                            {{ plannedObjectAction.impact.interval_count }} периодов
                        </strong>.
                        <template v-if="plannedObjectAction.impact.factual_employee_count">
                            Фактически на плановом объекте:
                            <strong>
                                {{ plannedObjectAction.impact.factual_employee_count }} сотрудников
                            </strong> — их текущее назначение тоже будет перенесено.
                        </template>
                        Недели до {{ formatDate(plannedObjectAction.effective_from) }} останутся без изменений.
                    </template>
                </b-alert>
                <b-form-group
                    v-if="plannedObjectAction.mode === 'cancel'"
                    label="Причина отмены"
                    label-for="planned-object-action-reason"
                >
                    <AppTextarea
                        v-model.trim="plannedObjectAction.reason"
                        id="planned-object-action-reason"
                        :disabled="plannedObjectActionSaving"
                        maxlength="2000"
                    ></AppTextarea>
                </b-form-group>
            </div>
            <template #modal-footer>
                <div class="workforce-planned-object-footer">
                    <b-alert v-if="plannedObjectAction.error" show variant="danger" class="mb-2">
                        {{ plannedObjectAction.error }}
                    </b-alert>
                    <p
                        v-if="plannedObjectActionBlockedReason"
                        id="planned-object-action-hint"
                        class="text-muted mb-2"
                        aria-live="polite"
                    >
                        {{ plannedObjectActionBlockedReason }}
                    </p>
                    <div class="d-flex justify-content-between">
                        <b-button
                            variant="outline-secondary"
                            class="mr-2"
                            :disabled="plannedObjectActionSaving"
                            @click="clearPlannedObjectAction"
                        >
                            К списку
                        </b-button>
                        <b-button
                            :variant="plannedObjectAction.mode === 'link' ? 'success' : 'danger'"
                            :disabled="plannedObjectActionSaving || !plannedObjectActionValid"
                            :aria-describedby="plannedObjectActionBlockedReason ? 'planned-object-action-hint' : null"
                            @click="applyPlannedObjectAction"
                        >
                            <b-spinner v-if="plannedObjectActionSaving" small class="mr-1"></b-spinner>
                            {{ plannedObjectActionSaving
                                ? 'Сохраняем…'
                                : (plannedObjectAction.mode === 'link' ? 'Связать с Аргусом' : 'Отменить плановый объект') }}
                        </b-button>
                    </div>
                </div>
            </template>
        </b-modal>

        <footer v-if="!accessDenied" class="workforce-plan-legend">
            <span><i class="legend-object"></i> Объект</span>
            <span><i class="legend-planned-object"></i> Плановый объект</span>
            <span><i class="legend-distribution"></i> ❌ Не распределён</span>
            <span><i class="legend-rest"></i> ОТДХ</span>
            <span><i class="legend-loupe"></i> 🔍 Выбыл из планирования</span>
            <span><i class="legend-missing"></i> План не заполнен</span>
        </footer>
    </div>
</template>

<script>
import {workforceEmployeeSearchText, matchesWorkforceSearch, registrySearchWords} from './employeeWorkforceSearch';
import EmployeeMultiFilter from './EmployeeMultiFilter.vue';
import EmployeeFilterOption from './EmployeeFilterOption.vue';
import {employeeFilterOptions, matchesEmployeeFilters, employeeSkillDescriptions} from './employeeRegistryFilters';
import {hiringFormOptions, hiringFormLabel, matchesHiringForm} from './employeeHiringForm';
import api from '@/utils/api';
import timesheetArrivalLabel from '@/utils/timesheetArrivalLabel';
import {socket} from '@/plugins/socket';
import planUtils from '@/utils/employeeWorkforcePlan';
import employeeWorkforcePlanFocus from './employeeWorkforcePlanFocus';
import {positionVueSelectDropdown} from '@/utils/vueSelectPositioning';
import selectionUtils from '@/utils/employeeWorkforceSelection';
import {SOURCE_SKILL_DESCRIPTIONS} from '@/utils/employeeSkills';
import employeeNameFormat from '@/utils/employeeNameFormat';
import EmployeeCreateModal from './EmployeeCreateModal.vue';
import EmployeeInspector from './EmployeeInspector.vue';
import CollaborationParticipants from '@/components/CollaborationParticipants.vue';
import {activeEditorForUser, revealCollaborationCell} from '@/utils/collaborationPresence';
import EmployeeWorkforceHistory from './EmployeeWorkforceHistory.vue';
import EmployeeNameFormatSwitcher from './EmployeeNameFormatSwitcher.vue';
import EmployeeWorkforcePlanRows from './EmployeeWorkforcePlanRows.vue';
import EmployeeWorkforceDisplayControls from './EmployeeWorkforceDisplayControls.vue';
import planDisplay from '@/utils/employeeWorkforcePlanDisplay';
import WorkforcePlanningTargetModal from './WorkforcePlanningTargetModal.vue';

const DEFAULT_PLANNING_COLOR_PALETTE = planDisplay.MANUAL_TARGET_PALETTE;
const DEFAULT_PLANNING_COLOR_TEXT = planDisplay.TARGET_COLOR_TEXT;
const PLAN_ROW_COPY_HINT_STORAGE_KEY =
    'employee-workforce:plan-row-copy-hint:v1';
const PLAN_ROW_COPY_HINT_LIMIT = 3;
const PLAN_GRID_IGNORED_REALTIME_CHANGES = new Set([
    'characteristic_pinned'
]);

function currentLocalDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function responseData(response) {
    return response && response.data && response.data.data
        ? response.data.data
        : response && response.data || {};
}

function emptyPlannedObjectAction() {
    return {
        item: null,
        mode: '',
        selected_date: '',
        effective_from: '',
        target: null,
        reason: '',
        error: '',
        impact: null,
        impact_date: '',
        impact_error: '',
        impact_loading: false,
        impact_request: 0
    };
}

function emptyArrivalDraft() {
    return {
        discrepancies: [], reason: '', accept_discrepancy: false,
        open: false,
        mode: 'create',
        arrival_date: '',
        other_date_open: false,
        pending: false,
        cancelling: false,
        error: '',
        warning: null,
        planned_moves: [],
        linked_planned_move_id: null
    };
}

export default {
    mixins: [employeeWorkforcePlanFocus],
    name: 'EmployeeWorkforcePlan',
    components: {
        CollaborationParticipants,
        EmployeeWorkforceHistory,
        EmployeeWorkforceDisplayControls,
        EmployeeFilterOption,
        EmployeeMultiFilter,
        WorkforcePlanningTargetModal,
        EmployeeCreateModal,
        EmployeeInspector,
        EmployeeNameFormatSwitcher,
        EmployeeWorkforcePlanRows
    },
    provide() {
        return {
            workforceEmployeeNameFormat: this.employeeNameFormatState
        };
    },
    data() {
        let arrivalDetailsExpanded = false;
        try {
            arrivalDetailsExpanded = window.localStorage.getItem('argus.workforce.arrivalDetailsExpanded') === 'true';
        } catch (error) { /* The panel also works when browser storage is unavailable. */ }
        const routeQuery = this.$route && this.$route.query || {};
        const today = currentLocalDate();
        const routeDateIntent = planUtils.hasExplicitRouteDate(routeQuery);
        const initialDate = planUtils.initialRouteDate(routeQuery, today);
        return {
            periodMode: planUtils.readPlanViewMode(this.$store && this.$store.getters && this.$store.getters.currentUser),
            periodAnchorDate: initialDate,
            loadedPlanKey: '',
            year: Number(initialDate.slice(0, 4)),
            query: '',
            filterDate: initialDate,
            assignmentFilter: 'all',
            assignmentFilterScope: 'future',
            currentAssignmentFilter: [],
            citizenshipFilter: 'all',
            hiringFormFilter: '',
            skillFilter: [],
            itrFilter: false,
            positionFilter: [],
            employeeNameFormatState: {
                value: employeeNameFormat.readEmployeeNameFormat()
            },
            copyNamesWithDetails: false,
            currentFilterOpen: false,
            zoom: planDisplay.DEFAULT_ZOOM,
            numericZoom: planDisplay.DEFAULT_ZOOM,
            contrast: planDisplay.readContrast(),
            displayReady: false,
            employees: [],
            objects: [],
            objectGroups: [],
            workforcePlannedObjects: [],
            permissions: {},
            createEmployeeVisible: false,
            importSummary: null,
            reconciliation: {
                objects: [],
                employees: [],
                object_catalog: [],
                object_group_catalog: [],
                planned_object_catalog: [],
                employee_catalog: []
            },
            importDecisions: {
                revision: 0,
                object_mappings: {},
                planned_objects: {},
                employee_mappings: {},
                employee_exclusions: {},
                conflict_resolutions: {}
            },
            cutover: {
                schema_ready: false,
                latest_batch: null,
                reconciliation: null,
                blockers: [],
                ready_to_apply: false,
                migration_completed: false
            },
            cutoverPending: false,
            showReconciliation: false,
            reconciliationTab: 0,
            savingDecisions: false,
            syncingSkills: false,
            planningCodeSavingKey: null,
            planningNamesVisible: false,
            planningNameTargetKey: null,
            plannedObjectDraftVisible: false,
            plannedObjectDraftMode: 'manager',
            plannedObjectDraftSaving: false,
            plannedObjectDraftError: '',
            plannedObjectDraft: {
                planning_code: '',
                name: '',
                comment: '',
                conflict_owner: null,
                rename_conflict_to: ''
            },
            plannedObjectsManagerVisible: false,
            plannedObjectsLoading: false,
            plannedObjectsError: '',
            plannedObjectsCanCreate: false,
            plannedObjectsCanManage: false,
            managedPlannedObjects: [],
            plannedObjectActionSaving: false,
            planningColorPalette: DEFAULT_PLANNING_COLOR_PALETTE.slice(),
            plannedObjectAction: emptyPlannedObjectAction(),
            lastSkillSyncAttempt: null,
            summaryCollapsed: false,
            mobileFiltersOpen: false,
            mobileContextPinned: false,
            mobileContextPreviewing: false,
            mobileContextHintVisible: false,
            rowCopyHintVisible: false,
            rowCopyHintSessionCount: 0,
            selectedCellKeys: [],
            selectionAnchor: null,
            selectionFocus: null,
            dragSelectionBaseKeys: [],
            dragSelectionStart: null,
            dragSelectionAdditive: false,
            dragSelectionActive: false,
            dragSelectionMoved: false,
            dragSelectionTargetId: '',
            dragPointerClientX: null,
            dragPointerClientY: null,
            selectionAutoscrollFrame: null,
            lastBlockedSelectionEmployeeId: null,
            quickEditorOpen: false,
            quickEditorPosition: {top: 100, left: 100},
            quickEditorDragging: false,
            quickEditorDragOffset: {x: 0, y: 0},
            viewportSize: {width: 1280, height: 720},
            editorQuery: '',
            editorSearchFocused: false,
            editorCommandIndex: 0,
            editorDirty: false,
            editorDetailsOpen: false,
            exactStartsOn: '',
            exactEndsOnInclusive: '',
            loading: false,
            backgroundRefreshing: false,
            loadRequestId: 0,
            saving: false,
            accessDenied: false,
            loupePreview: [], loupePreviewPending: false, loupePreviewError: '', loupePreviewRequest: 0,
            errorMessage: '',
            inspectorEmployee: null,
            inspectorLoading: false,
            inspectorRequestId: 0,
            editor: {
                state_type: '',
                building_id: null,
                object_group_id: null,
                planned_object_id: null,
                loupe_reason: '',
                loupe_reason_mode: 'preserve',
                note: '',
                note_mode: 'preserve'
            },
            arrivalDraft: emptyArrivalDraft(),
            arrivalDetailsExpanded,
            syncStatus: 'connecting',
            remoteEditors: {},
            realtimeRefreshTimer: null,
            presenceHeartbeatTimer: null,
            presenceCleanupTimer: null,
            routeSelectionApplied: false,
            routeDateIntent,
            observedToday: today,
            selectionFilterResetSuppressed: false
        };
    },
    computed: {
        loupePreviewKey() {
            return this.saveAllowed ? JSON.stringify(this.planChangePayload()) : '';
        },
        loupePreviewMessages() {
            const messages = [];
            this.loupePreview.forEach(effect => {
                (effect.extended || []).forEach(range => messages.push('Лупа продолжится до ' + this.formatDate(planUtils.addDays(range.ends_on, -1))));
                (effect.trimmed || []).forEach(range => messages.push('Продолжение будет снято до ' + this.formatDate(planUtils.addDays(range.ends_on, -1))));
                if (effect.rolling) messages.push('С каждой новой неделей граница сдвигается на неделю вправо, пока лупа не прервана.');
            });
            return Array.from(new Set(messages));
        },
        collaborationUserIds() {
            return Object.values(this.remoteEditors).map(editor => Number(editor.actor && editor.actor.user_id));
        },
        hiringFormOptions() { return hiringFormOptions; },
        currentUser() {
            return this.$store &&
                this.$store.getters &&
                this.$store.getters.currentUser || null;
        },
        summaryCollapsedStorageKey() {
            const user = this.currentUser || {};
            const company = user.company || 'company';
            const userId = user.userId || user.user_id || 'anonymous';
            return `employee-workforce:plan-summary-collapsed:v1:${company}:${userId}`;
        },
        window() {
            return this.periodMode === 'year'
                ? planUtils.yearWindow(this.year)
                : planUtils.rollingWindow(this.periodAnchorDate);
        },
        planWindowReady() {
            return this.loadedPlanKey === this.planLoadKey();
        },
        periodRangeLabel() {
            return `${this.formatDate(this.window.from)} — ${this.formatDate(this.lastYearDate)}`;
        },
        periodNavigationDisabled() {
            return this.saving || this.arrivalDraft.pending || this.arrivalDraft.cancelling;
        },
        weeks() {
            const todayMonday = planUtils.mondayOnOrBefore(currentLocalDate());
            let previousMonthGroup = null;
            return this.window.weeks.map(week => {
                const startsMonth = week.visible_starts_on.slice(0, 7);
                const endsMonth = planUtils.addDays(week.visible_ends_on, -1).slice(0, 7);
                const monthGroup = `${startsMonth}:${endsMonth}`;
                const value = Object.assign({}, week, {
                    number: planUtils.isoWeekNumber(week.starts_on),
                    starts_month: startsMonth,
                    ends_month: endsMonth,
                    month_group: monthGroup,
                    is_month_start: monthGroup !== previousMonthGroup,
                    is_current: week.starts_on === todayMonday,
                    is_filter: week.visible_starts_on <= this.filterDate &&
                        this.filterDate < week.visible_ends_on
                });
                previousMonthGroup = monthGroup;
                return value;
            });
        },
        weekStateIndex() {
            return planUtils.buildWeekStateIndex(
                this.employees,
                this.window.weeks
            );
        },
        currentIntervalIndex() {
            return this.employees.reduce((result, employee) => {
                result.set(
                    Number(employee.employee_id),
                    planUtils.stateOnDate(employee.intervals, this.currentStatusDate)
                );
                return result;
            }, new Map());
        },
        filteredEmployeeById() {
            return new Map(this.filteredEmployees.map(employee => (
                [Number(employee.employee_id), employee]
            )));
        },
        selectionEmployeeIndexes() {
            return new Map(this.filteredEmployees.map((employee, index) => (
                [Number(employee.employee_id), index]
            )));
        },
        weekByKey() {
            return new Map(this.weeks.map(week => [week.key, week]));
        },
        remoteEditorCellIndex() {
            const result = new Map();
            Object.keys(this.remoteEditors).forEach(id => {
                const editor = this.remoteEditors[id];
                (editor && editor.ranges || []).forEach(range => {
                    const employeeId = Number(range.employee_id);
                    if (!Number.isFinite(employeeId) || employeeId <= 0) return;
                    this.weeks.forEach(week => {
                        if (
                            !planUtils.rangesOverlap(
                                week.visible_starts_on,
                                week.visible_ends_on,
                                range.starts_on,
                                range.ends_on
                            )
                        ) return;
                        const key = selectionUtils.cellKey(employeeId, week.key);
                        if (!result.has(key)) result.set(key, editor);
                    });
                });
            });
            return result;
        },
        arrivalConfirmationCellIndex() {
            const result = new Map();
            this.employees.forEach(employee => {
                (Array.isArray(employee.arrival_confirmations)
                    ? employee.arrival_confirmations
                    : []
                ).forEach(confirmation => {
                    const week = this.weeks.find(item => (
                        confirmation.arrival_date >= item.visible_starts_on
                        && confirmation.arrival_date < item.visible_ends_on
                    ));
                    if (!week) return;
                    result.set(
                        selectionUtils.cellKey(employee.employee_id, week.key),
                        confirmation
                    );
                });
            });
            return result;
        },
        planningTargetColorIndex() {
            const result = new Map();
            const addTargets = (targetType, rows, idField) => {
                rows.forEach(item => {
                    const targetId = Number(item && item[idField]);
                    if (!Number.isFinite(targetId) || targetId <= 0) return;
                    const key = `${targetType}:${targetId}`;
                    const supplied = planDisplay.normalizeTargetColor(item.planning_color);
                    result.set(
                        key,
                        planDisplay.isTargetColor(supplied)
                            ? supplied
                            : this.automaticPlanningColor(key)
                    );
                });
            };
            addTargets('object', this.objects, 'building_id');
            addTargets('object_group', this.objectGroups, 'object_group_id');
            addTargets(
                'planned_object',
                this.workforcePlannedObjects,
                'planned_object_id'
            );
            return result;
        },
        selectionWeekIndexes() {
            return new Map(this.weeks.map((week, index) => [week.key, index]));
        },
        gridRowApi() {
            // Read computed indexes once per API generation. Reading them in every
            // cell repeats Vue 2's dependency traversal over the entire loaded plan.
            const indexes = {
                states: this.weekStateIndex,
                current: this.currentIntervalIndex,
                arrivals: this.arrivalConfirmationCellIndex,
                remote: this.remoteEditorCellIndex,
                colors: this.planningTargetColorIndex
            };
            return {
                cellDomId: this.cellDomId,
                cellPresentation: (employee, week, current) => this.cellPresentation(employee, week, current, indexes),
                employeeFullName: this.employeeFullName,
                employeeRowTitle: this.employeeRowTitle,
                employeeSkillLabel: this.employeeSkillLabel,
                employeeSkillTitle: this.employeeSkillTitle,
                extendCellDrag: this.extendCellDrag,
                handleCellClick: this.handleCellClick,
                importMatchLabel: this.importMatchLabel,
                openEmployeeInspector: this.openEmployeeInspector,
                rowSelectorDomId: this.rowSelectorDomId,
                selectEmployeeRow: this.selectEmployeeRow,
                startCellDrag: this.startCellDrag
            };
        },
        monthBands() {
            const rolling = this.periodMode === 'rolling';
            const weekWidth = this.zoom === 'fit'
                ? Math.max(14, (Number(this.viewportSize.width) - (this.isMobileViewport ? 224 : 326)) / 53)
                : (parseInt(this.weekWidth, 10) || 34);
            return this.weeks.reduce((bands, week, index) => {
                let band = bands[bands.length - 1];
                if (!band || band.group !== week.month_group) {
                    const transition = week.starts_month !== week.ends_month;
                    const startsYear = week.starts_month.slice(0, 4);
                    const endsYear = week.ends_month.slice(0, 4);
                    const month = this.monthLabelLong(week.starts_month);
                    const firstTitle = `${month}${rolling ? ` ${startsYear}` : ''}`;
                    band = {
                        key: `${week.month_group}:${index}`,
                        group: week.month_group,
                        start: index,
                        count: 0,
                        label: transition
                            ? `${this.monthInitial(week.starts_month)}/${this.monthInitial(week.ends_month)}`
                            : month,
                        shortLabel: transition
                            ? `${this.monthInitial(week.starts_month)}/${this.monthInitial(week.ends_month)}`
                            : month.slice(0, 3),
                        yearLabel: rolling
                            ? (startsYear === endsYear ? startsYear : `${startsYear.slice(2)}/${endsYear.slice(2)}`)
                            : '',
                        title: transition
                            ? `${firstTitle} / ${this.monthLabelLong(week.ends_month)}${rolling ? ` ${endsYear}` : ''}`
                            : firstTitle,
                        is_current: false
                    };
                    bands.push(band);
                }
                band.count += 1;
                band.is_current = band.is_current || week.is_current;
                return bands;
            }, []).map(band => {
                if (rolling && band.count * weekWidth < 64) band.label = band.shortLabel;
                // Fit mode can make a transition narrower than a four-digit year.
                // Keep its full date in the existing tooltip instead of clipping digits.
                if (band.count * weekWidth < 24) band.yearLabel = '';
                return band;
            });
        },
        weeklySummary() {
            return planUtils.weeklyCounts(
                this.employees,
                this.weeks,
                (employee, week) => this.cellState(employee, week)
            );
        },
        referencedWorkforcePlannedObjects() {
            const ids = new Set();
            this.employees.forEach(employee => {
                (employee.intervals || []).forEach(interval => {
                    if (interval.state_type === 'planned_object') {
                        ids.add(Number(interval.planned_object_id));
                    }
                });
            });
            return this.workforcePlannedObjects.filter(item => (
                ids.has(Number(item.planned_object_id))
            ));
        },
        planningNameTargetKeys() {
            return new Set(this.planningNameTargets.map(target => target.key));
        },
        linkablePlannedObjectKeys() {
            if (!this.permissions.can_edit) return new Set();
            return new Set(this.workforcePlannedObjects.filter(item => item.status === 'planned')
                .map(item => `planned_object:${Number(item.planned_object_id)}`));
        },
        planningNameTargets() {
            const objects = new Map(this.plannedObjects.map(item => [Number(item.building_id), item]));
            this.objects.forEach(item => objects.set(Number(item.building_id), item));
            const groups = new Map(this.plannedObjectGroups.map(item => [Number(item.object_group_id), item]));
            this.objectGroups.forEach(item => groups.set(Number(item.object_group_id), item));
            const toTarget = (type, field, item, canRename) => {
                const key = `${type}:${Number(item[field])}`;
                return {
                    key,
                    target_type: type,
                    target_id: Number(item[field]),
                    name: item.name,
                    planning_code: item.planning_code || null,
                    planning_color: planDisplay.normalizeTargetColor(item.planning_color) || null,
                    planning_color_source: item.planning_color_source || 'auto',
                    automatic_color: item.automatic_color || this.automaticPlanningColor(key),
                    can_edit_name: Boolean(canRename && this.permissions.can_edit),
                    can_edit_color: Boolean(this.permissions.can_edit)
                };
            };
            return [
                ...Array.from(objects.values()).map(item => toTarget('object', 'building_id', item, true)),
                ...Array.from(groups.values()).map(item => toTarget('object_group', 'object_group_id', item, !item.archived)),
                ...this.workforcePlannedObjects.map(item => toTarget('planned_object', 'planned_object_id', item, item.status === 'planned'))
            ].filter(item => item.can_edit_name || item.can_edit_color)
                .sort((left, right) => left.name.localeCompare(right.name, 'ru-RU'))
                .map(item => Object.assign(item, {
                    label: `${({object: 'Объект', object_group: 'Группа', planned_object: 'Плановый'})[item.target_type]} · ${item.planning_code ? `${item.planning_code} — ` : ''}${item.name}`
                }));
        },
        plannedObjects() {
            const objectNames = new Map(this.objects.map(object => (
                [String(Number(object.building_id)), {
                    name: object.name,
                    planning_code: object.planning_code || null,
                    planning_color: object.planning_color || null,
                    planning_color_source: object.planning_color_source || 'auto'
                }]
            )));
            const plannedIds = new Set();
            this.employees.forEach(employee => {
                (employee.intervals || []).forEach(interval => {
                    if (interval.state_type !== 'object' || !interval.building_id) return;
                    const buildingId = String(Number(interval.building_id));
                    plannedIds.add(buildingId);
                    if (!objectNames.has(buildingId) && interval.target_name) {
                        objectNames.set(buildingId, {
                            name: interval.target_name,
                            planning_code: interval.target_code || null,
                            planning_color: interval.target_color || null,
                            planning_color_source: 'auto'
                        });
                    }
                });
            });
            return Array.from(plannedIds).map(buildingId => {
                const object = objectNames.get(buildingId) || {};
                return {
                    building_id: buildingId,
                    name: object.name || `Объект #${buildingId}`,
                    planning_code: object.planning_code || null,
                    planning_color: object.planning_color || null,
                    planning_color_source: object.planning_color_source || 'auto'
                };
            }).sort((left, right) => left.name.localeCompare(right.name, 'ru-RU'));
        },
        plannedObjectGroups() {
            const groupDetails = new Map(this.objectGroups.map(group => (
                [String(Number(group.object_group_id)), {
                    name: group.name,
                    planning_code: group.planning_code || null,
                    planning_color: group.planning_color || null,
                    planning_color_source: group.planning_color_source || 'auto'
                }]
            )));
            const plannedIds = new Set();
            this.employees.forEach(employee => {
                (employee.intervals || []).forEach(interval => {
                    if (
                        interval.state_type !== 'object_group'
                        || !interval.object_group_id
                    ) return;
                    const objectGroupId = String(Number(interval.object_group_id));
                    plannedIds.add(objectGroupId);
                    if (!groupDetails.has(objectGroupId) && interval.target_name) {
                        groupDetails.set(objectGroupId, {
                            name: interval.target_name,
                            planning_code: interval.target_code || null,
                            planning_color: interval.target_color || null,
                            planning_color_source: 'auto'
                        });
                    }
                });
            });
            return Array.from(plannedIds).map(objectGroupId => {
                const details = groupDetails.get(objectGroupId) || {};
                return {
                    object_group_id: objectGroupId,
                    name: details.name || `Группа #${objectGroupId}`,
                    planning_code: details.planning_code || null,
                    planning_color: details.planning_color || null,
                    planning_color_source: details.planning_color_source || 'auto'
                };
            }).sort((left, right) => left.name.localeCompare(right.name, 'ru-RU'));
        },
        summaryRows() {
            const objectIds = new Set();
            const objectGroupIds = new Set();
            const plannedObjectIds = new Set();
            this.weeklySummary.forEach(week => {
                Object.keys(week.objects).forEach(buildingId => objectIds.add(buildingId));
                Object.keys(week.object_groups || {}).forEach(objectGroupId => (
                    objectGroupIds.add(objectGroupId)
                ));
                Object.keys(week.planned_objects || {}).forEach(plannedObjectId => (
                    plannedObjectIds.add(plannedObjectId)
                ));
            });
            const objects = this.plannedObjects
                .filter(object => objectIds.has(object.building_id))
                .map(object => Object.assign({
                    key: `object:${object.building_id}`,
                    kind: 'object',
                    building_id: object.building_id,
                    label: object.planning_code
                        ? `${object.planning_code} — ${object.name}`
                        : object.name
                }, this.planningTargetPalette(
                    'object',
                    object.building_id,
                    object.planning_color,
                    object.planning_color_source
                )));
            const objectGroups = this.plannedObjectGroups
                .filter(group => objectGroupIds.has(group.object_group_id))
                .map(group => Object.assign({
                    key: `object_group:${group.object_group_id}`,
                    kind: 'object-group',
                    object_group_id: group.object_group_id,
                    label: group.planning_code
                        ? `${group.planning_code} — ${group.name}`
                        : `Группа — ${group.name}`
                }, this.planningTargetPalette(
                    'object_group',
                    group.object_group_id,
                    group.planning_color,
                    group.planning_color_source
                )));
            const plannedObjects = this.workforcePlannedObjects
                .filter(item => plannedObjectIds.has(String(item.planned_object_id)))
                .map(item => Object.assign({
                    key: `planned_object:${item.planned_object_id}`,
                    kind: 'planned-object',
                    planned_object_id: String(item.planned_object_id),
                    label: `${item.planning_code} — ${item.name}`
                }, this.planningTargetPalette(
                    'planned_object',
                    item.planned_object_id,
                    item.planning_color,
                    item.planning_color_source
                )));
            return objects.concat(objectGroups, plannedObjects, [
                {
                    key: 'object_total', kind: 'object-total', count_key: 'object_total',
                    label: 'Всего на объектах', stacked: true,
                    description: 'Назначены по плану на объекты, группы или плановые объекты. Отдых, нераспределённые, выбывшие и сотрудники без заполненного плана сюда не входят.'
                },
                {
                    key: 'requires_distribution', kind: 'requires-distribution', count_key: 'requires_distribution',
                    label: '❌ Не распределены', stacked: true,
                    description: 'Остаются в планировании, но ещё не назначены на объект. В плане явно отмечены как «Не распределён».'
                },
                {
                    key: 'rest', kind: 'rest', count_key: 'rest', label: 'ОТДХ — отдых', stacked: true,
                    description: 'По плану находятся дома или на отдыхе. Входят в «Всего в планировании».'
                },
                {
                    key: 'loupe', kind: 'loupe', count_key: 'loupe', label: '🔍 Выбыли из планирования', stacked: true,
                    description: 'Сотрудники с отметкой 🔍. Исключены из «Всего в планировании», но остаются в «Всего сотрудников».'
                },
                {
                    key: 'missing', kind: 'missing', count_key: 'missing', label: 'План не заполнен', stacked: true,
                    description: 'Пустая ячейка: состояние сотрудника в этом столбце ещё не указано. Эти сотрудники входят в «Всего в планировании».'
                },
                {
                    key: 'planning_total', kind: 'planning-total', count_key: 'planning_total',
                    label: 'Всего в планировании',
                    stacked: true,
                    description: 'Всего в планировании = На объектах + Не распределены + ОТДХ + План не заполнен. Выбывшие из планирования не учитываются.'
                },
                {
                    key: 'total', kind: 'total', count_key: 'total', label: 'Всего сотрудников', stacked: true,
                    description: 'Все сотрудники, включённые в сводку, в том числе выбывшие из планирования. Это «Всего в планировании» + «Выбыли из планирования».'
                }
            ]);
        },
        visibleSummaryRows() {
            return this.summaryCollapsed ? [] : this.summaryRows;
        },
        summaryPresentations() {
            return this.visibleSummaryRows.map(row => {
                const planningTarget = [
                    'object',
                    'object-group',
                    'planned-object'
                ].includes(row.kind);
                const current = this.currentSummaryBreakdown(row);
                return {
                    row,
                    current: {
                        breakdown: current,
                        style: planningTarget && current.total > 0
                            ? this.planColorStyle(row.background, row.color)
                            : {},
                        title: current.total > 0
                            ? this.summaryTooltipTitle(
                                row,
                                `Состояние на ${this.formatDate(this.currentStatusDate)}`,
                                current
                            )
                            : null
                    },
                    weeks: this.weeks.map((week, weekIndex) => {
                        const breakdown = this.summaryBreakdown(row, weekIndex);
                        return {
                            week,
                            breakdown,
                            style: planningTarget && breakdown.total > 0
                                ? this.planColorStyle(row.background, row.color)
                                : {},
                            title: breakdown.total > 0
                                ? this.summaryTooltipTitle(
                                    row,
                                    this.weekTitle(week),
                                    breakdown
                                )
                                : null
                        };
                    })
                };
            });
        },
        currentStatusDate() {
            const today = currentLocalDate();
            return this.window.from <= today && today < this.window.to ? today : this.filterDate;
        },
        currentStatusWeek() {
            const date = this.currentStatusDate;
            return this.weeks.find(week => (
                week.visible_starts_on <= date && date < week.visible_ends_on
            )) || null;
        },
        currentStatusTitle() {
            if (!this.currentStatusWeek) return 'Состояние на выбранную дату';
            const filter = !this.currentAssignmentFilter.length
                ? 'все сотрудники'
                : this.currentAssignmentFilterShortLabel;
            return `Нажмите, чтобы отфильтровать сотрудников. Состояние на ${this.formatDate(this.currentStatusDate)}, ${this.currentStatusWeek.number}-я неделя. Фильтр: ${filter}`;
        },
        currentAssignmentCounts() {
            return planUtils.countsOnDate(
                this.employees,
                this.currentStatusDate,
                employee => this.currentCellInterval(employee)
            );
        },
        currentAssignmentFilterCounts() {
            return planUtils.countsOnDate(
                this.employeesMatchingOtherFilters.filter(employee => employee.excluded_from_board !== true),
                this.currentStatusDate,
                employee => this.currentCellInterval(employee)
            );
        },
        lastYearDate() {
            return planUtils.addDays(this.window.to, -1);
        },
        yearOptions() {
            const current = Number(currentLocalDate().slice(0, 4));
            const values = [];
            for (let year = current - 1; year <= current + 3; year += 1) {
                values.push({value: year, text: String(year)});
            }
            if (!values.some(option => option.value === this.year)) {
                values.push({value: this.year, text: String(this.year)});
                values.sort((left, right) => left.value - right.value);
            }
            return values;
        },
        displayPalette() {
            const sources = Object.assign({}, DEFAULT_PLANNING_COLOR_TEXT);
            Object.values(planDisplay.STATE_COLORS).forEach(([background, color]) => { sources[background] = color; });
            sources['#e4f0dc'] = '#355f26';
            sources['#dce7f0'] = '#253c50';
            sources['#f3f5f7'] = '#6e7b88';
            return Object.keys(sources).reduce((result, background) => {
                result[background] = planDisplay.colorPresentation(background, sources[background], this.contrast);
                return result;
            }, {});
        },
        displayColorVariables() {
            return Object.keys(this.displayPalette).reduce((result, background) => {
                const key = background.slice(1);
                result[`--plan-color-${key}`] = this.displayPalette[background].backgroundColor;
                result[`--plan-text-${key}`] = this.displayPalette[background].color;
                return result;
            }, {});
        },
        inspectedEmployeeId() {
            const employeeId = Number(
                this.inspectorEmployee && this.inspectorEmployee.employee_id
            );
            return employeeId > 0 ? employeeId : null;
        },
        isMobileViewport() {
            const width = Number(this.viewportSize.width || 1280);
            const height = Number(this.viewportSize.height || 720);
            if (width <= 767.98) return true;
            if (width > 1366 || height > 767.98 || typeof window === 'undefined') return false;
            return Boolean(
                window.matchMedia
                && window.matchMedia('(hover: none) and (pointer: coarse)').matches
            );
        },
        employeeColumnWidth() {
            return this.isMobileViewport ? 124 : 220;
        },
        rowSelectorColumnWidth() {
            return this.isMobileViewport ? 12 : 14;
        },
        employeeNameColumnWidth() {
            return this.employeeColumnWidth - this.rowSelectorColumnWidth;
        },
        currentColumnWidth() {
            return this.isMobileViewport ? 42 : 56;
        },
        pinnedColumnsWidth() {
            return this.employeeColumnWidth + this.currentColumnWidth;
        },
        weekWidth() {
            if (this.zoom === 'fit') {
                const reserved = this.isMobileViewport ? 224 : 326;
                return `max(14px, calc((100vw - ${reserved}px) / 53))`;
            }
            return `${planDisplay.weekWidth(this.zoom, this.isMobileViewport)}px`;
        },
        gridContentWidth() {
            if (this.zoom === 'fit') return '100%';
            const width = parseInt(this.weekWidth, 10) || 30;
            return `${this.pinnedColumnsWidth + this.weeks.length * width}px`;
        },
        gridStyle() {
            return {
                '--plan-week-width': this.weekWidth,
                '--plan-employee-width': `${this.employeeColumnWidth}px`,
                '--plan-row-selector-width': `${this.rowSelectorColumnWidth}px`,
                '--plan-employee-name-width': `${this.employeeNameColumnWidth}px`,
                '--plan-current-width': `${this.currentColumnWidth}px`,
                '--plan-pinned-width': `${this.pinnedColumnsWidth}px`,
                '--plan-columns': `var(--plan-row-selector-width) var(--plan-employee-name-width) var(--plan-current-width) repeat(${this.weeks.length}, var(--plan-week-width))`
            };
        },
        citizenshipFilterOptions() {
            const values = Array.from(new Set(this.employees.map(employee => (
                String(employee.citizenship || '').trim()
            )).filter(Boolean))).sort((left, right) => left.localeCompare(right, 'ru'));
            return [
                {value: 'all', text: 'Любое гражданство'},
                {value: '__none__', text: 'Гражданство не указано'}
            ].concat(values.map(value => ({value, text: value})));
        },
        skillFilterOptions() { return employeeFilterOptions(this.employees, 'skills'); },
        skillDescriptions() { return employeeSkillDescriptions(this.employees); },
        positionFilterOptions() { return employeeFilterOptions(this.employees, 'positions'); },
        activeCitizenshipFilterOption() {
            return this.citizenshipFilterOptions.find(option => (
                option.value === this.citizenshipFilter
            )) || null;
        },
        assignmentFilterRange() {
            const from = this.assignmentFilterScope === 'future'
                ? [this.window.from, planUtils.mondayOnOrBefore(this.observedToday)].sort().pop()
                : this.window.from;
            return from < this.window.to ? {from, to: this.window.to} : null;
        },
        assignmentFilterPeriodTitle() {
            const range = this.assignmentFilterRange;
            return range ? `Назначения с ${this.formatDate(range.from)} по ${this.formatDate(planUtils.addDays(range.to, -1))}. Прошлые колонки остаются видны.`
                : 'В открытом периоде нет текущей и будущих недель.';
        },
        assignmentFilterOptions() {
            const values = [
                {value: 'all', text: 'Все сотрудники'},
                {
                    value: 'object:any',
                    text: `Любой объект, группа или плановый объект ${this.assignmentFilterScope === 'future' ? 'с текущей недели' : this.periodMode === 'year' ? 'за год' : 'за период'}`
                }
            ];
            if (this.plannedObjects.length) {
                values.push({value: 'objects', text: '— Объекты —', disabled: true});
                this.plannedObjects.forEach(object => {
                    values.push({
                        value: `object:${object.building_id}`,
                        text: object.planning_code
                            ? `${object.planning_code} — ${object.name}`
                            : object.name
                    });
                });
            }
            if (this.plannedObjectGroups.length) {
                values.push({value: 'groups', text: '— Группы объектов —', disabled: true});
                this.plannedObjectGroups.forEach(group => {
                    values.push({
                        value: `object_group:${group.object_group_id}`,
                        text: group.planning_code ? `${group.planning_code} — ${group.name}` : group.name
                    });
                });
            }
            if (this.referencedWorkforcePlannedObjects.length) {
                values.push({
                    value: 'planned-objects',
                    text: '— Плановые объекты —',
                    disabled: true
                });
                this.referencedWorkforcePlannedObjects.forEach(item => {
                    values.push({
                        value: `planned_object:${item.planned_object_id}`,
                        text: `${item.planning_code} — ${item.name}`
                    });
                });
            }
            return values.concat([
                {value: 'states', text: '— Остальные состояния —', disabled: true},
                {value: 'state:requires_distribution', text: this.assignmentFilterScope === 'future' ? 'Есть нераспределённые недели с текущей' : 'Были не распределены'},
                {value: 'state:rest', text: this.assignmentFilterScope === 'future' ? 'Есть отдых с текущей недели' : 'Были на отдыхе'},
                {value: 'state:loupe', text: this.assignmentFilterScope === 'future' ? 'Есть выбытие с текущей недели' : 'Выбывали из планирования'},
                {value: 'state:missing', text: 'Есть незаполненные недели'}
            ]);
        },
        currentAssignmentFilterOptions() {
            const counts = this.currentAssignmentFilterCounts;
            const countedOption = (value, label, count) => ({value, label, count, text: `${label} · ${count}`});
            const values = [countedOption('all', 'Все сотрудники', counts.total)];
            values.push(countedOption('object:or-rest', 'На объектах и на отдыхе', counts.object_total + counts.rest));
            if (counts.object_total || this.currentAssignmentFilter.includes('object:any')) {
                values.push(countedOption('object:any', 'На объектах', counts.object_total));
            }
            const objects = this.plannedObjects.filter(object => (
                Number(counts.objects[object.building_id] || 0) > 0
                || this.currentAssignmentFilter.includes(`object:${object.building_id}`)
            ));
            if (objects.length) {
                values.push({value: 'objects', text: '— Объекты —', disabled: true});
                objects.forEach(object => {
                    const label = object.planning_code
                        ? `${object.planning_code} — ${object.name}`
                        : object.name;
                    values.push(countedOption(`object:${object.building_id}`, label, counts.objects[object.building_id] || 0));
                });
            }
            const groups = this.plannedObjectGroups.filter(group => (
                Number(counts.object_groups[group.object_group_id] || 0) > 0
                || this.currentAssignmentFilter.includes(`object_group:${group.object_group_id}`)
            ));
            if (groups.length) {
                values.push({
                    value: 'groups',
                    text: '— Группы объектов —',
                    disabled: true
                });
                groups.forEach(group => {
                    values.push(countedOption(`object_group:${group.object_group_id}`,
                        `${group.planning_code ? `${group.planning_code} — ` : ''}${group.name}`,
                        counts.object_groups[group.object_group_id] || 0));
                });
            }
            const plannedObjects = this.referencedWorkforcePlannedObjects.filter(item => (
                Number(counts.planned_objects[item.planned_object_id] || 0) > 0
                || this.currentAssignmentFilter.includes(`planned_object:${item.planned_object_id}`)
            ));
            if (plannedObjects.length) {
                values.push({
                    value: 'planned-objects',
                    text: '— Плановые объекты —',
                    disabled: true
                });
                plannedObjects.forEach(item => {
                    values.push(countedOption(`planned_object:${item.planned_object_id}`,
                        `${item.planning_code} — ${item.name}`, counts.planned_objects[item.planned_object_id] || 0));
                });
            }
            const states = [
                ['requires_distribution', '❌ Не распределены'],
                ['rest', 'ОТДХ — отдых'],
                ['loupe', '🔍 Выбыли из планирования'],
                ['missing', 'План не заполнен']
            ].filter(([state]) => Number(counts[state] || 0) > 0
                || this.currentAssignmentFilter.includes(`state:${state}`));
            if (states.length) {
                values.push({
                    value: 'states',
                    text: '— Остальные состояния —',
                    disabled: true
                });
                states.forEach(([state, label]) => {
                    values.push(countedOption(`state:${state}`, label, counts[state]));
                });
            }
            return values.map(option => option.disabled
                ? {...option, label: option.text.replace(/^— | —$/g, ''), count: null}
                : option);
        },
        currentAssignmentFilterShortLabel() {
            if (!this.currentAssignmentFilter.length) return 'Все';
            if (this.currentAssignmentFilter.length > 1) return `${this.currentAssignmentFilter.length} выб.`;
            const filter = this.currentAssignmentFilter[0];
            if (filter === 'object:any') return 'Объекты';
            if (filter === 'object:or-rest') return 'Объекты / отдых';
            if (filter.startsWith('object:')) {
                const buildingId = filter.slice(7);
                const object = this.plannedObjects.find(item => (
                    String(item.building_id) === buildingId
                ));
                return object && object.planning_code
                    || this.shortObjectName(object && object.name);
            }
            if (filter.startsWith('object_group:')) {
                const groupId = filter.slice(13);
                const group = this.plannedObjectGroups.find(item => (
                    String(item.object_group_id) === groupId
                ));
                return group && group.planning_code || this.shortObjectName(group && group.name || 'Группа');
            }
            if (filter.startsWith('planned_object:')) {
                const id = Number(filter.slice(15));
                const item = this.workforcePlannedObjects.find(candidate => (
                    Number(candidate.planned_object_id) === id
                ));
                return item && item.planning_code || 'План';
            }
            return ({
                'state:requires_distribution': '❌',
                'state:rest': 'ОТДХ',
                'state:loupe': '🔍',
                'state:missing': '—'
            })[filter] || 'Все';
        },
        activePlanFilterChips() {
            const chips = [];
            if (this.skillFilter.length) chips.push({key: 'skills', text: 'Навыки: ' + this.skillFilter.join(', '), variant: 'info'});
            if (this.itrFilter) chips.push({key: 'itr', text: 'ИТР', variant: 'info'});
            if (this.positionFilter.length) chips.push({key: 'positions', text: 'Должности: ' + this.positionFilter.join(', '), variant: 'info'});
            if (this.hiringFormFilter) chips.push({key: 'hiringForm', text: 'Форма найма: ' + hiringFormLabel(this.hiringFormFilter), variant: 'secondary'});
            const query = String(this.query || '').trim();
            if (query) {
                chips.push({
                    key: 'search',
                    text: `Поиск: ${query}`,
                    variant: 'primary'
                });
            }
            if (this.citizenshipFilter !== 'all') {
                chips.push({
                    key: 'citizenship',
                    text: this.activeCitizenshipFilterOption
                        ? this.activeCitizenshipFilterOption.text
                        : this.citizenshipFilter,
                    variant: 'warning'
                });
            }
            if (this.assignmentFilter !== 'all') {
                const option = this.assignmentFilterOptions.find(item => (
                    item.value === this.assignmentFilter
                ));
                chips.push({
                    key: 'year-assignment',
                    text: `${this.assignmentFilterScope === 'future' ? 'С текущей недели' : this.periodMode === 'year' ? 'За год' : 'За период'}: ${option && option.text || this.assignmentFilter}`,
                    title: `${this.assignmentFilterPeriodTitle} Нажмите, чтобы снять фильтр назначения.`,
                    variant: 'info'
                });
            }
            this.currentAssignmentFilter.forEach(value => {
                const option = this.currentAssignmentFilterOptions.find(item => (
                    item.value === value
                ));
                const label = String(option && option.text || value)
                    .replace(/\s+·\s+\d+$/, '');
                chips.push({
                    key: `current-assignment:${value}`,
                    text: `На ${this.formatDate(this.currentStatusDate)}: ${label}`,
                    variant: 'warning'
                });
            });
            return chips;
        },
        hasActivePlanFilters() {
            return this.activePlanFilterChips.length > 0;
        },
        employeesMatchingOtherFilters() {
            const words = registrySearchWords(this.query);
            const index = this.employeeSearchIndex;
            return this.employees.filter(employee => (
                matchesWorkforceSearch(index.get(employee) || '', words) &&
                matchesHiringForm(employee, this.hiringFormFilter) &&
                matchesEmployeeFilters(employee, this.skillFilter, this.positionFilter, this.itrFilter) &&
                this.employeeMatchesCitizenshipFilter(employee) &&
                this.employeeMatchesAssignmentFilter(employee)
            ));
        },
        employeeSearchIndex() {
            return new Map(this.employees.map(employee => [employee, workforceEmployeeSearchText(employee)]));
        },
        filteredEmployees() {
            return this.employeesMatchingOtherFilters.filter(this.employeeMatchesCurrentAssignmentFilter);
        },
        employeeRowsContentOffset() {
            // Match the summary row heights in CSS, including the stacked totals.
            return 54 + this.visibleSummaryRows.reduce((height, row) => (
                height + (row.stacked ? 36 : 21)
            ), 0) + 28;
        },
        canEdit() {
            return this.permissions.can_edit === true;
        },
        canCreatePlannedObjects() {
            return this.permissions.can_edit === true;
        },
        importEmployeeIssueCount() {
            if (!this.importSummary || !this.importSummary.employees) return 0;
            return Object.keys(this.importSummary.employees).reduce((sum, status) => (
                ['matched', 'excluded'].includes(status)
                    ? sum
                    : sum + Number(this.importSummary.employees[status] || 0)
            ), 0);
        },
        importObjectMatchedCount() {
            return this.importSummary && this.importSummary.objects
                ? Number(this.importSummary.objects.matched || 0)
                : 0;
        },
        importConflictCount() {
            return this.importConflictRows.reduce((sum, row) => (
                sum + row.conflicts.filter(conflict => (
                    !this.conflictDecision(row.source_record_key, conflict.key)
                )).length
            ), 0);
        },
        unmatchedImportEmployees() {
            return (this.reconciliation.employees || []).filter(row => (
                row.status !== 'matched'
            ));
        },
        importConflictRows() {
            return (this.reconciliation.employees || []).filter(row => (
                Array.isArray(row.conflicts) && row.conflicts.length
            ));
        },
        hasSelection() {
            return this.selectedCellKeys.length > 0;
        },
        selectionKeySet() {
            return new Set(this.selectedCellKeys);
        },
        selectedCellCoordinates() {
            return this.selectedCellKeys.reduce((result, key) => {
                const coordinate = selectionUtils.parseCellKey(key);
                if (!coordinate) return result;
                const employee = this.filteredEmployeeById.get(coordinate.employee_id);
                const week = this.weekByKey.get(coordinate.week_key);
                if (employee && week) result.push({employee, week});
                return result;
            }, []);
        },
        selectedEmployeeIds() {
            return new Set(this.selectedCellCoordinates.map(coordinate => (
                Number(coordinate.employee.employee_id)
            )));
        },
        selectedFullRowEmployeeIds() {
            const counts = this.selectedCellCoordinates.reduce((result, coordinate) => {
                const employeeId = Number(coordinate.employee.employee_id);
                result.set(employeeId, Number(result.get(employeeId) || 0) + 1);
                return result;
            }, new Map());
            const required = this.weeks.length;
            return new Set(Array.from(counts.entries()).filter(([, count]) => (
                required > 0 && count >= required
            )).map(([employeeId]) => employeeId));
        },
        selectedFullRowEmployees() {
            const selectedIds = this.selectedFullRowEmployeeIds;
            return this.filteredEmployees.filter(employee => (
                selectedIds.has(Number(employee.employee_id))
            ));
        },
        hasSelectedFullRows() {
            return this.selectedFullRowEmployees.length > 0;
        },
        selectedSingleCell() {
            return this.selectedCellCoordinates.length === 1
                ? this.selectedCellCoordinates[0]
                : null;
        },
        selectedArrivalInterval() {
            const selected = this.selectedSingleCell;
            if (!selected) return null;
            const state = this.cellState(selected.employee, selected.week);
            const interval = state && state.interval || null;
            return interval && ['object', 'object_group', 'planned_object'].includes(
                interval.state_type
            ) ? interval : null;
        },
        selectedBaselinePresence() {
            const selected = this.selectedSingleCell;
            return selected ? planUtils.baselinePresenceForRange(
                selected.employee, this.selectedArrivalInterval,
                selected.week.visible_starts_on, selected.week.visible_ends_on
            ) : null;
        },
        selectedArrivalConfirmation() {
            const selected = this.selectedSingleCell;
            const interval = this.selectedArrivalInterval;
            if (!selected || !interval) return null;
            const confirmations = Array.isArray(selected.employee.arrival_confirmations)
                ? selected.employee.arrival_confirmations
                : [];
            const overlapping = confirmations.filter(confirmation => (
                (confirmation.period_starts_on < interval.ends_on && confirmation.period_ends_on > interval.starts_on)
                || (!confirmation.source_plan_interval_id && (confirmation.confirmed_presence || []).some(period =>
                    period.starts_on < interval.ends_on && (!period.ends_on || period.ends_on > interval.starts_on)))
            ));
            return overlapping.find(confirmation => (
                this.arrivalTargetKey(confirmation) === this.arrivalTargetKey(interval)
            )) || overlapping.find(confirmation => (
                confirmation.arrival_date >= selected.week.visible_starts_on
                && confirmation.arrival_date < selected.week.visible_ends_on
            )) || null;
        },
        arrivalActionAvailable() {
            const selected = this.selectedSingleCell;
            return Boolean(
                selected
                && this.canConfirmArrivalCell(selected.employee, selected.week)
            );
        },
        arrivalDateOptions() {
            const selected = this.selectedSingleCell;
            if (!selected) return [];
            const labels = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
            return labels.map((label, index) => {
                const date = planUtils.addDays(selected.week.starts_on, index);
                return {
                    date,
                    label,
                    short_date: this.shortDate(date),
                    disabled: date > this.observedToday
                };
            });
        },
        arrivalTargetLabel() {
            const interval = this.selectedArrivalInterval;
            return interval ? this.stateTitle(interval) : '';
        },
        arrivalSubmitLabel() {
            if (this.arrivalDraft.pending) return 'Сохраняем…';
            if (this.arrivalDraft.warning) return 'Подтвердить несмотря на расхождения';
            return this.arrivalDraft.mode === 'correct'
                ? 'Исправить дату прибытия'
                : 'Подтвердить прибытие';
        },
        selectionRanges() {
            return selectionUtils.selectionRanges(
                this.selectedCellKeys,
                this.filteredEmployees,
                this.weeks,
                this.window
            );
        },
        selectionFirstRange() {
            return this.selectionRanges[0] || null;
        },
        selectionEmployeeRecords() {
            const ids = new Set(this.selectionRanges.map(range => range.employee_id));
            return this.employees.filter(employee => ids.has(Number(employee.employee_id)));
        },
        selectionUnifiedPeriod() {
            return selectionUtils.unifiedSelectionPeriod(this.selectionRanges);
        },
        exactPeriodAvailable() {
            return Boolean(this.selectionUnifiedPeriod);
        },
        selectionSummaryLabel() {
            const employees = this.selectionEmployeeRecords.length;
            const cells = this.selectedCellKeys.length;
            return `${employees} ${this.pluralize(employees, ['сотрудник', 'сотрудника', 'сотрудников'])} · ${cells} ${this.pluralize(cells, ['ячейка', 'ячейки', 'ячеек'])}`;
        },
        selectionPeriodLabel() {
            if (this.selectionUnifiedPeriod) {
                return `${this.formatDate(this.selectionUnifiedPeriod.starts_on)} — ${this.formatDate(planUtils.addDays(this.selectionUnifiedPeriod.ends_on, -1))}`;
            }
            const count = this.selectionRanges.length;
            return `${count} ${this.pluralize(count, ['диапазон', 'диапазона', 'диапазонов'])} с разными периодами`;
        },
        selectedCurrentStateLabel() {
            if (!this.selectedCellCoordinates.length) return 'не выбрано';
            const labels = new Set(this.selectedCellCoordinates.map(({employee, week}) => {
                const state = this.cellState(employee, week);
                const interval = state && state.interval;
                return this.intervalCellLabel(interval) || 'План не заполнен';
            }));
            return labels.size === 1 ? Array.from(labels)[0] : 'Смешанные значения';
        },
        stateOptions() {
            return [
                {value: '', text: 'Выберите состояние', disabled: true},
                {value: 'target', text: 'Объект, группа или плановый объект'},
                {value: 'requires_distribution', text: '❌ Не распределён'},
                {value: 'rest', text: 'ОТДХ — отдых'},
                {value: 'loupe', text: '🔍 Выбыл из планирования'},
                {value: 'clear', text: 'Очистить план на период'}
            ];
        },
        quickStateOptions() {
            return this.stateOptions.filter(option => (
                option.value && option.value !== 'target'
            ));
        },
        quickEditorStateOptions() {
            return this.quickStateOptions.filter(option => option.value !== 'clear');
        },
        editorStateChoice() {
            return ['object', 'object_group', 'planned_object'].includes(this.editor.state_type)
                ? 'target'
                : this.editor.state_type;
        },
        targetOptions() {
            const objects = this.objects;
            const groups = this.objectGroups;
            const plannedObjects = this.workforcePlannedObjects;
            return objects.filter(object => object.is_active !== false).map(object => ({
                value: `object:${Number(object.building_id)}`,
                target_type: 'object',
                target_id: Number(object.building_id),
                label: object.planning_code
                    ? `Объект · ${object.planning_code} — ${object.name}`
                    : `Объект · ${object.name}`
            })).concat(groups.filter(group => group.archived !== true).map(group => ({
                value: `object_group:${Number(group.object_group_id)}`,
                target_type: 'object_group',
                target_id: Number(group.object_group_id),
                label: group.planning_code
                    ? `Группа · ${group.planning_code} — ${group.name}`
                    : `Группа · ${group.name}`
            })), plannedObjects.filter(item => item.status === 'planned').map(item => ({
                value: `planned_object:${Number(item.planned_object_id)}`,
                target_type: 'planned_object',
                target_id: Number(item.planned_object_id),
                label: `Плановый · ${item.planning_code} — ${item.name}`
            })));
        },
        selectedTargetOption() {
            const value = this.editor.state_type === 'object'
                ? `object:${Number(this.editor.building_id)}`
                : this.editor.state_type === 'object_group'
                    ? `object_group:${Number(this.editor.object_group_id)}`
                    : this.editor.state_type === 'planned_object'
                        ? `planned_object:${Number(this.editor.planned_object_id)}`
                    : '';
            return this.targetOptions.find(option => option.value === value) || null;
        },
        commandOptions() {
            const states = [
                {value: 'state:requires_distribution', state_type: 'requires_distribution', label: '❌ Не распределён', search: 'крест не распределен нераспределен свободен'},
                {value: 'state:rest', state_type: 'rest', label: 'ОТДХ — отдых', search: 'отдых отд отдх отпуск'},
                {value: 'state:loupe', state_type: 'loupe', label: '🔍 Выбыл из планирования', search: 'лупа выбыл уволен пропал'}
            ];
            const targets = this.targetOptions.map(option => ({
                value: option.value,
                target: option,
                label: option.label,
                hint: option.target_type === 'object_group'
                    ? 'Группа объектов'
                    : (option.target_type === 'planned_object'
                        ? 'Плановый объект · контракт не заключён'
                        : 'Объект'),
                search: `${option.label} ${option.value}`
            }));
            return states.concat(targets);
        },
        filteredCommandOptions() {
            const query = String(this.editorQuery || '').trim().toLocaleLowerCase('ru-RU');
            if (!query) {
                return this.commandOptions.filter(option => option.target);
            }
            const tokens = query.split(/\s+/).filter(Boolean);
            return this.commandOptions.filter(option => {
                const haystack = `${option.label} ${option.search || ''}`
                    .toLocaleLowerCase('ru-RU');
                return tokens.every(token => haystack.includes(token));
            });
        },
        selectionImpact() {
            const result = {replaced: 0, unchanged: 0, empty: 0};
            if (!this.editorDirty) return result;
            const target = this.editorStateSignature();
            this.selectedCellCoordinates.forEach(({employee, week}) => {
                const state = this.cellState(employee, week);
                const interval = state && state.interval;
                const current = this.intervalStateSignature(interval);
                let changes = this.editor.state_type === 'clear'
                    ? Boolean(interval)
                    : current !== target || this.editor.state_type === 'requires_distribution';
                if (!changes && this.editor.note_mode === 'set') {
                    changes = String(interval && interval.note || '') !==
                        String(this.editor.note || '').trim();
                } else if (!changes && this.editor.note_mode === 'clear') {
                    changes = Boolean(interval && interval.note);
                }
                if (
                    !changes
                    && this.editor.state_type === 'loupe'
                    && this.editor.loupe_reason_mode === 'set'
                ) {
                    changes = String(interval && interval.loupe_reason || '') !==
                        String(this.editor.loupe_reason || '').trim();
                } else if (
                    !changes
                    && this.editor.state_type === 'loupe'
                    && this.editor.loupe_reason_mode === 'clear'
                ) {
                    changes = Boolean(interval && interval.loupe_reason);
                }
                if (!changes) result.unchanged += 1;
                else if (interval) result.replaced += 1;
                else result.empty += 1;
            });
            return result;
        },
        editorSelectionLabel() {
            if (this.editor.state_type === 'clear') return 'Очистить план';
            if (['object', 'object_group', 'planned_object'].includes(this.editor.state_type)) {
                return this.selectedTargetOption && this.selectedTargetOption.label || 'Объект не выбран';
            }
            const option = this.stateOptions.find(item => item.value === this.editor.state_type);
            return option && option.text || 'Состояние не выбрано';
        },
        editorApplyLabel() {
            const cells = this.selectedCellKeys.length;
            return this.editor.state_type === 'clear'
                ? `Очистить ${cells} ${this.pluralize(cells, ['ячейку', 'ячейки', 'ячеек'])}`
                : `Применить к ${cells} ${this.pluralize(cells, ['ячейке', 'ячейкам', 'ячейкам'])}`;
        },
        quickEditorStyle() {
            if (this.isMobileViewport) return {};
            const availableHeight = Math.max(
                180,
                Number(this.viewportSize.height || 720) -
                    Number(this.quickEditorPosition.top || 0) - 8
            );
            return {
                top: `${this.quickEditorPosition.top}px`,
                left: `${this.quickEditorPosition.left}px`,
                maxHeight: `${availableHeight}px`
            };
        },
        saveAllowed() {
            if (!this.planWindowReady || this.loading) return false;
            if (!this.hasSelection || !this.editorDirty || !this.editor.state_type) return false;
            if (
                this.exactPeriodAvailable && (
                    !this.exactStartsOn
                    || !this.exactEndsOnInclusive
                    || this.exactEndsOnInclusive < this.exactStartsOn
                )
            ) return false;
            if (this.editor.state_type === 'object' && !this.editor.building_id) {
                return false;
            }
            if (
                this.editor.state_type === 'object_group'
                && !this.editor.object_group_id
            ) return false;
            if (
                this.editor.state_type === 'planned_object'
                && !this.editor.planned_object_id
            ) return false;
            if (this.editor.note_mode === 'set' && !String(this.editor.note || '').trim()) {
                return false;
            }
            if (
                this.editor.state_type === 'loupe'
                && this.editor.loupe_reason_mode === 'set'
                && !String(this.editor.loupe_reason || '').trim()
            ) return false;
            if (!this.selectionImpact.replaced && !this.selectionImpact.empty) {
                return false;
            }
            return true;
        },
        plannedObjectDraftValid() {
            const code = String(this.plannedObjectDraft.planning_code || '').trim();
            if (!code || code.length > 16) return false;
            if (!String(this.plannedObjectDraft.name || '').trim()) return false;
            if (
                this.plannedObjectDraft.conflict_owner
                && (
                    !this.plannedObjectDraftCanReassignCode
                    || !String(this.plannedObjectDraft.rename_conflict_to || '').trim()
                )
            ) return false;
            return true;
        },
        plannedObjectDraftCanReassignCode() {
            return this.permissions.can_edit === true;
        },
        plannedObjectDraftTitle() {
            return 'Создать плановый объект';
        },
        plannedObjectDraftActionLabel() {
            return 'Создать плановый объект';
        },
        plannedObjectReplacementOptions() {
            const values = this.targetOptions.filter(option => (
                ['object', 'object_group'].includes(option.target_type)
            ));
            if (this.plannedObjectAction.mode === 'cancel') {
                return [{
                    value: 'requires_distribution',
                    target_type: 'requires_distribution',
                    target_id: null,
                    label: '❌ Не распределён'
                }].concat(values);
            }
            return values;
        },
        plannedObjectActionBlockedReason() {
            const action = this.plannedObjectAction;
            if (!action.item) return '';
            if (!this.plannedObjectsCanManage || !this.permissions.can_edit) {
                return 'Для этого действия нужно право редактирования плана.';
            }
            if (!action.effective_from) return 'Выберите день нужной недели.';
            const targetTypes = action.mode === 'link'
                ? ['object', 'object_group']
                : ['requires_distribution', 'object', 'object_group'];
            if (!action.target || !targetTypes.includes(action.target.target_type)) {
                return action.mode === 'link'
                    ? 'Выберите объект или группу в Аргусе.'
                    : 'Выберите, чем заменить будущий план.';
            }
            if (action.mode === 'cancel' && String(action.reason || '').trim().length < 3) {
                return 'Укажите причину отмены — минимум 3 символа.';
            }
            if (action.impact_loading) return 'Дождитесь расчёта изменений.';
            if (!action.impact || action.impact_date !== action.effective_from) {
                return 'Для продолжения нужен расчёт изменений за выбранную неделю.';
            }
            return '';
        },
        plannedObjectActionValid() {
            return Boolean(this.plannedObjectAction.item)
                && ['link', 'cancel'].includes(this.plannedObjectAction.mode)
                && !this.plannedObjectActionBlockedReason;
        },
        boardRoute() {
            const date = this.selectionFirstRange
                ? this.selectionFirstRange.starts_on
                : this.currentStatusDate;
            return {name: 'employees-workforce', query: {date}};
        },
        syncStatusLabel() {
            if (this.syncStatus === 'connected') return 'Совместная работа включена';
            if (this.syncStatus === 'offline') return 'Нет сети';
            if (this.syncStatus === 'connecting') return 'Подключение…';
            return 'Переподключение…';
        }
    },
    watch: {
        loupePreviewKey: {
            immediate: true,
            async handler(key) {
                const request = ++this.loupePreviewRequest;
                this.loupePreview = []; this.loupePreviewError = ''; this.loupePreviewPending = Boolean(key);
                if (!key) return;
                try {
                    const response = await api.post('/api/employees/workforce/plan/ranges/preview', JSON.parse(key), {ignoreModal: true});
                    if (request === this.loupePreviewRequest) this.loupePreview = responseData(response).effects || [];
                } catch (error) {
                    if (request === this.loupePreviewRequest) this.loupePreviewError = this.errorText(error, 'Не удалось рассчитать продолжение');
                } finally { if (request === this.loupePreviewRequest) this.loupePreviewPending = false; }
            }
        },
        filterDate() {
            this.$nextTick(this.scrollToFilterWeek);
        },
        query() {
            this.resetSelectionForFilter();
        },
        assignmentFilter() {
            this.resetSelectionForFilter();
        },
        assignmentFilterScope() {
            this.resetSelectionForFilter();
        },
        currentAssignmentFilter() {
            this.resetSelectionForFilter();
        },
        hiringFormFilter() { this.resetSelectionForFilter(); },
        skillFilter() { this.resetSelectionForFilter(); },
        itrFilter() { this.resetSelectionForFilter(); },
        positionFilter() { this.resetSelectionForFilter(); },
        citizenshipFilter() {
            this.resetSelectionForFilter();
        }
    },
    async mounted() {
        this._workforcePlanDestroyed = false;
        this._selectionDomKeySet = new Set(this.selectedCellKeys);
        this._pendingSelectedCellKeys = null;
        this._ownWorkforceMutationIds = new Map();
        this._planLoadPromise = null;
        this._queuedPlanLoad = null;
        this.updateViewportSize();
        this.restoreSummaryCollapsed();
        const routeReplaced = await this.normalizePlanRouteDate();
        if (routeReplaced || this._workforcePlanDestroyed) return;
        await this.loadPlan();
        if (this._workforcePlanDestroyed) return;
        this.showMobileContextHintIfNeeded();
        this.setupRealtime();
        window.addEventListener('mouseup', this.stopCellDrag);
        window.addEventListener('blur', this.stopCellDrag);
        window.addEventListener('focus', this.handlePlanWindowFocus);
        window.addEventListener('keydown', this.handlePlanKeydown);
        window.addEventListener('mousemove', this.moveQuickEditor);
        window.addEventListener('resize', this.updateViewportSize);
        window.addEventListener('mouseup', this.stopQuickEditorDrag);
        document.addEventListener('mousedown', this.handlePlanDocumentPointer);
        document.addEventListener('pointerdown', this.handleCurrentFilterOutside, true);
        document.addEventListener(
            'visibilitychange',
            this.handlePlanVisibilityChange
        );
        this.presenceCleanupTimer = window.setInterval(
            this.cleanupRemoteEditors,
            5000
        );
        this.centerCurrentWeek();
    },
    activated() {
        this.zoom = planDisplay.DEFAULT_ZOOM;
        this.numericZoom = planDisplay.DEFAULT_ZOOM;
        this.contrast = planDisplay.readContrast();
        this.$nextTick(this.centerCurrentWeek);
    },
    beforeDestroy() {
        this._workforcePlanDestroyed = true;
        this._queuedPlanLoad = null;
        this.emitPlanPresence(true);
        socket.off('connect', this.onRealtimeConnect);
        socket.off('disconnect', this.onRealtimeDisconnect);
        socket.off('connect_error', this.onRealtimeDisconnect);
        socket.off('workforce:changed', this.onWorkforceChanged);
        socket.off('workforce:plan-presence', this.onPlanPresence);
        if (socket.connected) socket.emit('workforce:unsubscribe');
        window.removeEventListener('mouseup', this.stopCellDrag);
        window.removeEventListener('blur', this.stopCellDrag);
        window.removeEventListener('focus', this.handlePlanWindowFocus);
        window.removeEventListener('keydown', this.handlePlanKeydown);
        window.removeEventListener('mousemove', this.moveQuickEditor);
        window.removeEventListener('resize', this.updateViewportSize);
        window.removeEventListener('mouseup', this.stopQuickEditorDrag);
        document.removeEventListener('mousedown', this.handlePlanDocumentPointer);
        document.removeEventListener('pointerdown', this.handleCurrentFilterOutside, true);
        document.removeEventListener(
            'visibilitychange',
            this.handlePlanVisibilityChange
        );
        [
            'realtimeRefreshTimer',
            'presenceHeartbeatTimer',
            'presenceCleanupTimer'
        ].forEach(field => {
            if (this[field]) window.clearInterval(this[field]);
            this[field] = null;
        });
        if (this._mobileContextPreviewTimer) {
            window.clearTimeout(this._mobileContextPreviewTimer);
            this._mobileContextPreviewTimer = null;
        }
        if (this._mobileContextHintTimer) {
            window.clearTimeout(this._mobileContextHintTimer);
            this._mobileContextHintTimer = null;
        }
        this.cancelSelectionRectangle();
        this._ownWorkforceMutationIds = null;
    },
    methods: {
        retryLoupePreview() {
            this.$options.watch.loupePreviewKey.handler.call(this, this.loupePreviewKey);
        },
        timesheetArrivalLabel,
        setPlanZoom(value) {
            const viewport = this.$refs.viewport;
            const oldWidth = this.zoom === 'fit'
                ? Math.max(14, (Number(this.viewportSize.width) - (this.isMobileViewport ? 224 : 326)) / 53)
                : planDisplay.weekWidth(this.zoom, this.isMobileViewport);
            const weekOffset = viewport ? viewport.scrollLeft / oldWidth : 0;
            this.zoom = planDisplay.normalizeZoom(value);
            this.numericZoom = this.zoom;
            this.$nextTick(() => this.setPlanScrollLeft(weekOffset * planDisplay.weekWidth(this.zoom, this.isMobileViewport)));
        },
        toggleFitZoom() {
            if (this.zoom === 'fit') {
                this.zoom = this.numericZoom;
                this.$nextTick(() => this.setPlanScrollLeft(this._scrollBeforeFit || 0));
            } else {
                this._scrollBeforeFit = this.$refs.viewport ? this.$refs.viewport.scrollLeft : 0;
                this.numericZoom = this.zoom;
                this.zoom = 'fit';
            }
        },
        setPlanContrast(value) {
            this.contrast = planDisplay.normalizeContrast(value);
            planDisplay.writeContrast(this.contrast);
        },
        planColorStyle(background, color) {
            if (!this.contrast) return {backgroundColor: background, color};
            return this.displayPalette[background] || planDisplay.colorPresentation(background, color, this.contrast);
        },
        setEmployeeNameFormat(value) {
            this.employeeNameFormatState.value =
                employeeNameFormat.writeEmployeeNameFormat(value);
        },
        readRowCopyHintCount() {
            try {
                const value = Number(window.localStorage.getItem(
                    PLAN_ROW_COPY_HINT_STORAGE_KEY
                ));
                return Number.isFinite(value) ? Math.max(0, value) : 0;
            } catch (error) {
                return this.rowCopyHintSessionCount;
            }
        },
        writeRowCopyHintCount(value) {
            const count = Math.max(0, Number(value) || 0);
            this.rowCopyHintSessionCount = count;
            try {
                window.localStorage.setItem(
                    PLAN_ROW_COPY_HINT_STORAGE_KEY,
                    String(count)
                );
            } catch (error) {
                // Подсказка продолжает работать в рамках текущего открытия.
            }
        },
        showRowCopyHintIfNeeded(modifierUsed) {
            if (modifierUsed) {
                this.hideRowCopyHint(true);
                return;
            }
            if (this.rowCopyHintVisible) return;
            const count = Math.max(
                this.rowCopyHintSessionCount,
                this.readRowCopyHintCount()
            );
            if (count >= PLAN_ROW_COPY_HINT_LIMIT) return;
            this.rowCopyHintVisible = true;
            this.writeRowCopyHintCount(count + 1);
        },
        hideRowCopyHint(permanently) {
            this.rowCopyHintVisible = false;
            if (permanently) {
                this.writeRowCopyHintCount(PLAN_ROW_COPY_HINT_LIMIT);
            }
        },
        async writeClipboardText(text) {
            const value = String(text || '');
            if (!value) return false;
            if (
                typeof navigator !== 'undefined'
                && navigator.clipboard
                && typeof navigator.clipboard.writeText === 'function'
            ) {
                try {
                    await navigator.clipboard.writeText(value);
                    return true;
                } catch (error) {
                    // Старые WebView и запрещённый Clipboard API используют fallback.
                }
            }
            if (
                typeof document === 'undefined'
                || !document.body
                || typeof document.execCommand !== 'function'
            ) return false;
            const activeElement = document.activeElement;
            const textarea = document.createElement('textarea');
            textarea.value = value;
            textarea.setAttribute('readonly', 'readonly');
            textarea.style.position = 'fixed';
            textarea.style.left = '-9999px';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            textarea.setSelectionRange(0, textarea.value.length);
            let copied = false;
            try {
                copied = document.execCommand('copy');
            } finally {
                document.body.removeChild(textarea);
                if (activeElement && typeof activeElement.focus === 'function') {
                    try {
                        activeElement.focus({preventScroll: true});
                    } catch (error) {
                        activeElement.focus();
                    }
                }
            }
            return copied;
        },
        async copyEmployeeNames(employees) {
            const payload = employeeNameFormat.employeeNameCopyPayload(
                employees,
                this.employeeNameFormatState.value,
                3,
                this.copyNamesWithDetails
            );
            if (!payload.names.length) {
                this.$bvToast.toast('Нет ФИО для копирования.', {
                    variant: 'warning', solid: true, autoHideDelay: 2200
                });
                return;
            }
            const copied = await this.writeClipboardText(payload.text);
            if (!copied) {
                this.$bvToast.toast(
                    'Браузер не разрешил запись в буфер обмена.',
                    {variant: 'danger', solid: true, autoHideDelay: 3000}
                );
                return;
            }
            this.hideRowCopyHint(true);
            const preview = payload.preview.join(' · ')
                + (payload.remaining ? ` · …ещё ${payload.remaining}` : '');
            this.$bvToast.toast(preview, {
                title: `Скопировано ${payload.names.length} ФИО`,
                variant: 'success',
                solid: true,
                autoHideDelay: 3200
            });
        },
        copySelectedEmployeeNames() {
            return this.copyEmployeeNames(this.selectedFullRowEmployees);
        },
        copyVisibleEmployeeNames() {
            return this.copyEmployeeNames(this.filteredEmployees);
        },
        createWorkforceMutationId() {
            return [
                'plan',
                Date.now().toString(36),
                Math.random().toString(36).slice(2, 12)
            ].join('-');
        },
        rememberOwnWorkforceMutation(mutationId) {
            if (!(this._ownWorkforceMutationIds instanceof Map)) {
                this._ownWorkforceMutationIds = new Map();
            }
            const now = Date.now();
            this._ownWorkforceMutationIds.forEach((expiresAt, id) => {
                if (Number(expiresAt) <= now) this._ownWorkforceMutationIds.delete(id);
            });
            this._ownWorkforceMutationIds.set(String(mutationId), now + 60000);
        },
        forgetOwnWorkforceMutation(mutationId) {
            if (this._ownWorkforceMutationIds instanceof Map) {
                this._ownWorkforceMutationIds.delete(String(mutationId || ''));
            }
        },
        consumeOwnWorkforceMutation(event) {
            const mutationId = event && event.mutation_id;
            if (!mutationId || !(this._ownWorkforceMutationIds instanceof Map)) {
                return false;
            }
            const expiresAt = this._ownWorkforceMutationIds.get(String(mutationId));
            this._ownWorkforceMutationIds.delete(String(mutationId));
            return Number(expiresAt || 0) > Date.now();
        },
        browserStorage() {
            return typeof window !== 'undefined' ? window.localStorage : null;
        },
        restoreSummaryCollapsed() {
            const storage = this.browserStorage();
            if (!storage) {
                this.summaryCollapsed = this.isMobileViewport;
                return;
            }
            try {
                const stored = storage.getItem(this.summaryCollapsedStorageKey);
                this.summaryCollapsed = stored === null
                    ? this.isMobileViewport
                    : stored === '1';
            } catch (error) {
                // Локальная настройка не должна мешать работе плана.
            }
        },
        toggleSummary() {
            this.summaryCollapsed = !this.summaryCollapsed;
            const storage = this.browserStorage();
            if (storage) {
                try {
                    storage.setItem(
                        this.summaryCollapsedStorageKey,
                        this.summaryCollapsed ? '1' : '0'
                    );
                } catch (error) {
                    // Сводка продолжит работать, даже если localStorage недоступен.
                }
            }
            this.$nextTick(this.updateVirtualEmployeeRange);
        },
        replacePlanRouteQuery(query) {
            return this.$router.replace({
                name: 'employees-workforce-plan',
                query
            }).then(() => true).catch(error => {
                if (!error || error.name !== 'NavigationDuplicated') throw error;
                return false;
            });
        },
        async normalizePlanRouteDate() {
            const query = Object.assign({}, this.$route && this.$route.query || {});
            if (this.routeDateIntent && query.plan_intent === 'focus') return false;
            if (this.routeDateIntent) {
                if (!Object.prototype.hasOwnProperty.call(query, 'plan_intent')) {
                    return false;
                }
                delete query.plan_intent;
                return this.replacePlanRouteQuery(query);
            }
            if (
                String(query.date || '') === this.observedToday
                && Object.keys(query).length === 1
            ) return false;
            return this.replacePlanRouteQuery({date: this.observedToday});
        },
        setPeriodMode(mode) {
            if (this.periodNavigationDisabled || mode === this.periodMode) return;
            const date = this.filterDate || this.periodAnchorDate;
            planUtils.writePlanViewMode(this.currentUser, mode);
            return this.navigatePlanPeriod(date, mode);
        },
        setPlanYear(value) {
            const year = Number(value);
            if (this.periodNavigationDisabled || year === this.year) return;
            const today = currentLocalDate();
            return this.navigatePlanPeriod(today.startsWith(`${year}-`) ? today : `${year}-01-01`, 'year');
        },
        shiftPlanPeriod(weeks) {
            if (this.periodNavigationDisabled || this.periodMode !== 'rolling') return;
            return this.navigatePlanPeriod(planUtils.addDays(this.periodAnchorDate, weeks * 7));
        },
        navigatePlanPeriod(date, mode) {
            if (this.periodNavigationDisabled) return Promise.resolve();
            this.routeFocusApplied = true;
            this.focusedEmployeeId = null;
            this.routeFocusMessage = '';
            const viewport = this.$refs.viewport;
            // The viewport is unmounted during a load; retain the original vertical
            // position when another navigation replaces the queued request.
            this._periodScrollTop = viewport ? viewport.scrollTop : (this._periodScrollTop || 0);
            const previousKey = this.planLoadKey();
            this.clearSelection();
            this.closeEmployeeInspector();
            this.assignmentFilter = 'all';
            this.currentAssignmentFilter = [];
            this.currentFilterOpen = false;
            this.periodMode = mode || this.periodMode;
            this.periodAnchorDate = date;
            this.year = Number(date.slice(0, 4));
            this.filterDate = date;
            if (previousKey === this.planLoadKey() && this.planWindowReady && !this._planLoadPromise) {
                return this.$nextTick(() => this.restorePeriodScroll(date, this._periodScrollTop));
            }
            return this.loadPlan(true, {centerDate: date, scrollTop: this._periodScrollTop});
        },
        restorePeriodScroll(date, scrollTop) {
            const viewport = this.$refs.viewport;
            if (!viewport) return;
            viewport.scrollTop = Math.max(0, Math.min(scrollTop || 0, viewport.scrollHeight - viewport.clientHeight));
            this.scrollToPlanDate(date);
            this.updateVirtualEmployeeRange();
        },
        retryPlanPeriod() {
            return this.loadPlan(true, {centerDate: this.filterDate, scrollTop: this._periodScrollTop || 0});
        },
        goToToday() {
            if (this.periodNavigationDisabled) return;
            const today = currentLocalDate();
            this.observedToday = today;
            this.routeDateIntent = false;
            this.routeSelectionApplied = true;
            this.navigatePlanPeriod(today);
            this.replacePlanRouteQuery({date: today});
        },
        handlePlanWindowFocus() {
            const today = currentLocalDate();
            if (today === this.observedToday) return;
            if (this.hasSelection || this.saving) return;
            this.goToToday();
        },
        handlePlanVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.handlePlanWindowFocus();
            }
        },
        currentAssignmentOptionSelected(value) {
            return value === 'all' ? !this.currentAssignmentFilter.length : this.currentAssignmentFilter.includes(value);
        },
        sizeCurrentAssignmentDropdown() {
            this.$nextTick(() => {
                const select = this.$refs.currentAssignmentSelect;
                const menu = select && select.$el.querySelector('.vs__dropdown-menu');
                if (!menu) return;
                const left = menu.getBoundingClientRect().left;
                menu.style.maxWidth = `${Math.max(0, window.innerWidth - left - 16)}px`;
            });
        },
        closeCurrentAssignmentFilter(restoreFocus = true) {
            this.currentFilterOpen = false;
            if (restoreFocus) this.$nextTick(() => {
                const trigger = document.getElementById('workforce-plan-current-filter');
                if (trigger) trigger.focus({preventScroll: true});
            });
        },
        handleCurrentFilterOutside(event) {
            if (!this.currentFilterOpen || !event || !event.target) return;
            if (event.target.closest && event.target.closest(
                '.workforce-plan-current-filter-popover, #workforce-plan-current-filter'
            )) return;
            this.closeCurrentAssignmentFilter(false);
        },
        setCurrentAssignmentFilter(values) {
            const selected = Array.isArray(values) ? values : [];
            this.currentAssignmentFilter = selected.includes('all') && this.currentAssignmentFilter.length
                ? [] : Array.from(new Set(selected.filter(value => value !== 'all')));
            this.clearSelection();
        },
        clearPlanFilterChip(key) {
            if (key === 'skills') this.skillFilter = [];
            if (key === 'itr') this.itrFilter = false;
            if (key === 'positions') this.positionFilter = [];
            if (key === 'search') this.query = '';
            if (key === 'hiringForm') this.hiringFormFilter = '';
            if (key === 'citizenship') this.citizenshipFilter = 'all';
            if (key === 'year-assignment') this.assignmentFilter = 'all';
            if (key.startsWith('current-assignment:')) {
                this.currentAssignmentFilter = this.currentAssignmentFilter.filter(value => (
                    value !== key.slice('current-assignment:'.length)
                ));
            }
        },
        clearAllPlanFilters() {
            this.assignmentFilterScope = 'future';
            this.skillFilter = [];
            this.itrFilter = false;
            this.positionFilter = [];
            this.query = '';
            this.citizenshipFilter = 'all';
            this.hiringFormFilter = '';
            this.assignmentFilter = 'all';
            this.currentAssignmentFilter = [];
            this.currentFilterOpen = false;
        },
        eventTargetAcceptsText(target) {
            if (!target) return false;
            const tagName = String(target.tagName || '').toLowerCase();
            return ['input', 'textarea', 'select'].includes(tagName)
                || target.isContentEditable === true;
        },
        resetSelectionForFilter() {
            if (this.selectionFilterResetSuppressed) return;
            if (this.hasSelection || this.quickEditorOpen) this.clearSelection();
            this.resetVirtualEmployeeRange();
        },
        handlePlanKeydown(event) {
            if (event && event.key === 'Escape' && this.currentFilterOpen) {
                event.preventDefault();
                this.closeCurrentAssignmentFilter();
                return;
            }
            if (!event || this.saving || this.inspectorEmployee || this.planningNamesVisible) {
                return;
            }
            if (
                String(event.key || '').toLocaleLowerCase('ru-RU') === 'c'
                && (event.ctrlKey || event.metaKey)
                && this.hasSelectedFullRows
                && !this.eventTargetAcceptsText(event.target)
            ) {
                event.preventDefault();
                this.copySelectedEmployeeNames();
                return;
            }
            if (event.key === 'Escape' && this.quickEditorOpen) {
                event.preventDefault();
                this.closeQuickEditor();
                return;
            }
            if (
                event.key === 'Enter'
                && (event.ctrlKey || event.metaKey)
                && this.quickEditorOpen
                && this.saveAllowed
                && !this.eventTargetAcceptsText(event.target)
            ) {
                event.preventDefault();
                this.saveSelectedRanges();
                return;
            }
            if (!this.hasSelection || this.eventTargetAcceptsText(event.target)) return;
            if (event.key === 'Delete') {
                event.preventDefault();
                this.prepareClearAction();
                return;
            }
            if (
                event.key.length === 1
                && !event.ctrlKey
                && !event.metaKey
                && !event.altKey
            ) {
                event.preventDefault();
                this.openQuickEditorAt(this.dragSelectionTargetId, event.key);
            }
        },
        handlePlanDocumentPointer(event) {
            if (!this.quickEditorOpen || !event || !event.target) return;
            if (this.$refs.quickEditor && this.$refs.quickEditor.contains(event.target)) return;
            if (event.target.closest && event.target.closest('.workforce-plan-cell')) return;
            this.closeQuickEditor();
        },
        intervalMatchesAssignmentFilter(interval, filter) {
            if (!interval) return filter === 'state:missing';
            if (filter === 'object:any' || filter === 'object:or-rest') {
                return ['object', 'object_group', 'planned_object'].includes(
                    interval.state_type
                ) || (filter === 'object:or-rest' && interval.state_type === 'rest');
            }
            if (filter.startsWith('object:')) {
                return interval.state_type === 'object' &&
                    String(Number(interval.building_id)) === filter.slice(7);
            }
            if (filter.startsWith('object_group:')) {
                return interval.state_type === 'object_group' &&
                    String(Number(interval.object_group_id)) ===
                    filter.slice(13);
            }
            if (filter.startsWith('planned_object:')) {
                return interval.state_type === 'planned_object'
                    && String(Number(interval.planned_object_id)) === filter.slice(15);
            }
            if (filter.startsWith('state:')) {
                return interval.state_type === filter.slice(6);
            }
            return true;
        },
        employeeMatchesAssignmentFilter(employee) {
            if (this.assignmentFilter === 'all') return true;
            const range = this.assignmentFilterRange;
            if (!range) return false;
            if (this.assignmentFilter === 'state:missing') {
                return this.weeks.some(week => {
                    if (!planUtils.rangesOverlap(week.visible_starts_on, week.visible_ends_on, range.from, range.to)) return false;
                    const state = this.cellState(employee, week);
                    return !state || !state.interval;
                });
            }
            return (employee.intervals || []).some(interval => (
                planUtils.rangesOverlap(
                    interval.starts_on,
                    interval.ends_on,
                    range.from,
                    range.to
                ) && this.intervalMatchesAssignmentFilter(
                    interval,
                    this.assignmentFilter
                )
            ));
        },
        employeeMatchesCitizenshipFilter(employee) {
            if (this.citizenshipFilter === 'all') return true;
            const citizenship = String(employee && employee.citizenship || '').trim();
            if (this.citizenshipFilter === '__none__') return !citizenship;
            return citizenship === this.citizenshipFilter;
        },
        employeeMatchesCurrentAssignmentFilter(employee) {
            if (!this.currentAssignmentFilter.length) return true;
            const interval = planUtils.stateOnDate(
                employee.intervals,
                this.currentStatusDate
            );
            return this.currentAssignmentFilter.some(filter => this.intervalMatchesAssignmentFilter(interval, filter));
        },
        employeeSkillLabel(employee) {
            const skills = Array.isArray(employee && employee.skills)
                ? employee.skills.filter(Boolean)
                : [];
            return skills.length ? skills.join('/') : 'Навыки не указаны';
        },
        employeeSkillTitle(employee) {
            const skills = Array.isArray(employee && employee.skills)
                ? employee.skills.filter(Boolean)
                : [];
            if (!skills.length) return 'Навыки не указаны';
            const details = new Map(
                (Array.isArray(employee && employee.skill_details)
                    ? employee.skill_details
                    : []
                ).filter(Boolean).map(skill => [
                    String(skill.name || '').trim().toLocaleUpperCase('ru-RU'),
                    skill.description || null
                ])
            );
            return skills.map(name => {
                const code = String(name).trim().toLocaleUpperCase('ru-RU');
                const description = details.get(code)
                    || SOURCE_SKILL_DESCRIPTIONS[code]
                    || 'расшифровка не указана';
                return `${name} — ${description}`;
            }).join('\n');
        },
        employeeFullName(employee) {
            return employeeNameFormat.formatEmployeeName(employee, 'full');
        },
        employeeRowTitle(employee) {
            const characteristic = employee.latest_characteristic;
            return [
                this.employeeFullName(employee),
                employee.source_display_name && employee.source_display_name !== employee.display_name
                    ? `В таблице: ${employee.source_display_name}`
                    : null,
                employee.import_match_status && employee.import_match_status !== 'matched'
                    ? `Сопоставление: ${this.importMatchLabel(employee.import_match_status)}`
                    : null,
                employee.import_conflicts && employee.import_conflicts.length
                    ? `Расхождений с Аргусом: ${employee.import_conflicts.length}`
                    : null,
                employee.citizenship ? `Гражданство: ${employee.citizenship}` : null,
                employee.hiring_form === 'gph' ? 'Форма найма: ГПХ' : null,
                `Навыки: ${this.employeeSkillTitle(employee)}`,
                characteristic && characteristic.content
                    ? `Последняя характеристика (${this.formatDate(characteristic.characteristic_date)}):\n${characteristic.content}`
                    : null
            ].filter(Boolean).join('\n');
        },
        planLoadKey() {
            return [
                this.window.from,
                this.window.to
            ].join('|');
        },
        loadPlan(preserveScroll, options) {
            const request = {
                preserveScroll: Boolean(preserveScroll),
                background: Boolean(options && options.background),
                key: this.planLoadKey(),
                window: {from: this.window.from, to: this.window.to},
                centerDate: options && options.centerDate,
                scrollTop: options && options.scrollTop
            };
            if (this._planLoadPromise) {
                const queued = this._queuedPlanLoad;
                this._queuedPlanLoad = {
                    preserveScroll: request.preserveScroll || Boolean(
                        queued && queued.preserveScroll
                    ),
                    background: queued
                        ? queued.background && request.background
                        : request.background,
                    key: request.key,
                    centerDate: request.centerDate || (queued && queued.key === request.key && queued.centerDate),
                    scrollTop: request.scrollTop !== undefined ? request.scrollTop : (queued ? queued.scrollTop : undefined)
                };
                return this._planLoadPromise;
            }
            const promise = this.runPlanLoadQueue(request);
            this._planLoadPromise = promise;
            return promise;
        },
        async runPlanLoadQueue(request) {
            let result;
            try {
                result = await this.performPlanLoad(request);
            } finally {
                this._planLoadPromise = null;
            }
            const queued = this._queuedPlanLoad;
            this._queuedPlanLoad = null;
            if (queued && !this._workforcePlanDestroyed) {
                return this.loadPlan(queued.preserveScroll, {
                    background: queued.background,
                    centerDate: queued.centerDate,
                    scrollTop: queued.scrollTop
                });
            }
            return result;
        },
        async performPlanLoad(request) {
            const viewport = this.$refs.viewport;
            const preserveScroll = request.preserveScroll;
            const scrollLeft = preserveScroll && viewport ? viewport.scrollLeft : 0;
            const scrollTop = request.scrollTop !== undefined
                ? request.scrollTop : (preserveScroll && viewport ? viewport.scrollTop : 0);
            const requestId = this.loadRequestId + 1;
            const requestedWindow = request.window;
            const background = Boolean(
                request.background
                && this.employees.length
                && this.loadedPlanKey === request.key
            );
            this.loadRequestId = requestId;
            if (background) this.backgroundRefreshing = true;
            else this.loading = true;
            this.errorMessage = '';
            try {
                const response = await api.get('/api/employees/workforce/plan', {
                    params: requestedWindow,
                    ignoreModal: true
                });
                if (
                    requestId !== this.loadRequestId
                    || this._workforcePlanDestroyed
                    || request.key !== this.planLoadKey()
                ) return;
                const data = responseData(response);
                this.applyPlanPayload(data);
                this.displayReady = true;
                this.loadedPlanKey = request.key;
                if (requestId !== this.loadRequestId || this._workforcePlanDestroyed) return;
                this.accessDenied = false;
                this.applyRouteSelection();
                this.$nextTick(() => {
                    if (requestId !== this.loadRequestId || request.key !== this.planLoadKey()) return;
                    if (request.centerDate) {
                        this.restorePeriodScroll(request.centerDate, scrollTop);
                        return;
                    }
                    if (preserveScroll && this.$refs.viewport) {
                        this.$refs.viewport.scrollLeft = scrollLeft;
                        this.$refs.viewport.scrollTop = scrollTop;
                        if (this.$refs.topScrollbar) {
                            this.$refs.topScrollbar.scrollLeft = scrollLeft;
                        }
                    }
                    this.updateVirtualEmployeeRange();
                });
            } catch (error) {
                if (requestId !== this.loadRequestId || this._workforcePlanDestroyed || request.key !== this.planLoadKey()) return;
                if (error && error.response && error.response.status === 403) {
                    this.accessDenied = true;
                } else {
                    this.errorMessage = this.errorText(
                        error,
                        'Не удалось загрузить план сотрудников'
                    );
                }
            } finally {
                if (background) this.backgroundRefreshing = false;
                else this.loading = false;
            }
            if (this.$route && this.$route.query.plan_intent === 'focus' && !this.errorMessage) {
                await this.applyRouteFocus();
            }
        },
        async onEmployeeCreated(employee) {
            await this.loadPlan(true);
            const name = employee && (
                employee.display_name
                || employee.full_name
                || [employee.last_name, employee.first_name].filter(Boolean).join(' ')
            );
            this.$bvToast.toast(
                name ? `Сотрудник «${name}» создан` : 'Сотрудник создан',
                {variant: 'success', solid: true, autoHideDelay: 2200}
            );
        },
        importEmployeeCount(status) {
            return this.importSummary && this.importSummary.employees
                ? Number(this.importSummary.employees[status] || 0)
                : 0;
        },
        shortChecksum(value) {
            const checksum = String(value || '');
            return checksum ? `${checksum.slice(0, 10)}…${checksum.slice(-6)}` : 'нет';
        },
        importMatchLabel(status) {
            return ({
                review: 'проверить',
                ambiguous: 'несколько совпадений',
                unmatched: 'не найден в Аргусе',
                excluded: 'исключён из переноса'
            })[status] || status;
        },
        applyPlanPayload(data) {
            const value = data || {};
            this.employees = Array.isArray(value.employees) ? value.employees : [];
            this.objects = Array.isArray(value.objects) ? value.objects : [];
            this.workforcePlannedObjects = Array.isArray(value.planned_objects)
                ? value.planned_objects
                : [];
            this.planningColorPalette = Array.isArray(value.planning_color_palette)
                && value.planning_color_palette.length
                ? value.planning_color_palette.map(planDisplay.normalizeTargetColor)
                : DEFAULT_PLANNING_COLOR_PALETTE.slice();
            const canFinalizeImport = this.permissions.can_finalize_import;
            this.permissions = value.permissions || {};
            if (
                canFinalizeImport === true
                && !Object.prototype.hasOwnProperty.call(
                    this.permissions,
                    'can_finalize_import'
                )
            ) this.$set(this.permissions, 'can_finalize_import', true);
            this.importSummary = value.import_summary || null;
            if (value.cutover) {
                this.cutover = Object.assign({
                    schema_ready: false,
                    latest_batch: null,
                    reconciliation: null,
                    blockers: [],
                    ready_to_apply: false,
                    migration_completed: false
                }, value.cutover);
            }
            this.reconciliation = Object.assign({
                objects: [],
                employees: [],
                object_catalog: [],
                object_group_catalog: [],
                planned_object_catalog: [],
                employee_catalog: []
            }, value.reconciliation || {});
            this.objectGroups = Array.isArray(value.object_groups)
                ? value.object_groups
                : (this.reconciliation.object_group_catalog || []);
            const decisions = value.import_decisions || {};
            this.importDecisions = {
                revision: Number(decisions.revision || 0),
                object_mappings: Object.assign({}, decisions.object_mappings || {}),
                planned_objects: Object.keys(decisions.planned_objects || {}).reduce(
                    (result, code) => {
                        result[code] = Object.assign({}, decisions.planned_objects[code]);
                        return result;
                    },
                    {}
                ),
                employee_mappings: Object.assign({}, decisions.employee_mappings || {}),
                employee_exclusions: Object.assign(
                    {},
                    decisions.employee_exclusions || {}
                ),
                conflict_resolutions: Object.keys(
                    decisions.conflict_resolutions || {}
                ).reduce((result, sourceKey) => {
                    result[sourceKey] = Object.assign(
                        {},
                        decisions.conflict_resolutions[sourceKey]
                    );
                    return result;
                }, {})
            };
        },
        importObjectCatalogItem(buildingId) {
            return (this.reconciliation.object_catalog || []).find(object => (
                Number(object.building_id) === Number(buildingId)
            )) || null;
        },
        importEmployeeCatalogItem(employeeId) {
            return (this.reconciliation.employee_catalog || []).find(employee => (
                Number(employee.employee_id) === Number(employeeId)
            )) || null;
        },
        objectImportOption(object, hint) {
            return Object.assign({}, object, {
                target_type: 'object',
                target_id: Number(object.building_id),
                label: `${hint ? `${hint} · ` : ''}Объект #${object.building_id} · ${object.name}`
            });
        },
        objectGroupImportOption(group, hint) {
            return Object.assign({}, group, {
                target_type: 'object_group',
                target_id: Number(group.object_group_id),
                label: `${hint ? `${hint} · ` : ''}Группа #${group.object_group_id} · ${group.name}`
            });
        },
        plannedObjectImportOption(object, hint) {
            return Object.assign({}, object, {
                target_type: 'planned_object',
                target_id: object.planned_object_id
                    ? Number(object.planned_object_id)
                    : null,
                target_code: object.planning_code,
                label: `${hint ? `${hint} · ` : ''}Плановый · ${object.planning_code} — ${object.name}`
            });
        },
        importObjectOptions(row) {
            const options = [];
            const seen = new Set();
            const addObject = (object, hint) => {
                if (
                    !object
                    || object.is_active === false
                    || seen.has(`object:${Number(object.building_id)}`)
                ) return;
                seen.add(`object:${Number(object.building_id)}`);
                options.push(this.objectImportOption(object, hint));
            };
            const addGroup = (group, hint) => {
                if (
                    !group
                    || group.archived === true
                    || seen.has(`object_group:${Number(group.object_group_id)}`)
                ) return;
                seen.add(`object_group:${Number(group.object_group_id)}`);
                options.push(this.objectGroupImportOption(group, hint));
            };
            const addPlannedObject = (object, hint) => {
                if (
                    !object
                    || object.status !== 'planned'
                    || seen.has(`planned_object:${Number(object.planned_object_id)}`)
                ) return;
                seen.add(`planned_object:${Number(object.planned_object_id)}`);
                options.push(this.plannedObjectImportOption(object, hint));
            };
            if (row.recommended_target && row.recommended_target.target_type === 'object_group') {
                addGroup(row.recommended_target, 'Рекомендуется');
            } else if (row.recommended_target) {
                addObject(row.recommended_target, 'Рекомендуется');
            } else {
                addObject(row.recommended_object, 'Рекомендуется');
            }
            (row.candidates || []).forEach(object => addObject(object, 'Кандидат'));
            (this.reconciliation.object_group_catalog || []).forEach(group => addGroup(group, ''));
            (this.reconciliation.planned_object_catalog || []).forEach(object => (
                addPlannedObject(object, '')
            ));
            (this.reconciliation.object_catalog || []).forEach(object => addObject(object, ''));
            options.unshift({
                target_type: 'create_planned_object',
                target_code: row.source.planning_code,
                label: `＋ Создать плановый объект «${row.source.planning_code}»`
            });
            return options;
        },
        filterImportObjectOptions(options, search) {
            const query = String(search || '').trim().toLocaleLowerCase('ru-RU');
            if (!query) return options || [];
            const tokens = query.split(/\s+/).filter(Boolean);
            return (options || []).filter(option => {
                const haystack = String(option && option.label || '')
                    .toLocaleLowerCase('ru-RU');
                return tokens.every(token => haystack.includes(token));
            });
        },
        importTargetOptionKey(option) {
            return option
                ? `${option.target_type}:${option.target_id || option.target_code || ''}`
                : 'empty';
        },
        selectedImportObject(sourceCode) {
            const raw = this.importDecisions.object_mappings[sourceCode];
            const mapping = typeof raw === 'number'
                ? {target_type: 'object', target_id: raw}
                : raw;
            if (!mapping) return null;
            if (mapping.target_type === 'object_group') {
                const group = (this.reconciliation.object_group_catalog || []).find(item => (
                    Number(item.object_group_id) === Number(mapping.target_id)
                ));
                return group ? this.objectGroupImportOption(group, '') : null;
            }
            if (mapping.target_type === 'planned_object') {
                if (mapping.target_id) {
                    const object = (this.reconciliation.planned_object_catalog || [])
                        .find(item => (
                            Number(item.planned_object_id) === Number(mapping.target_id)
                        ));
                    return object ? this.plannedObjectImportOption(object, '') : null;
                }
                const code = mapping.target_code || sourceCode;
                const draft = this.importDecisions.planned_objects[code];
                return draft ? this.plannedObjectImportOption(
                    Object.assign({}, draft, {planning_code: code}),
                    'Будет создан при переносе'
                ) : null;
            }
            const object = this.importObjectCatalogItem(mapping.target_id);
            return object ? this.objectImportOption(object, '') : null;
        },
        selectedPlanningCodeTarget(row) {
            const selected = row && row.source
                ? this.selectedImportObject(row.source.planning_code)
                : null;
            return selected && ['object', 'object_group'].includes(selected.target_type)
                ? selected
                : null;
        },
        openPlanningNames(target) {
            if (!this.permissions.can_edit) return;
            this.planningNameTargetKey = target
                ? `${target.target_type}:${Number(target.target_id)}` : null;
            this.planningNamesVisible = true;
        },
        onPlanningTargetSettingsSaved() {
            this.$bvToast.toast('Цвет и название в плане сохранены', {
                variant: 'success', solid: true, autoHideDelay: 2200
            });
        },
        applyPlanningName(target) {
            const targetType = target.target_type;
            const targetId = Number(target.target_id);
            const idField = {
                object: 'building_id', object_group: 'object_group_id', planned_object: 'planned_object_id'
            }[targetType];
            const catalogs = {
                object: [this.objects, this.reconciliation.object_catalog],
                object_group: [this.objectGroups, this.reconciliation.object_group_catalog],
                planned_object: [this.workforcePlannedObjects, this.managedPlannedObjects,
                    this.reconciliation.planned_object_catalog]
            }[targetType] || [];
            catalogs.forEach(items => (items || []).forEach(item => {
                if (Number(item[idField]) === targetId) this.$set(item, 'planning_code', target.planning_code);
            }));
            this.employees.forEach(employee => (employee.intervals || []).forEach(interval => {
                if (interval.state_type === targetType && Number(interval[idField]) === targetId) {
                    this.$set(interval, 'target_code', target.planning_code);
                }
            }));
        },
        async savePlanningName(target, input) {
            if (!this.permissions.can_edit) {
                throw new Error('Нет права изменять краткие названия.');
            }
            const segment = {object: 'objects', object_group: 'object-groups', planned_object: 'planned-objects'}[target.target_type];
            const mutationId = this.createWorkforceMutationId();
            this.rememberOwnWorkforceMutation(mutationId);
            try {
                const response = await api.put(
                    `/api/employees/workforce/${segment}/${target.target_id}/planning-code`,
                    input,
                    {ignoreModal: true, headers: {'X-Workforce-Mutation-Id': mutationId}}
                );
                const saved = responseData(response);
                this.applyPlanningName(Object.assign({}, target, {planning_code: saved.planning_code}));
                if (saved.renamed_owner) this.applyPlanningName(saved.renamed_owner);
                return saved;
            } catch (error) {
                this.forgetOwnWorkforceMutation(mutationId);
                if (error.response && error.response.data
                    && ['WORKFORCE_PLANNING_CODE_CHANGED', 'WORKFORCE_PLANNED_OBJECT_NOT_PLANNED']
                        .includes(error.response.data.code)) {
                    await this.loadPlan(true, {background: true});
                }
                throw error;
            }
        },
        planningCodeTargetKey(target) {
            return target
                ? `${target.target_type}:${Number(target.target_id)}`
                : null;
        },
        planningCodeMatches(row) {
            const target = this.selectedPlanningCodeTarget(row);
            const sourceCode = String(row && row.source && row.source.planning_code || '')
                .trim()
                .toLocaleUpperCase('ru-RU');
            const targetCode = String(target && target.planning_code || '')
                .trim()
                .toLocaleUpperCase('ru-RU');
            return Boolean(sourceCode && sourceCode === targetCode);
        },
        planningCodeActionLabel(row) {
            const target = this.selectedPlanningCodeTarget(row);
            const sourceCode = row && row.source && row.source.planning_code;
            const targetLabel = target && target.target_type === 'object_group'
                ? 'группе'
                : 'объекту';
            return target && target.planning_code
                ? `Заменить код «${target.planning_code}» на «${sourceCode}»`
                : `Присвоить ${targetLabel} код «${sourceCode}»`;
        },
        async assignSourcePlanningCode(row) {
            const target = this.selectedPlanningCodeTarget(row);
            if (!target || this.planningCodeSavingKey !== null) return;
            const targetId = Number(target.target_id);
            const targetKey = this.planningCodeTargetKey(target);
            const isGroup = target.target_type === 'object_group';
            this.planningCodeSavingKey = targetKey;
            this.errorMessage = '';
            try {
                const response = await api.put(
                    isGroup
                        ? `/api/employees/workforce/object-groups/${targetId}/planning-code`
                        : `/api/employees/workforce/objects/${targetId}/planning-code`,
                    {planning_code: row.source.planning_code},
                    {ignoreModal: true}
                );
                const saved = responseData(response);
                const catalogs = isGroup
                    ? [this.reconciliation.object_group_catalog, this.objectGroups]
                    : [this.reconciliation.object_catalog, this.objects];
                catalogs.forEach(catalog => {
                    (catalog || []).forEach(item => {
                        const itemId = Number(isGroup ? item.object_group_id : item.building_id);
                        if (itemId === targetId) {
                            this.$set(item, 'planning_code', saved.planning_code);
                        }
                    });
                });
                this.$bvToast.toast(
                    `${isGroup ? 'Группе' : 'Объекту'} «${saved.name}» присвоен код «${saved.planning_code}»`,
                    {variant: 'success', solid: true, autoHideDelay: 2400}
                );
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось сохранить кодовое наименование'
                );
            } finally {
                this.planningCodeSavingKey = null;
            }
        },
        setImportObject(sourceCode, option) {
            if (option && option.target_type === 'create_planned_object') {
                this.preparePlannedObjectDraft(sourceCode);
                return;
            }
            if (option && option.target_type && option.target_id) {
                this.$set(
                    this.importDecisions.object_mappings,
                    sourceCode,
                    {
                        target_type: option.target_type,
                        target_id: Number(option.target_id)
                    }
                );
                return;
            }
            if (option && option.target_type === 'planned_object' && option.target_code) {
                this.$set(this.importDecisions.object_mappings, sourceCode, {
                    target_type: 'planned_object',
                    target_code: option.target_code
                });
                return;
            }
            this.$delete(this.importDecisions.object_mappings, sourceCode);
        },
        planningCodeOwner(code) {
            const normalized = String(code || '').trim().toLocaleUpperCase('ru-RU');
            const objects = (this.reconciliation.object_catalog || []).concat(this.objects || []);
            const object = objects.find(item => (
                String(item.planning_code || '').trim().toLocaleUpperCase('ru-RU')
                    === normalized
            ));
            if (object) {
                return {
                    target_type: 'object',
                    target_id: Number(object.building_id),
                    name: object.name
                };
            }
            const groups = (this.reconciliation.object_group_catalog || [])
                .concat(this.objectGroups || []);
            const group = groups.find(item => (
                String(item.planning_code || '').trim().toLocaleUpperCase('ru-RU')
                    === normalized
            ));
            if (group) {
                return {
                    target_type: 'object_group',
                    target_id: Number(group.object_group_id),
                    name: group.name
                };
            }
            const plannedObjects = (this.reconciliation.planned_object_catalog || [])
                .concat(this.workforcePlannedObjects || [], this.managedPlannedObjects || []);
            const planned = plannedObjects.find(item => (
                item.status === 'planned'
                && String(item.planning_code || '').trim().toLocaleUpperCase('ru-RU')
                    === normalized
            ));
            return planned ? {
                target_type: 'planned_object',
                target_id: Number(planned.planned_object_id),
                name: planned.name
            } : null;
        },
        suggestedPreviousPlanningCode(code, owner) {
            const match = String(owner && owner.name || '').match(
                /(?:^|\D)((?:19|20)\d{2})(?:\D|$)/
            );
            const suffix = match ? match[1] : String(Number(this.periodAnchorDate.slice(0, 4)) - 1);
            return `${String(code || '').slice(0, Math.max(1, 16 - suffix.length))}${suffix}`;
        },
        preparePlannedObjectCreation(mode) {
            const typedCode = String(mode === 'selection' ? this.editorQuery : '')
                .trim()
                .toLocaleUpperCase('ru-RU');
            const planningCode = /^[0-9A-ZА-ЯЁ/_-]{1,16}$/u.test(typedCode)
                ? typedCode
                : '';
            const owner = planningCode ? this.planningCodeOwner(planningCode) : null;
            this.plannedObjectDraftMode = mode === 'selection' ? 'selection' : 'manager';
            this.plannedObjectDraftError = '';
            this.plannedObjectDraft = {
                planning_code: planningCode,
                name: '',
                comment: 'Контракт ещё не заключён',
                conflict_owner: owner,
                rename_conflict_to: owner && this.plannedObjectDraftCanReassignCode
                    ? this.suggestedPreviousPlanningCode(planningCode, owner)
                    : ''
            };
            this.plannedObjectDraftVisible = true;
        },
        updatePlannedObjectDraftConflict() {
            const code = String(this.plannedObjectDraft.planning_code || '')
                .trim()
                .toLocaleUpperCase('ru-RU');
            const previousOwner = this.plannedObjectDraft.conflict_owner;
            const owner = code ? this.planningCodeOwner(code) : null;
            this.$set(this.plannedObjectDraft, 'conflict_owner', owner);
            if (!owner) {
                this.$set(this.plannedObjectDraft, 'rename_conflict_to', '');
            } else if (
                !previousOwner
                || previousOwner.target_type !== owner.target_type
                || Number(previousOwner.target_id) !== Number(owner.target_id)
            ) {
                this.$set(
                    this.plannedObjectDraft,
                    'rename_conflict_to',
                    this.plannedObjectDraftCanReassignCode
                        ? this.suggestedPreviousPlanningCode(code, owner)
                        : ''
                );
            }
            this.plannedObjectDraftError = '';
        },
        async acceptPlannedObjectDraft() {
            if (!this.plannedObjectDraftValid) return;
            const draft = this.plannedObjectDraft;
            const code = String(draft.planning_code || '').trim().toLocaleUpperCase('ru-RU');
            const owner = draft.conflict_owner;
            const mode = this.plannedObjectDraftMode;
            this.plannedObjectDraftSaving = true;
            this.plannedObjectDraftError = '';
            try {
                const response = await api.post(
                    '/api/employees/workforce/planned-objects',
                    {
                        planning_code: code,
                        name: String(draft.name || '').trim(),
                        comment: String(draft.comment || '').trim() || null,
                        year: Number(this.periodAnchorDate.slice(0, 4)),
                        conflict_owner: owner ? {
                            target_type: owner.target_type,
                            target_id: Number(owner.target_id)
                        } : null,
                        rename_conflict_to: owner
                            ? String(draft.rename_conflict_to || '')
                                .trim()
                                .toLocaleUpperCase('ru-RU')
                            : null
                    },
                    {ignoreModal: true}
                );
                const saved = responseData(response);
                this.plannedObjectDraftVisible = false;
                await this.loadPlan(true);
                if (mode === 'selection') {
                    this.applyTargetOption({
                        value: `planned_object:${Number(saved.planned_object_id)}`,
                        target_type: 'planned_object',
                        target_id: Number(saved.planned_object_id),
                        label: `Плановый · ${saved.planning_code} — ${saved.name}`
                    });
                    this.editorQuery = '';
                    this.quickEditorOpen = true;
                } else {
                    await this.openPlannedObjectsManager();
                }
                this.$bvToast.toast(
                    mode === 'selection'
                        ? `Плановый объект «${saved.planning_code}» создан и выбран`
                        : `Плановый объект «${saved.planning_code}» создан`,
                    {variant: 'success', solid: true, autoHideDelay: 2600}
                );
            } catch (error) {
                const data = error && error.response && error.response.data || {};
                const details = data.details || {};
                if (data.code === 'WORKFORCE_PLANNING_CODE_CONFLICT' && details.owner) {
                    this.$set(this.plannedObjectDraft, 'conflict_owner', details.owner);
                    this.$set(
                        this.plannedObjectDraft,
                        'rename_conflict_to',
                        details.suggested_code
                            || this.suggestedPreviousPlanningCode(code, details.owner)
                    );
                }
                this.plannedObjectDraftError = this.errorText(
                    error,
                    'Не удалось создать плановый объект'
                );
            } finally {
                this.plannedObjectDraftSaving = false;
            }
        },
        async openPlannedObjectLink(target) {
            if (this.plannedObjectsLoading || !this.linkablePlannedObjectKeys.has(target.key)) return;
            await this.openPlannedObjectsManager();
            if (!this.plannedObjectsManagerVisible || !this.plannedObjectsCanManage) return;
            const item = this.managedPlannedObjects.find(candidate => (
                Number(candidate.planned_object_id) === Number(target.target_id)
            ));
            if (!item || item.status !== 'planned') {
                this.$bvToast.toast('Плановый объект уже связан или отменён.', {
                    variant: 'info', solid: true, autoHideDelay: 3000
                });
                this.loadPlan(true, {background: true});
                return;
            }
            this.preparePlannedObjectAction(item, 'link');
        },
        positionVueSelectDropdown,
        preventPlannedObjectsHideWhileSaving(event) {
            if (this.plannedObjectActionSaving) event.preventDefault();
        },
        async openPlannedObjectsManager() {
            this.plannedObjectsManagerVisible = true;
            this.plannedObjectsLoading = true;
            this.plannedObjectsError = '';
            this.managedPlannedObjects = [];
            this.plannedObjectsCanCreate = false;
            this.plannedObjectsCanManage = false;
            this.clearPlannedObjectAction();
            try {
                const response = await api.get('/api/employees/workforce/planned-objects', {
                    ignoreModal: true
                });
                const value = responseData(response);
                this.managedPlannedObjects = value.planned_objects || [];
                this.plannedObjectsCanCreate = Boolean(
                    value.permissions && value.permissions.can_create
                );
                this.plannedObjectsCanManage = Boolean(
                    value.permissions && value.permissions.can_manage
                );
            } catch (error) {
                this.plannedObjectsError = this.errorText(
                    error,
                    'Не удалось загрузить плановые объекты'
                );
            } finally {
                this.plannedObjectsLoading = false;
            }
        },
        plannedObjectStatusLabel(item) {
            if (item.status === 'planned') return 'Планируется · контракт не заключён';
            if (item.status === 'linked') {
                return `Связан с целью Аргуса с ${this.formatDate(item.effective_from)}`;
            }
            if (item.status === 'cancelled') {
                return `Отменён${item.effective_from ? ` с ${this.formatDate(item.effective_from)}` : ''}${item.cancellation_reason ? ` · ${item.cancellation_reason}` : ''}`;
            }
            return item.status;
        },
        preparePlannedObjectAction(item, mode) {
            if (this.plannedObjectActionSaving || !this.plannedObjectsCanManage) return;
            const week = planUtils.mondayOnOrBefore(currentLocalDate());
            this.plannedObjectAction = Object.assign(emptyPlannedObjectAction(), {
                item,
                mode,
                selected_date: week,
                effective_from: week,
                target: mode === 'cancel'
                    ? {
                        value: 'requires_distribution',
                        target_type: 'requires_distribution',
                        target_id: null,
                        label: '❌ Не распределён'
                    }
                    : null
            });
            this.loadPlannedObjectImpact();
        },
        clearPlannedObjectAction() {
            this.plannedObjectAction = emptyPlannedObjectAction();
        },
        changePlannedObjectWeek(value) {
            if (this.plannedObjectActionSaving) return;
            const action = this.plannedObjectAction;
            action.selected_date = value;
            const date = new Date(`${value}T00:00:00.000Z`);
            const valid = /^\d{4}-\d{2}-\d{2}$/.test(value || '')
                && Number.isFinite(date.getTime())
                && date.toISOString().slice(0, 10) === value;
            action.effective_from = valid ? planUtils.mondayOnOrBefore(value) : '';
            return this.loadPlannedObjectImpact();
        },
        async loadPlannedObjectImpact() {
            if (this.plannedObjectActionSaving) return;
            const action = this.plannedObjectAction;
            const request = ++action.impact_request;
            const effectiveFrom = action.effective_from;
            action.impact = null;
            action.impact_date = '';
            action.impact_error = '';
            action.error = '';
            action.impact_loading = false;
            if (!action.item || !/^\d{4}-\d{2}-\d{2}$/.test(action.effective_from || '')) {
                return;
            }
            const id = Number(action.item.planned_object_id);
            const isCurrent = () => this.plannedObjectAction === action
                && action.impact_request === request
                && action.effective_from === effectiveFrom;
            action.impact_loading = true;
            try {
                const response = await api.get(
                    `/api/employees/workforce/planned-objects/${id}/impact`,
                    {
                        params: {effective_from: effectiveFrom},
                        ignoreModal: true
                    }
                );
                if (isCurrent()) {
                    action.impact = responseData(response);
                    action.impact_date = effectiveFrom;
                }
            } catch (error) {
                if (isCurrent()) {
                    action.impact_error = this.errorText(
                        error,
                        'Не удалось рассчитать затронутый план'
                    );
                }
            } finally {
                if (isCurrent()) {
                    action.impact_loading = false;
                }
            }
        },
        async applyPlannedObjectAction() {
            if (!this.plannedObjectActionValid || this.plannedObjectActionSaving) return;
            const action = this.plannedObjectAction;
            const target = action.target;
            const id = Number(action.item.planned_object_id);
            action.error = '';
            this.plannedObjectActionSaving = true;
            try {
                if (action.mode === 'link') {
                    await api.post(
                        `/api/employees/workforce/planned-objects/${id}/link`,
                        {
                            target_type: target.target_type,
                            target_id: Number(target.target_id),
                            effective_from: action.effective_from
                        },
                        {ignoreModal: true}
                    );
                } else {
                    const replacement = {state_type: target.target_type};
                    if (target.target_type === 'object') {
                        replacement.building_id = Number(target.target_id);
                    }
                    if (target.target_type === 'object_group') {
                        replacement.object_group_id = Number(target.target_id);
                    }
                    await api.post(
                        `/api/employees/workforce/planned-objects/${id}/cancel`,
                        {
                            effective_from: action.effective_from,
                            reason: action.reason,
                            replacement
                        },
                        {ignoreModal: true}
                    );
                }
                this.$bvToast.toast(
                    action.mode === 'link'
                        ? 'Плановый объект связан'
                        : 'Плановый объект отменён',
                    {variant: 'success', solid: true, autoHideDelay: 2400}
                );
                this.plannedObjectActionSaving = false;
                this.plannedObjectsManagerVisible = false;
                this.clearPlannedObjectAction();
                await this.loadPlan(true);
            } catch (error) {
                action.error = this.errorText(
                    error,
                    'Не удалось изменить плановый объект'
                );
            } finally {
                this.plannedObjectActionSaving = false;
            }
        },
        objectEvidenceLabel(row) {
            const values = (row.assignment_evidence || []).slice(0, 3).map(evidence => {
                const target = evidence.target_name || (
                    evidence.target_type === 'home'
                        ? 'Домой'
                        : evidence.target_type === 'requires_distribution'
                            ? 'Не распределён'
                            : evidence.target_type
                );
                return `${evidence.count} чел. → ${target}`;
            });
            return values.length ? values.join('; ') : 'Нет фактической подсказки';
        },
        employeeImportOption(employee, hint) {
            return Object.assign({}, employee, {
                label: `${hint ? `${hint} · ` : ''}#${employee.employee_id} ${employee.display_name}`
            });
        },
        importEmployeeOptions(row) {
            const options = [];
            const seen = new Set();
            const add = (employee, hint) => {
                if (!employee || seen.has(Number(employee.employee_id))) return;
                seen.add(Number(employee.employee_id));
                options.push(this.employeeImportOption(employee, hint));
            };
            (row.candidates || []).forEach(employee => add(employee, 'Кандидат'));
            (this.reconciliation.employee_catalog || []).forEach(employee => add(employee, ''));
            return options;
        },
        selectedImportEmployee(sourceKey) {
            const employee = this.importEmployeeCatalogItem(
                this.importDecisions.employee_mappings[sourceKey]
            );
            return employee ? this.employeeImportOption(employee, '') : null;
        },
        importEmployeeExcluded(sourceKey) {
            return Boolean(this.importDecisions.employee_exclusions[sourceKey]);
        },
        importEmployeeExclusionReason(sourceKey) {
            return this.importDecisions.employee_exclusions[sourceKey] || '';
        },
        setImportEmployeeExcluded(sourceKey, excluded) {
            if (excluded) {
                this.$set(
                    this.importDecisions.employee_exclusions,
                    sourceKey,
                    'Не переносить: строка не относится к действующему сотруднику Аргуса.'
                );
                this.$delete(this.importDecisions.employee_mappings, sourceKey);
                return;
            }
            this.$delete(this.importDecisions.employee_exclusions, sourceKey);
        },
        setImportEmployeeExclusionReason(sourceKey, reason) {
            this.$set(this.importDecisions.employee_exclusions, sourceKey, reason);
        },
        setImportEmployee(sourceKey, option) {
            if (option && option.employee_id) {
                this.$delete(this.importDecisions.employee_exclusions, sourceKey);
                this.$set(
                    this.importDecisions.employee_mappings,
                    sourceKey,
                    Number(option.employee_id)
                );
                return;
            }
            this.$delete(this.importDecisions.employee_mappings, sourceKey);
        },
        conflictDecision(sourceKey, conflictKey) {
            const row = this.importDecisions.conflict_resolutions[sourceKey];
            return row && row[conflictKey] || null;
        },
        setConflictDecision(sourceKey, conflictKey, decision) {
            if (!this.importDecisions.conflict_resolutions[sourceKey]) {
                this.$set(this.importDecisions.conflict_resolutions, sourceKey, {});
            }
            this.$set(
                this.importDecisions.conflict_resolutions[sourceKey],
                conflictKey,
                decision
            );
        },
        conflictLabel(conflict) {
            if (conflict.type === 'citizenship') {
                return `Гражданство: таблица — ${conflict.source_value || 'нет'}; Аргус — ${conflict.argus_value || 'нет'}`;
            }
            if (conflict.type === 'year_plan') {
                return `Годовой план: таблица — ${Number(conflict.source_interval_count || 0)} интервалов; Аргус — ${Number(conflict.argus_interval_count || 0)} интервалов`;
            }
            const labels = {
                current_assignment: 'Текущее назначение',
                current_plan: 'План Аргуса',
                legacy_planned_move: 'Старое плановое перемещение'
            };
            return `${labels[conflict.type] || conflict.type}: таблица — ${this.humanizeConflictState(conflict.source_state)}; Аргус — ${this.humanizeConflictState(conflict.argus_state)}`;
        },
        humanizeConflictState(value) {
            const state = String(value || 'missing');
            if (state.startsWith('object:')) {
                const object = this.importObjectCatalogItem(Number(state.slice(7)));
                return object ? `объект «${object.name}»` : 'неизвестный объект';
            }
            if (state.startsWith('object_group:')) {
                const groupId = Number(state.slice(13));
                const group = (this.reconciliation.object_group_catalog || []).find(item => (
                    Number(item.object_group_id) === groupId
                ));
                return group ? `группа «${group.name}»` : 'неизвестная группа объектов';
            }
            if (state.startsWith('source_object:')) {
                return `код «${state.slice(14)}» из таблицы`;
            }
            return ({
                rest: 'отдых',
                loupe: 'выбыл из планирования',
                requires_distribution: 'не распределён',
                home: 'дома',
                missing: 'нет данных'
            })[state] || state;
        },
        planChangePayload() {
            const unified = this.exactPeriodAvailable
                ? {
                    starts_on: this.exactStartsOn,
                    ends_on: planUtils.addDays(this.exactEndsOnInclusive, 1)
                }
                : null;
            const employeeById = new Map(this.employees.map(employee => (
                [Number(employee.employee_id), employee]
            )));
            const grouped = new Map();
            this.selectionRanges.forEach(range => {
                const employeeId = Number(range.employee_id);
                if (!grouped.has(employeeId)) grouped.set(employeeId, []);
                grouped.get(employeeId).push(unified || {
                    starts_on: range.starts_on,
                    ends_on: range.ends_on
                });
            });
            const payload = {
                plan_window: {from: this.window.from, to: this.window.to},
                state_type: this.editor.state_type,
                building_id: this.editor.building_id,
                object_group_id: this.editor.object_group_id,
                planned_object_id: this.editor.planned_object_id,
                note: this.editor.note || null,
                note_mode: this.editor.note_mode,
                loupe_reason: this.editor.loupe_reason || null,
                loupe_reason_mode: this.editor.loupe_reason_mode,
                operations: Array.from(grouped.entries()).map(([employeeId, ranges]) => ({
                    employee_id: employeeId,
                    expected_revision: Number(employeeById.get(employeeId).revision || 0),
                    ranges
                }))
            };
            if (this.editor.state_type === 'clear') {
                payload.clear = true;
                delete payload.state_type;
                delete payload.building_id;
                delete payload.object_group_id;
                delete payload.planned_object_id;
                delete payload.loupe_reason;
                delete payload.loupe_reason_mode;
            }
            return payload;
        },
        async saveSelectedRanges() {
            if (!this.saveAllowed || this.saving || this.loupePreviewPending || this.loupePreviewError) return;
            const payload = this.planChangePayload();
            this.saving = true;
            this.errorMessage = '';
            const mutationId = this.createWorkforceMutationId();
            this.rememberOwnWorkforceMutation(mutationId);
            try {
                const response = await api.put(
                    '/api/employees/workforce/plan/ranges/batch',
                    payload,
                    {
                        ignoreModal: true,
                        headers: {'X-Workforce-Mutation-Id': mutationId}
                    }
                );
                const patched = planUtils.applyPlanBatchResult(
                    this.employees,
                    responseData(response)
                );
                this.employees = patched.employees;
                this.closeQuickEditor();
                this.emitPlanPresence();
                const cells = this.selectedCellKeys.length;
                this.$bvToast.toast(payload.clear
                    ? `План очищен: ${cells} ${this.pluralize(cells, ['ячейка', 'ячейки', 'ячеек'])}`
                    : `План обновлён: ${cells} ${this.pluralize(cells, ['ячейка', 'ячейки', 'ячеек'])}`, {
                    variant: 'success', solid: true, autoHideDelay: 2200
                });
            } catch (error) {
                this.forgetOwnWorkforceMutation(mutationId);
                if (error && error.response && error.response.status === 409) {
                    await this.loadPlan(true);
                    this.errorMessage = 'Коллега уже изменил одну из выбранных строк. Весь пакет отменён, данные обновлены — проверьте выделение.';
                } else {
                    this.errorMessage = this.errorText(error, 'Не удалось сохранить план');
                }
            } finally {
                this.saving = false;
            }
        },
        applyRouteSelection() {
            if (this.$route && this.$route.query.plan_intent === 'focus') return;
            if (!this.routeDateIntent || this.routeSelectionApplied) return;
            const query = this.$route && this.$route.query || {};
            const employeeId = Number(query.employee_id);
            const date = String(query.date || '');
            if (!employeeId || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
            const employee = this.employees.find(item => (
                Number(item.employee_id) === employeeId
            ));
            const week = this.weeks.find(item => (
                item.visible_starts_on <= date && date < item.visible_ends_on
            ));
            if (!employee || !week) return;
            this.selectionFilterResetSuppressed = true;
            this.query = employee.display_name || '';
            this.routeSelectionApplied = true;
            const coordinate = this.selectionCoordinate(employee, week);
            this.selectionAnchor = coordinate;
            this.selectionFocus = coordinate;
            this.setSelectedCellKeys([
                selectionUtils.cellKey(employeeId, week.key)
            ]);
            this.resetEditorDraft();
            this.$nextTick(() => {
                this.selectionFilterResetSuppressed = false;
                this.openQuickEditorAt(this.cellDomId(employee, week));
                this.exactStartsOn = date;
                this.exactEndsOnInclusive = date;
                const stateType = String(query.state_type || '');
                if (!['object', 'object_group', 'planned_object', 'requires_distribution', 'rest', 'loupe'].includes(stateType)) return;
                this.editor.state_type = stateType;
                this.editor.building_id = stateType === 'object'
                    ? Number(query.building_id) || null
                    : null;
                this.editor.object_group_id = stateType === 'object_group'
                    ? Number(query.object_group_id) || null
                    : null;
                this.editor.planned_object_id = stateType === 'planned_object'
                    ? Number(query.planned_object_id) || null
                    : null;
                this.editorDirty = true;
            });
            this.startPresenceHeartbeat();
        },
        selectionCoordinate(employee, week) {
            return {
                employee_id: Number(employee.employee_id),
                week_key: week.key
            };
        },
        rowSelectorDomId(employee) {
            return `workforce-plan-row-selector-${Number(employee && employee.employee_id) || 0}`;
        },
        selectEmployeeRow(employee, event) {
            if (event && typeof event.preventDefault === 'function') event.preventDefault();
            const anchorRect = this.quickEditorEventRect(event);
            if (!this.canEditEmployee(employee)) {
                this.$bvToast.toast(
                    'Сначала сопоставьте сотрудника с карточкой Аргуса.',
                    {variant: 'warning', solid: true, autoHideDelay: 2600}
                );
                return;
            }
            if (!this.weeks.length) return;
            const rowKeys = this.weeks.map(week => (
                selectionUtils.cellKey(employee.employee_id, week.key)
            ));
            const modifierAdditive = Boolean(
                event && (event.ctrlKey || event.metaKey)
            );
            const additive = modifierAdditive || (
                this.isMobileViewport && this.hasSelectedFullRows
            );
            const rowKeySet = new Set(rowKeys);
            const wholeRowSelected = rowKeys.every(key => this.selectionKeySet.has(key));
            let nextKeys = rowKeys;
            if (additive && wholeRowSelected) {
                nextKeys = this.selectedCellKeys.filter(key => !rowKeySet.has(key));
            } else if (additive) {
                nextKeys = selectionUtils.addCells(this.selectedCellKeys, rowKeys);
            }
            this.closeQuickEditor(false);
            if (!nextKeys.length) {
                this.clearSelection();
                return;
            }
            this.selectionAnchor = selectionUtils.parseCellKey(nextKeys[0]);
            this.selectionFocus = selectionUtils.parseCellKey(nextKeys[nextKeys.length - 1]);
            this.dragSelectionTargetId = additive && wholeRowSelected
                ? this.cellDomId(
                    {employee_id: this.selectionAnchor.employee_id},
                    {key: this.selectionAnchor.week_key}
                )
                : this.rowSelectorDomId(employee);
            this.setSelectedCellKeys(nextKeys);
            this.emitPlanPresence();
            this.startPresenceHeartbeat();
            this.openQuickEditorAt(this.dragSelectionTargetId, '', anchorRect);
            this.showRowCopyHintIfNeeded(modifierAdditive);
        },
        selectionDomIds(key) {
            const coordinate = selectionUtils.parseCellKey(key);
            if (!coordinate) return [];
            const employee = {employee_id: coordinate.employee_id};
            const week = {key: coordinate.week_key};
            const ids = [this.cellDomId(employee, week)];
            if (
                this.currentStatusWeek
                && this.currentStatusWeek.key === coordinate.week_key
            ) ids.push(this.cellDomId(employee, week, 'current'));
            return ids;
        },
        setSelectionDomState(key, selected) {
            if (typeof document === 'undefined') return;
            this.selectionDomIds(key).forEach(id => {
                const element = document.getElementById(id);
                if (element) {
                    element.classList.toggle(
                        'workforce-plan-cell--selected',
                        selected
                    );
                }
            });
        },
        syncSelectionDom(keys) {
            const values = selectionUtils.uniqueKeys(keys);
            const next = new Set(values);
            const previous = this._selectionDomKeySet instanceof Set
                ? this._selectionDomKeySet
                : new Set();
            previous.forEach(key => {
                if (!next.has(key)) this.setSelectionDomState(key, false);
            });
            next.forEach(key => {
                if (!previous.has(key)) this.setSelectionDomState(key, true);
            });
            this._selectionDomKeySet = next;
            return values;
        },
        previewSelectedCellKeys(keys) {
            this._pendingSelectedCellKeys = this.syncSelectionDom(keys);
        },
        setSelectedCellKeys(keys) {
            const values = this.syncSelectionDom(keys);
            this._pendingSelectedCellKeys = null;
            this.selectedCellKeys = values;
        },
        commitSelectionPreview() {
            if (!Array.isArray(this._pendingSelectedCellKeys)) return;
            this.selectedCellKeys = this._pendingSelectedCellKeys.slice();
            this._pendingSelectedCellKeys = null;
        },
        cellIsVisuallySelected(employee, week) {
            return this._selectionDomKeySet instanceof Set
                && this._selectionDomKeySet.has(selectionUtils.cellKey(
                    employee.employee_id,
                    week.key
                ));
        },
        applySelectionRectangle(start, end, baseKeys) {
            const result = selectionUtils.rectangleCellKeys({
                employees: this.filteredEmployees,
                weeks: this.weeks,
                employeeIndexes: this.selectionEmployeeIndexes,
                weekIndexes: this.selectionWeekIndexes,
                start,
                end,
                canEditEmployee: this.canEditEmployee
            });
            this.previewSelectedCellKeys(baseKeys
                ? selectionUtils.addCells(baseKeys, result.keys)
                : result.keys);
            this.selectionFocus = end;
            if (
                result.blocked_employee
                && Number(result.blocked_employee.employee_id) !==
                    Number(this.lastBlockedSelectionEmployeeId)
            ) {
                this.lastBlockedSelectionEmployeeId = Number(
                    result.blocked_employee.employee_id
                );
                this.$bvToast.toast(
                    `Выделение остановлено на ${result.blocked_employee.display_name || 'несопоставленной строке'}.`,
                    {variant: 'warning', solid: true, autoHideDelay: 2200}
                );
            }
        },
        selectCell(employee, week) {
            if (
                !this.canEditEmployee(employee)
                || (!this.canEdit && !this.canConfirmArrivalCell(employee, week))
            ) return;
            const coordinate = this.selectionCoordinate(employee, week);
            this.selectionAnchor = coordinate;
            this.selectionFocus = coordinate;
            this.setSelectedCellKeys([
                selectionUtils.cellKey(employee.employee_id, week.key)
            ]);
            this.resetEditorDraft();
            this.emitPlanPresence();
            this.startPresenceHeartbeat();
        },
        startCellDrag(employee, week, event) {
            if (!this.canEditEmployee(employee)) return;
            if (!this.canEdit) {
                if (!this.canConfirmArrivalCell(employee, week)) return;
                this.selectCell(employee, week);
                this.dragSelectionTargetId = event && event.currentTarget
                    ? String(event.currentTarget.id || '')
                    : this.cellDomId(employee, week);
                return;
            }
            const coordinate = this.selectionCoordinate(employee, week);
            const additive = Boolean(event && (event.ctrlKey || event.metaKey));
            const shift = Boolean(event && event.shiftKey && this.selectionAnchor);
            this.dragSelectionActive = true;
            this.lastBlockedSelectionEmployeeId = null;
            this.dragSelectionMoved = false;
            this.dragSelectionAdditive = additive;
            this.dragSelectionBaseKeys = additive ? this.selectedCellKeys.slice() : [];
            this.dragSelectionStart = shift ? this.selectionAnchor : coordinate;
            if (!shift) this.selectionAnchor = coordinate;
            this.selectionFocus = coordinate;
            this.dragSelectionTargetId = event && event.currentTarget
                ? String(event.currentTarget.id || '')
                : this.cellDomId(employee, week);
            this.dragPointerClientX = event && event.clientX;
            this.dragPointerClientY = event && event.clientY;
            this.closeQuickEditor(false);
            if (additive) {
                this.previewSelectedCellKeys(selectionUtils.toggleCell(
                    this.dragSelectionBaseKeys,
                    selectionUtils.cellKey(employee.employee_id, week.key)
                ));
            } else {
                this.applySelectionRectangle(this.dragSelectionStart, coordinate);
            }
            this.startSelectionAutoscroll();
            this.startPresenceHeartbeat();
        },
        extendCellDrag(employee, week, event) {
            if (!this.dragSelectionActive) return;
            const coordinate = this.selectionCoordinate(employee, week);
            if (
                !this.selectionFocus
                || this.selectionFocus.employee_id !== coordinate.employee_id
                || this.selectionFocus.week_key !== coordinate.week_key
            ) this.dragSelectionMoved = true;
            this.dragSelectionTargetId = event && event.currentTarget
                ? String(event.currentTarget.id || '')
                : this.cellDomId(employee, week);
            this.dragPointerClientX = event && event.clientX;
            this.dragPointerClientY = event && event.clientY;
            this.scheduleSelectionRectangle(coordinate);
        },
        stopCellDrag(event) {
            if (!this.dragSelectionActive) return;
            this.flushSelectionRectangle();
            this.cancelSelectionRectangle();
            const openQuickActions = Boolean(
                this.dragSelectionActive
                && this.dragSelectionMoved
                && this.dragSelectionTargetId
                && event
                && event.type === 'mouseup'
            );
            const targetId = this.dragSelectionTargetId;
            this.dragSelectionActive = false;
            this.stopSelectionAutoscroll();
            this.commitSelectionPreview();
            this.dragSelectionAdditive = false;
            this.dragSelectionStart = null;
            this.dragSelectionBaseKeys = [];
            this.emitPlanPresence();
            if (openQuickActions) {
                this.openQuickEditorAt(
                    targetId,
                    '',
                    this.quickEditorEventRect(event)
                );
            } else if (event && event.type === 'blur') {
                this.dragSelectionMoved = false;
                this.dragSelectionTargetId = '';
            }
        },
        scheduleSelectionRectangle(coordinate) {
            this._pendingDragSelectionCoordinate = coordinate;
            if (
                typeof window === 'undefined'
                || typeof window.requestAnimationFrame !== 'function'
            ) {
                this.flushSelectionRectangle();
                return;
            }
            if (this._selectionPreviewFrame) return;
            this._selectionPreviewFrame = window.requestAnimationFrame(() => {
                this._selectionPreviewFrame = null;
                this.flushSelectionRectangle();
            });
        },
        flushSelectionRectangle() {
            const coordinate = this._pendingDragSelectionCoordinate;
            this._pendingDragSelectionCoordinate = null;
            if (!coordinate || !this.dragSelectionActive) return;
            this.applySelectionRectangle(
                this.dragSelectionStart,
                coordinate,
                this.dragSelectionAdditive ? this.dragSelectionBaseKeys : null
            );
        },
        cancelSelectionRectangle() {
            this._pendingDragSelectionCoordinate = null;
            if (
                this._selectionPreviewFrame
                && typeof window !== 'undefined'
                && typeof window.cancelAnimationFrame === 'function'
            ) window.cancelAnimationFrame(this._selectionPreviewFrame);
            this._selectionPreviewFrame = null;
        },
        startSelectionAutoscroll() {
            if (
                this.selectionAutoscrollFrame
                || typeof window === 'undefined'
                || typeof window.requestAnimationFrame !== 'function'
            ) return;
            this.selectionAutoscrollFrame = window.requestAnimationFrame(
                this.runSelectionAutoscroll
            );
        },
        runSelectionAutoscroll() {
            this.selectionAutoscrollFrame = null;
            if (!this.dragSelectionActive) return;
            const viewport = this.$refs.viewport;
            if (viewport && Number.isFinite(this.dragPointerClientX)) {
                const rect = viewport.getBoundingClientRect();
                const threshold = 48;
                let verticalScrollChanged = false;
                if (this.dragPointerClientX > rect.right - threshold) {
                    viewport.scrollLeft += 14;
                } else if (this.dragPointerClientX < rect.left + threshold) {
                    viewport.scrollLeft -= 14;
                }
                if (Number.isFinite(this.dragPointerClientY)) {
                    if (this.dragPointerClientY > rect.bottom - threshold) {
                        viewport.scrollTop += 12;
                        verticalScrollChanged = true;
                    } else if (this.dragPointerClientY < rect.top + threshold) {
                        viewport.scrollTop -= 12;
                        verticalScrollChanged = true;
                    }
                }
                this.syncPlanScroll('viewport');
                if (verticalScrollChanged) this.scheduleVirtualEmployeeRange();
            }
            this.startSelectionAutoscroll();
        },
        stopSelectionAutoscroll() {
            if (
                this.selectionAutoscrollFrame
                && typeof window !== 'undefined'
                && typeof window.cancelAnimationFrame === 'function'
            ) window.cancelAnimationFrame(this.selectionAutoscrollFrame);
            this.selectionAutoscrollFrame = null;
        },
        updateViewportSize() {
            if (typeof window === 'undefined') return;
            this.viewportSize = {
                width: window.innerWidth,
                height: window.innerHeight
            };
            this.scheduleVirtualEmployeeRange();
            if (this.quickEditorOpen) {
                this.quickEditorPosition = this.clampQuickEditorPosition(
                    this.quickEditorPosition.left,
                    this.quickEditorPosition.top
                );
            }
        },
        handleViewportScroll() {
            this.syncPlanScroll('viewport');
            const viewport = this.$refs.viewport;
            const rows = this.$refs.employeeRows;
            if (rows) {
                rows.handleViewportScroll(
                    viewport,
                    this.employeeRowsContentOffset
                );
            }
        },
        scheduleVirtualEmployeeRange() {
            const rows = this.$refs.employeeRows;
            if (rows) {
                rows.scheduleRangeUpdate(
                    this.$refs.viewport,
                    this.employeeRowsContentOffset
                );
                return;
            }
            this.$nextTick(() => {
                const mountedRows = this.$refs.employeeRows;
                if (mountedRows) mountedRows.scheduleRangeUpdate(
                    this.$refs.viewport,
                    this.employeeRowsContentOffset
                );
            });
        },
        updateVirtualEmployeeRange() {
            const rows = this.$refs.employeeRows;
            if (rows) rows.updateRange(
                this.$refs.viewport,
                this.employeeRowsContentOffset
            );
        },
        resetVirtualEmployeeRange() {
            const viewport = this.$refs.viewport;
            if (viewport) viewport.scrollTop = 0;
            const rows = this.$refs.employeeRows;
            if (rows) rows.resetRange(false);
            else this.$nextTick(this.updateVirtualEmployeeRange);
        },
        clampQuickEditorPosition(left, top) {
            const margin = 8;
            const viewportWidth = Number(this.viewportSize.width || (
                typeof window !== 'undefined' ? window.innerWidth : 1280
            ));
            const viewportHeight = Number(this.viewportSize.height || (
                typeof window !== 'undefined' ? window.innerHeight : 720
            ));
            const width = Math.min(390, Math.max(180, viewportWidth - margin * 2));
            const maxLeft = Math.max(margin, viewportWidth - width - margin);
            const maxTop = Math.max(margin, viewportHeight - 180 - margin);
            return {
                left: Math.min(maxLeft, Math.max(margin, Number(left) || margin)),
                top: Math.min(maxTop, Math.max(margin, Number(top) || margin))
            };
        },
        quickEditorTargetRect(targetId) {
            if (typeof document === 'undefined') return null;
            const target = targetId && document.getElementById(targetId);
            if (!target) return null;
            const rect = target.getBoundingClientRect();
            return {
                left: rect.left,
                top: rect.top,
                right: rect.right,
                bottom: rect.bottom
            };
        },
        positionQuickEditor(targetId, suppliedRect) {
            const rect = suppliedRect || this.quickEditorTargetRect(targetId);
            if (!rect) return;
            const margin = 8;
            const viewportWidth = Number(this.viewportSize.width || window.innerWidth);
            const viewportHeight = Number(this.viewportSize.height || window.innerHeight);
            const width = Math.min(390, Math.max(180, viewportWidth - margin * 2));
            const height = Math.min(520, Math.max(180, viewportHeight - margin * 2));
            let left = rect.left;
            let top = rect.bottom + margin;
            if (left + width > viewportWidth - margin) {
                left = viewportWidth - width - margin;
            }
            if (top + height > viewportHeight - margin) {
                top = Math.max(margin, rect.top - height - margin);
            }
            this.quickEditorPosition = this.clampQuickEditorPosition(left, top);
        },
        startQuickEditorDrag(event) {
            if (
                this.isMobileViewport
                || !event
                || event.button !== 0
                || !this.$refs.quickEditor
            ) return;
            const rect = this.$refs.quickEditor.getBoundingClientRect();
            this.quickEditorDragging = true;
            this.quickEditorDragOffset = {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top
            };
        },
        moveQuickEditor(event) {
            if (!this.quickEditorDragging || !event) return;
            event.preventDefault();
            this.quickEditorPosition = this.clampQuickEditorPosition(
                event.clientX - this.quickEditorDragOffset.x,
                event.clientY - this.quickEditorDragOffset.y
            );
        },
        stopQuickEditorDrag() {
            this.quickEditorDragging = false;
        },
        toggleEditorDetails() {
            this.editorDetailsOpen = !this.editorDetailsOpen;
            this.quickEditorPosition = this.clampQuickEditorPosition(
                this.quickEditorPosition.left,
                this.quickEditorPosition.top
            );
        },
        quickEditorEventRect(event) {
            if (
                !event
                || !Number.isFinite(Number(event.clientX))
                || !Number.isFinite(Number(event.clientY))
            ) return null;
            const left = Number(event.clientX);
            const top = Number(event.clientY);
            return {left, right: left, top, bottom: top};
        },
        openQuickEditorAt(targetId, initialQuery, suppliedRect) {
            if (
                !this.hasSelection
                || (!this.canEdit && !this.arrivalActionAvailable
                && !this.selectedArrivalConfirmation && !this.selectedBaselinePresence && !this.hasSelectedFullRows)
            ) return;
            const resolvedTargetId = targetId || this.dragSelectionTargetId;
            const targetRect = suppliedRect
                || this.quickEditorTargetRect(resolvedTargetId);
            this.resetEditorDraft();
            this.editorQuery = String(initialQuery || '');
            this.editorSearchFocused = false;
            this.editorCommandIndex = 0;
            this.positionQuickEditor(resolvedTargetId, targetRect);
            this.quickEditorOpen = true;
            this.$nextTick(() => {
                if (initialQuery && this.$refs.quickEditorSearch) {
                    this.$refs.quickEditorSearch.focus();
                }
            });
        },
        closeQuickEditor(resetDraft = true) {
            if (!this.quickEditorOpen) return;
            this.stopQuickEditorDrag();
            this.quickEditorOpen = false;
            if (resetDraft) this.resetEditorDraft();
        },
        closeQuickEditorFromButton() {
            if (this.isMobileViewport && this.hasSelectedFullRows) {
                this.clearSelection();
                return;
            }
            this.closeQuickEditor();
        },
        handleEditorSearchKeydown(event) {
            if (!event) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                this.closeQuickEditor();
                return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const count = this.filteredCommandOptions.length;
                if (!count) return;
                const direction = event.key === 'ArrowDown' ? 1 : -1;
                this.editorCommandIndex = (
                    this.editorCommandIndex + direction + count
                ) % count;
                return;
            }
            if (event.key !== 'Enter') return;
            event.preventDefault();
            const option = this.filteredCommandOptions[this.editorCommandIndex];
            if (option) this.chooseCommandOption(option);
            if ((event.ctrlKey || event.metaKey) && this.saveAllowed) {
                this.$nextTick(this.saveSelectedRanges);
            }
        },
        deferEditorSearchClose() {
            setTimeout(() => {
                this.editorSearchFocused = false;
            }, 120);
        },
        chooseCommandOption(option) {
            if (!option) return;
            if (option.target) this.applyTargetOption(option.target);
            else this.chooseCellState(option.state_type, false);
            this.editorQuery = '';
            this.editorSearchFocused = false;
            this.editorCommandIndex = 0;
        },
        markEditorDirty() {
            this.editorDirty = true;
        },
        handleCellClick(employee, week, event) {
            if (!this.canEditEmployee(employee)) {
                this.$bvToast.toast(
                    'Сначала сопоставьте сотрудника из таблицы с карточкой Аргуса.',
                    {variant: 'warning', solid: true, autoHideDelay: 2600}
                );
                return;
            }
            if (!this.canEdit && !this.canConfirmArrivalCell(employee, week)) return;
            if (this.dragSelectionMoved) {
                this.dragSelectionMoved = false;
                return;
            }
            const targetId = event && event.currentTarget && event.currentTarget.id
                || this.cellDomId(employee, week);
            const anchorRect = this.quickEditorEventRect(event);
            if (event && (event.ctrlKey || event.metaKey)) {
                this.openQuickEditorAt(targetId, '', anchorRect);
                return;
            }
            const key = selectionUtils.cellKey(employee.employee_id, week.key);
            if (!this.selectionKeySet.has(key)) {
                this.selectCell(employee, week);
            }
            this.openQuickEditorAt(targetId, '', anchorRect);
        },
        prepareClearAction() {
            if (!this.hasSelection || this.saving || !this.canEdit) return;
            if (!this.quickEditorOpen) this.openQuickEditorAt(this.dragSelectionTargetId);
            this.chooseCellState('clear', false);
        },
        canEditEmployee(employee) {
            return Number(employee && employee.employee_id) > 0;
        },
        arrivalTargetKey(value) {
            if (!value || !value.target_type && !value.state_type) return '';
            const type = value.target_type || value.state_type;
            if (type === 'object') return `object:${Number(value.building_id) || 0}`;
            if (type === 'object_group') {
                return `object_group:${Number(value.object_group_id) || 0}`;
            }
            if (type === 'planned_object') {
                return `planned_object:${Number(value.planned_object_id) || 0}`;
            }
            return String(type);
        },
        canConfirmArrivalCell(employee, week) {
            if (
                !employee
                || !week
                || week.visible_starts_on > this.observedToday
            ) return false;
            const state = this.cellState(employee, week);
            const interval = state && state.interval;
            if (!interval) return false;
            if (interval.state_type === 'planned_object') {
                return this.permissions.can_confirm_planned_object_arrival === true;
            }
            if (interval.state_type === 'object') {
                return this.canEdit || (this.permissions.confirmable_object_ids || [])
                    .map(Number)
                    .includes(Number(interval.building_id));
            }
            if (interval.state_type === 'object_group') {
                return this.canEdit || (
                    this.permissions.confirmable_object_group_ids || []
                ).map(Number).includes(Number(interval.object_group_id));
            }
            return false;
        },
        cellDomId(employee, week, suffix) {
            const employeeId = Number(employee && employee.employee_id) || 0;
            const weekKey = String(week && week.key || 'week').replace(/[^a-z0-9_-]/gi, '-');
            return `workforce-plan-cell-${employeeId}-${weekKey}${suffix ? `-${suffix}` : ''}`;
        },
        applyTargetOption(option) {
            this.editor.building_id = null;
            this.editor.object_group_id = null;
            this.editor.planned_object_id = null;
            if (!option || !option.target_type || !option.target_id) return false;
            this.editor.state_type = option.target_type;
            if (option.target_type === 'object') {
                this.editor.building_id = Number(option.target_id);
            } else if (option.target_type === 'object_group') {
                this.editor.object_group_id = Number(option.target_id);
            } else if (option.target_type === 'planned_object') {
                this.editor.planned_object_id = Number(option.target_id);
            } else {
                return false;
            }
            this.editor.loupe_reason = '';
            this.editorDirty = true;
            return true;
        },
        chooseEditorTarget(option) {
            if (!option) {
                this.editor.building_id = null;
                this.editor.object_group_id = null;
                this.editor.planned_object_id = null;
                this.editor.state_type = 'object';
                this.editorDirty = true;
                return;
            }
            this.applyTargetOption(option);
        },
        chooseEditorState(stateType) {
            if (stateType === 'target') {
                if (!['object', 'object_group', 'planned_object'].includes(
                    this.editor.state_type
                )) {
                    this.editor.state_type = 'object';
                }
                return;
            }
            this.chooseCellState(stateType, false);
        },
        chooseCellState(stateType) {
            this.editor.state_type = stateType;
            this.editor.building_id = null;
            this.editor.object_group_id = null;
            this.editor.planned_object_id = null;
            if (stateType !== 'loupe') this.editor.loupe_reason = '';
            this.editorDirty = true;
        },
        resetArrivalDraft() {
            this.arrivalDraft = emptyArrivalDraft();
        },
        clearArrivalWarnings() {
            this.arrivalDraft.warning = null;
            this.arrivalDraft.planned_moves = [];
            this.arrivalDraft.linked_planned_move_id = null;
            this.arrivalDraft.error = '';
        },
        selectArrivalDate(date) {
            this.arrivalDraft.arrival_date = date;
            this.arrivalDraft.other_date_open = false;
            this.clearArrivalWarnings();
        },
        openArrivalDraft(mode) {
            if (!this.arrivalActionAvailable) return;
            const correction = mode === 'correct' && this.selectedArrivalConfirmation;
            const defaultDate = correction
                ? correction.arrival_date
                : '';
            this.arrivalDraft = Object.assign(emptyArrivalDraft(), {
                open: true,
                mode: correction ? 'correct' : 'create',
                arrival_date: defaultDate,
                other_date_open: Boolean(
                    defaultDate
                    && !this.arrivalDateOptions.some(day => day.date === defaultDate)
                )
            });
            this.$nextTick(() => {
                this.quickEditorPosition = this.clampQuickEditorPosition(
                    this.quickEditorPosition.left,
                    this.quickEditorPosition.top
                );
            });
        },
        patchArrivalConfirmation(employeeId, confirmation, replacedId) {
            const employee = this.employees.find(item => (
                Number(item.employee_id) === Number(employeeId)
            ));
            if (!employee) return;
            const confirmations = (Array.isArray(employee.arrival_confirmations)
                ? employee.arrival_confirmations
                : []
            ).filter(item => Number(item.id) !== Number(replacedId || confirmation.id));
            confirmations.push(confirmation);
            confirmations.sort((left, right) => (
                String(left.arrival_date).localeCompare(String(right.arrival_date))
            ));
            this.$set(employee, 'arrival_confirmations', confirmations);
        },
        removeArrivalConfirmation(employeeId, confirmationId) {
            const employee = this.employees.find(item => (
                Number(item.employee_id) === Number(employeeId)
            ));
            if (!employee) return;
            this.$set(
                employee,
                'arrival_confirmations',
                (employee.arrival_confirmations || []).filter(item => (
                    Number(item.id) !== Number(confirmationId)
                ))
            );
        },
        async submitArrivalConfirmation() {
            const selected = this.selectedSingleCell;
            const interval = this.selectedArrivalInterval;
            if (
                !selected
                || !interval
                || !this.arrivalDraft.arrival_date
                || this.arrivalDraft.pending
            ) return;
            const existing = this.selectedArrivalConfirmation;
            const correction = this.arrivalDraft.mode === 'correct' && existing;
            const mutationId = this.createWorkforceMutationId();
            const payload = {
                arrival_date: this.arrivalDraft.arrival_date,
                arrival_discrepancies: this.arrivalDraft.discrepancies.map(item => ({warning_token: item.warning_token,
                    reason: this.arrivalDraft.reason, accept_discrepancy: this.arrivalDraft.accept_discrepancy})),
                confirm_plan_mismatch: Boolean(this.arrivalDraft.warning),
                confirm_fact_replacement: Boolean(this.arrivalDraft.warning),
                linked_planned_move_id:
                    this.arrivalDraft.linked_planned_move_id || null
            };
            const base = `/api/employees/workforce/plan/${Number(selected.employee.employee_id)}/intervals/${Number(interval.id)}/arrival-confirmations`;
            this.arrivalDraft.pending = true;
            this.arrivalDraft.error = '';
            this.rememberOwnWorkforceMutation(mutationId);
            try {
                const config = {
                    ignoreModal: true,
                    headers: {'X-Workforce-Mutation-Id': mutationId}
                };
                const response = correction
                    ? await api.put(`${base}/${Number(existing.id)}`, payload, config)
                    : await api.post(base, payload, config);
                const result = responseData(response);
                if (result.confirmation) {
                    this.patchArrivalConfirmation(
                        selected.employee.employee_id,
                        result.confirmation,
                        correction ? existing.id : null
                    );
                }
                this.resetArrivalDraft();
                await this.loadPlan(true, {background: true});
                if (
                    this.inspectorEmployee
                    && Number(this.inspectorEmployee.employee_id)
                        === Number(selected.employee.employee_id)
                ) this.refreshEmployeeInspector();
                this.$bvToast.toast(
                    correction
                        ? 'Дата фактического прибытия исправлена'
                        : 'Фактическое прибытие подтверждено',
                    {variant: 'success', solid: true, autoHideDelay: 2400}
                );
            } catch (error) {
                this.forgetOwnWorkforceMutation(mutationId);
                const data = error && error.response && error.response.data || {};
                if (data.code === 'TIMESHEET_ARRIVAL_CONFIRMATION_REQUIRED') {
                    this.arrivalDraft.discrepancies = data.details.requirements;
                    this.arrivalDraft.error = 'Укажите объяснение и подтвердите расхождение.';
                } else if (data.code === 'WORKFORCE_PLAN_ARRIVAL_CONFIRMATION_REQUIRED') {
                    this.arrivalDraft.warning = data.details || {};
                    this.arrivalDraft.error = '';
                } else if (
                    data.code === 'WORKFORCE_ARRIVAL_PLANNED_MOVE_CHOICE_REQUIRED'
                ) {
                    this.arrivalDraft.planned_moves = data.details
                        && data.details.planned_moves || [];
                    this.arrivalDraft.linked_planned_move_id = null;
                    this.arrivalDraft.error = '';
                } else {
                    this.arrivalDraft.error = this.errorText(
                        error,
                        'Не удалось подтвердить прибытие'
                    );
                }
            } finally {
                this.arrivalDraft.pending = false;
            }
        },
        async cancelArrivalConfirmation() {
            const selected = this.selectedSingleCell;
            const confirmation = this.selectedArrivalConfirmation;
            if (!selected || !confirmation || this.arrivalDraft.cancelling) return;
            const cancellationText = confirmation.assignment_event_origin === 'created'
                ? 'Фактическое назначение, созданное этим подтверждением, будет отменено. Предыдущее назначение снова станет действующим.'
                : confirmation.assignment_event_origin === 'object_assignment'
                    ? 'Подтверждение будет удалено. Дополнительное назначение сотрудника на объекте сохранится.'
                    : 'Связь с уже существующим фактическим назначением будет удалена. Само назначение из табеля или доски сохранится.';
            const approved = await this.$bvModal.msgBoxConfirm(
                cancellationText,
                {
                    title: 'Отменить подтверждение прибытия?',
                    okTitle: 'Отменить подтверждение',
                    cancelTitle: 'Оставить',
                    okVariant: 'danger',
                    centered: true
                }
            );
            if (!approved) return;
            const mutationId = this.createWorkforceMutationId();
            this.arrivalDraft.cancelling = true;
            this.rememberOwnWorkforceMutation(mutationId);
            try {
                await api.post(
                    `/api/employees/workforce/plan/${Number(selected.employee.employee_id)}/arrival-confirmations/${Number(confirmation.id)}/cancel`,
                    {},
                    {
                        ignoreModal: true,
                        headers: {'X-Workforce-Mutation-Id': mutationId}
                    }
                );
                this.removeArrivalConfirmation(
                    selected.employee.employee_id,
                    confirmation.id
                );
                await this.loadPlan(true, {background: true});
                if (
                    this.inspectorEmployee
                    && Number(this.inspectorEmployee.employee_id)
                        === Number(selected.employee.employee_id)
                ) this.refreshEmployeeInspector();
                this.$bvToast.toast('Подтверждение прибытия отменено', {
                    variant: 'success', solid: true, autoHideDelay: 2200
                });
            } catch (error) {
                this.forgetOwnWorkforceMutation(mutationId);
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось отменить подтверждение прибытия'
                );
            } finally {
                this.arrivalDraft.cancelling = false;
            }
        },
        inspectorCanManage(section) {
            const manage = this.inspectorEmployee
                && this.inspectorEmployee.card_access
                && this.inspectorEmployee.card_access.manage;
            return Boolean(manage && manage[section] === true);
        },
        inspectorPlaceholder(employee) {
            return Object.assign({}, employee, {
                position: employee && (
                    employee.position || employee.operational_position
                ) || null,
                priority: null,
                contacts: [],
                characteristics: [],
                characteristics_total: 0,
                planned_moves: [],
                card_access: {view: {}, manage: {}},
                can_open_card: Number(employee && employee.employee_id) > 0,
                is_absent: false,
                absence: null
            });
        },
        normalizeInspectorEmployee(details, fallback) {
            const value = details || {};
            const absence = value.current_absence || null;
            const presentation = absence && absence.presentation || null;
            const cardAccess = value.permissions && value.permissions.card_access
                || value.card_access
                || fallback && fallback.card_access
                || {view: {}, manage: {}};
            const characteristics = Array.isArray(value.characteristics)
                ? value.characteristics
                : [];
            const plannedMoves = (Array.isArray(value.planned_moves)
                ? value.planned_moves
                : []
            ).map(move => {
                if (!move || move.target_name) return move;
                let targetName = null;
                if (move.target_type === 'object') {
                    const object = this.objects.find(item => (
                        Number(item.building_id) === Number(move.building_id)
                    ));
                    targetName = object && object.name || null;
                } else if (move.target_type === 'object_group') {
                    const group = (this.reconciliation.object_group_catalog || [])
                        .find(item => (
                            Number(item.object_group_id) === Number(move.object_group_id)
                        ));
                    targetName = group && group.name || null;
                }
                return Object.assign({}, move, {target_name: targetName});
            });
            return Object.assign({}, fallback || {}, value, {
                position: value.position
                    || value.operational_position
                    || value.as_of && value.as_of.current_employment
                        && value.as_of.current_employment.official_position
                    || value.latest_employment_period
                        && value.latest_employment_period.official_position
                    || fallback && (fallback.position || fallback.operational_position)
                    || null,
                priority: value.reliability_priority || null,
                department_name: value.operational_department
                    && value.operational_department.name
                    || null,
                contacts: Array.isArray(value.contact_methods)
                    ? value.contact_methods
                    : [],
                card_access: cardAccess,
                can_open_card: true,
                is_absent: Boolean(absence),
                absence: absence ? {
                    id: absence.id,
                    type: absence.absence_type,
                    code: presentation && presentation.code || null,
                    label: presentation && presentation.label || null,
                    starts_on: absence.starts_on,
                    planned_ends_on: absence.planned_ends_on || null,
                    confirmed_ends_on: absence.confirmed_ends_on || null,
                    note: absence.note || null
                } : null,
                absence_label: presentation
                    ? `${presentation.code} — ${presentation.label}`
                    : null,
                characteristics,
                characteristics_total: characteristics.length,
                planned_moves: plannedMoves,
                planned_move: plannedMoves[0] || null
            });
        },
        async openEmployeeInspector(employee, preserveEmployee) {
            this.closeQuickEditor();
            const employeeId = Number(employee && employee.employee_id);
            if (!employeeId || employeeId < 0) {
                this.$bvToast.toast(
                    'Сначала сопоставьте сотрудника с карточкой Аргуса.',
                    {variant: 'warning', solid: true, autoHideDelay: 2600}
                );
                return;
            }
            const requestId = this.inspectorRequestId + 1;
            this.inspectorRequestId = requestId;
            this.inspectorLoading = true;
            const fallback = this.inspectorPlaceholder(employee);
            if (!preserveEmployee) this.inspectorEmployee = fallback;
            try {
                const response = await api.get(
                    `/api/employees/workforce/board/employee/${encodeURIComponent(employeeId)}`,
                    {
                        params: {date: this.currentStatusDate},
                        ignoreModal: true
                    }
                );
                if (requestId !== this.inspectorRequestId) return;
                this.inspectorEmployee = Object.assign(
                    {},
                    fallback,
                    responseData(response)
                );
            } catch (error) {
                if (requestId !== this.inspectorRequestId) return;
                this.inspectorEmployee = fallback;
            } finally {
                if (requestId === this.inspectorRequestId) {
                    this.inspectorLoading = false;
                }
            }
        },
        refreshEmployeeInspector() {
            if (!this.inspectorEmployee) return;
            const employeeId = Number(this.inspectorEmployee.employee_id);
            const employee = this.employees.find(item => (
                Number(item.employee_id) === employeeId
            )) || this.inspectorEmployee;
            this.openEmployeeInspector(employee, true);
        },
        closeEmployeeInspector() {
            this.inspectorRequestId += 1;
            this.inspectorLoading = false;
            this.inspectorEmployee = null;
        },
        selectWeekForAll(week) {
            if (this.filteredEmployees.length === 1) {
                this.selectCell(this.filteredEmployees[0], week);
            }
        },
        pluralize(value, forms) {
            const count = Math.abs(Number(value || 0)) % 100;
            const last = count % 10;
            if (count > 10 && count < 20) return forms[2];
            if (last === 1) return forms[0];
            if (last > 1 && last < 5) return forms[1];
            return forms[2];
        },
        intervalStateSignature(interval) {
            if (!interval) return 'missing';
            if (interval.state_type === 'object') {
                return `object:${Number(interval.building_id) || 0}`;
            }
            if (interval.state_type === 'object_group') {
                return `object_group:${Number(interval.object_group_id) || 0}`;
            }
            if (interval.state_type === 'planned_object') {
                return `planned_object:${Number(interval.planned_object_id) || 0}`;
            }
            return String(interval.state_type || 'missing');
        },
        editorStateSignature() {
            if (this.editor.state_type === 'object') {
                return `object:${Number(this.editor.building_id) || 0}`;
            }
            if (this.editor.state_type === 'object_group') {
                return `object_group:${Number(this.editor.object_group_id) || 0}`;
            }
            if (this.editor.state_type === 'planned_object') {
                return `planned_object:${Number(this.editor.planned_object_id) || 0}`;
            }
            return String(this.editor.state_type || 'missing');
        },
        resetEditorDraft() {
            this.arrivalDraft = emptyArrivalDraft();
            this.editor = {
                state_type: '',
                building_id: null,
                object_group_id: null,
                planned_object_id: null,
                loupe_reason: '',
                loupe_reason_mode: 'preserve',
                note: '',
                note_mode: 'preserve'
            };
            this.editorQuery = '';
            this.editorSearchFocused = false;
            this.editorCommandIndex = 0;
            this.editorDirty = false;
            this.editorDetailsOpen = false;
            const intervals = this.selectedCellCoordinates.map(({employee, week}) => {
                const state = this.cellState(employee, week);
                return state && state.interval || null;
            });
            const signatures = new Set(intervals.map(interval => (
                this.intervalStateSignature(interval)
            )));
            if (signatures.size === 1 && intervals.length) {
                const interval = intervals[0];
                if (interval) {
                    this.editor.state_type = interval.state_type || '';
                    this.editor.building_id = interval.building_id || null;
                    this.editor.object_group_id = interval.object_group_id || null;
                    this.editor.planned_object_id = interval.planned_object_id || null;
                }
            }
            if (this.selectionUnifiedPeriod) {
                this.exactStartsOn = this.selectionUnifiedPeriod.starts_on;
                this.exactEndsOnInclusive = planUtils.addDays(
                    this.selectionUnifiedPeriod.ends_on,
                    -1
                );
            } else {
                this.exactStartsOn = '';
                this.exactEndsOnInclusive = '';
            }
        },
        clearSelection() {
            this.emitPlanPresence(true);
            this.stopPresenceHeartbeat();
            this.stopSelectionAutoscroll();
            this.cancelSelectionRectangle();
            this.quickEditorOpen = false;
            this.dragSelectionActive = false;
            this.dragSelectionMoved = false;
            this.dragSelectionTargetId = '';
            this.dragSelectionStart = null;
            this.dragSelectionBaseKeys = [];
            this.dragSelectionAdditive = false;
            this.selectionAnchor = null;
            this.selectionFocus = null;
            this.rowCopyHintVisible = false;
            this.setSelectedCellKeys([]);
            this.resetEditorDraft();
        },
        cellState(employee, week) {
            const state = planUtils.weekStateFromIndex(
                this.weekStateIndex,
                employee,
                week
            );
            return state === undefined
                ? planUtils.weekState(employee.intervals, week)
                : state;
        },
        currentCellInterval(employee) {
            const employeeId = Number(employee && employee.employee_id);
            if (this.currentIntervalIndex.has(employeeId)) {
                return this.currentIntervalIndex.get(employeeId);
            }
            return planUtils.stateOnDate(employee.intervals, this.currentStatusDate);
        },
        automaticPlanningColor(target) {
            const source = String(target && target.key || target || 'planning-target');
            const [type, id] = source.split(':');
            const catalog = type === 'object' ? this.objects : type === 'object_group'
                ? this.objectGroups : this.workforcePlannedObjects;
            const field = type === 'object' ? 'building_id' : type === 'object_group' ? 'object_group_id' : 'planned_object_id';
            const entry = (catalog || []).find(item => Number(item[field]) === Number(id));
            if (entry && planDisplay.isTargetColor(entry.automatic_color)) return entry.automatic_color;
            if (entry && entry.planning_color_source !== 'custom' && planDisplay.isTargetColor(entry.planning_color)) return entry.planning_color;
            let hash = 2166136261;
            for (let index = 0; index < source.length; index += 1) {
                hash ^= source.charCodeAt(index);
                hash = Math.imul(hash, 16777619) >>> 0;
            }
            return planDisplay.TARGET_PALETTE[hash % planDisplay.TARGET_PALETTE.length];
        },
        planningColorText(color) {
            return DEFAULT_PLANNING_COLOR_TEXT[String(color || '').toLowerCase()]
                || '#30485d';
        },
        planningTargetPalette(targetType, targetId, color, colorSource) {
            const key = `${targetType}:${Number(targetId)}`;
            const normalizedColor = planDisplay.normalizeTargetColor(color);
            const custom = colorSource === 'custom'
                && planDisplay.isTargetColor(normalizedColor);
            const background = custom
                ? normalizedColor
                : this.automaticPlanningColor(key);
            return {
                target_type: targetType,
                target_id: Number(targetId),
                background,
                color: this.planningColorText(background),
                color_source: custom ? 'custom' : 'auto'
            };
        },
        summaryCount(row, weekIndex) {
            const week = this.weeklySummary[weekIndex];
            if (!week) return 0;
            if (row.kind === 'object') {
                return Number(week.objects[row.building_id] || 0);
            }
            if (row.kind === 'object-group') {
                return Number(
                    week.object_groups && week.object_groups[row.object_group_id] || 0
                );
            }
            if (row.kind === 'planned-object') {
                return Number(
                    week.planned_objects
                    && week.planned_objects[row.planned_object_id]
                    || 0
                );
            }
            return Number(week[row.count_key] || 0);
        },
        currentSummaryCount(row) {
            const counts = this.currentAssignmentCounts;
            if (row.kind === 'object') {
                return Number(counts.objects[row.building_id] || 0);
            }
            if (row.kind === 'object-group') {
                return Number(counts.object_groups[row.object_group_id] || 0);
            }
            if (row.kind === 'planned-object') {
                return Number(counts.planned_objects[row.planned_object_id] || 0);
            }
            return Number(counts[row.count_key] || 0);
        },
        summaryBreakdownFromCounts(counts, row) {
            const breakdown = counts && counts.breakdown || {};
            let value;
            if (row.kind === 'object') {
                value = breakdown.objects && breakdown.objects[row.building_id];
            } else if (row.kind === 'object-group') {
                value = breakdown.object_groups
                    && breakdown.object_groups[row.object_group_id];
            } else if (row.kind === 'planned-object') {
                value = breakdown.planned_objects
                    && breakdown.planned_objects[row.planned_object_id];
            } else {
                value = breakdown[row.count_key];
            }
            return value || {total: 0, workers: 0, itr: 0};
        },
        summaryBreakdown(row, weekIndex) {
            return this.summaryBreakdownFromCounts(
                this.weeklySummary[weekIndex],
                row
            );
        },
        currentSummaryBreakdown(row) {
            return this.summaryBreakdownFromCounts(
                this.currentAssignmentCounts,
                row
            );
        },
        currentSummaryTitle(row) {
            const counts = this.currentSummaryBreakdown(row);
            return this.summaryTooltipTitle(
                row,
                `Состояние на ${this.formatDate(this.currentStatusDate)}`,
                counts
            );
        },
        currentCellTitle(employee) {
            const interval = this.currentCellInterval(employee);
            const parts = [
                employee.display_name,
                `Состояние на ${this.formatDate(this.currentStatusDate)}`
            ];
            if (!interval) {
                parts.push('План не заполнен');
            } else {
                parts.push(this.stateTitle(interval));
                if (interval.auto_unassigned) parts.push('Заполнено автоматически: «Не распределён»');
                if (interval.auto_loupe) parts.push('Автопродление выбытия до ' + this.formatDate(planUtils.addDays(interval.loupe_autofill_until, -1)) + (interval.loupe_autofill_rolling ? '. Каждую новую неделю продлевается на неделю, пока не прервано.' : ''));
                parts.push(`${this.formatDate(interval.starts_on)} — ${this.formatDate(planUtils.addDays(interval.ends_on, -1))}`);
            }
            const remote = this.currentStatusWeek
                ? this.remoteEditorFor(employee, this.currentStatusWeek)
                : null;
            if (remote) {
                parts.push(`Сейчас редактирует: ${remote.actor.display_name || 'Коллега'}`);
            }
            if (this.currentStatusWeek) {
                const arrival = planUtils.currentArrivalConfirmation(employee, interval, this.currentStatusDate);
                if (arrival) {
                    parts.push(
                        `✓ Фактическое прибытие подтверждено ${this.formatDate(arrival.arrival_date)}`
                    );
                }
            }
            return parts.join('\n');
        },
        summaryRowClasses(row) {
            return {
                [`workforce-plan-summary--${row.kind}`]: true,
                'workforce-plan-summary--stacked': Boolean(row.stacked)
            };
        },
        summaryLabelStyle(row) {
            if (!['object', 'object-group', 'planned-object'].includes(row.kind)) return {};
            return this.planColorStyle(row.background, row.color);
        },
        summaryCellStyle(row, weekIndex) {
            if (
                !['object', 'object-group', 'planned-object'].includes(row.kind)
                || !this.summaryCount(row, weekIndex)
            ) return {};
            return this.planColorStyle(row.background, row.color);
        },
        currentSummaryCellStyle(row) {
            if (
                !['object', 'object-group', 'planned-object'].includes(row.kind)
                || !this.currentSummaryCount(row)
            ) return {};
            return this.planColorStyle(row.background, row.color);
        },
        intervalPlanningTarget(interval) {
            if (!interval) return null;
            if (interval.state_type === 'object' && interval.building_id) {
                return {type: 'object', id: Number(interval.building_id)};
            }
            if (interval.state_type === 'object_group' && interval.object_group_id) {
                return {type: 'object_group', id: Number(interval.object_group_id)};
            }
            if (interval.state_type === 'planned_object' && interval.planned_object_id) {
                return {type: 'planned_object', id: Number(interval.planned_object_id)};
            }
            return null;
        },
        intervalPlanningColor(interval, colors) {
            const target = this.intervalPlanningTarget(interval);
            if (!target) return null;
            const key = `${target.type}:${target.id}`;
            const supplied = planDisplay.normalizeTargetColor(interval.target_color);
            return (colors || this.planningTargetColorIndex).get(key)
                || (planDisplay.isTargetColor(supplied) ? supplied : this.automaticPlanningColor(key));
        },
        cellStyleFromValues(interval, remote, colors) {
            const style = remote ? {'--plan-presence-color': remote.color} : {};
            const stateColors = interval && planDisplay.STATE_COLORS[interval.state_type];
            const targetColor = this.intervalPlanningColor(interval, colors);
            const color = targetColor || stateColors && stateColors[0];
            if (!color) return style;
            Object.assign(style, this.planColorStyle(color, targetColor ? this.planningColorText(color) : stateColors[1]));
            if (interval.state_type === 'planned_object') {
                style.borderColor = style.color;
            }
            return style;
        },
        cellTitleFromValues(employee, week, state, arrival, remote, current) {
            const parts = [
                employee.display_name,
                current
                    ? `Состояние на ${this.formatDate(this.currentStatusDate)}`
                    : this.weekTitle(week)
            ];
            if (!state || !state.interval) {
                parts.push('План не заполнен');
            } else {
                const interval = state.interval;
                parts.push(this.stateTitle(interval));
                if (interval.auto_unassigned) parts.push('Заполнено автоматически: «Не распределён»');
                if (interval.auto_loupe) parts.push('Автопродление выбытия до ' + this.formatDate(planUtils.addDays(interval.loupe_autofill_until, -1)) + (interval.loupe_autofill_rolling ? '. Каждую новую неделю продлевается на неделю, пока не прервано.' : ''));
                parts.push(`${this.formatDate(interval.starts_on)} — ${this.formatDate(planUtils.addDays(interval.ends_on, -1))}`);
                if (!current && state.mixed) {
                    parts.push('Внутри недели есть изменение состояния');
                }
            }
            if (remote) {
                parts.push(`Сейчас редактирует: ${remote.actor && remote.actor.display_name || 'Коллега'}`);
            }
            if (arrival) {
                parts.push(
                    `✓ Фактическое прибытие подтверждено ${this.formatDate(arrival.arrival_date)}`
                );
            }
            const baseline = this.baselineForCell(employee, week, state && state.interval, current);
            if (baseline) parts.push(this.baselinePresenceTitle(baseline));
            return parts.join('\n');
        },
        baselineForCell(employee, week, interval, current) {
            const from = current ? this.currentStatusDate : week.visible_starts_on;
            const to = current ? planUtils.addDays(from, 1) : week.visible_ends_on;
            return planUtils.baselinePresenceForRange(employee, interval, from, to);
        },
        baselinePresenceTitle(period) {
            const object = this.objects.find(item => Number(item.building_id) === Number(period.building_id));
            const name = object && (object.name || object.target_name) || 'Объект';
            return `Присутствие принято по табелю при переносе ${this.formatDate(period.snapshot_date)}: ${name}. Дата прибытия не устанавливалась.`;
        },
        cellPresentation(employee, week, current, indexes) {
            const interval = current ? (indexes ? indexes.current.get(Number(employee.employee_id)) : this.currentCellInterval(employee)) : null;
            const state = current
                ? {interval, mixed: false}
                : indexes ? planUtils.weekStateFromIndex(indexes.states, employee, week) : this.cellState(employee, week);
            const resolvedInterval = state && state.interval || null;
            const type = resolvedInterval && resolvedInterval.state_type || 'missing';
            const cellKey = indexes ? selectionUtils.cellKey(employee.employee_id, week.key) : null;
            const remote = indexes ? indexes.remote.get(cellKey) || null : this.remoteEditorFor(employee, week);
            const arrival = current
                ? planUtils.currentArrivalConfirmation(employee, resolvedInterval, this.currentStatusDate)
                : indexes ? indexes.arrivals.get(cellKey) || null : this.arrivalConfirmationForWeek(employee, week);
            const classes = {
                [`workforce-plan-cell--${type}`]: true,
                'workforce-plan-cell--selected': this.cellIsVisuallySelected(
                    employee,
                    week
                ),
                'workforce-plan-cell--remote': Boolean(remote)
            };
            if (!current) {
                classes['workforce-plan-cell--mixed'] = Boolean(state && state.mixed);
                classes['workforce-plan-cell--month'] = week.is_month_start;
                classes['workforce-plan-cell--today'] = week.is_current;
            }
            const remoteName = remote && remote.actor && remote.actor.display_name || 'К';
            return {
                classes,
                style: this.cellStyleFromValues(resolvedInterval, remote, indexes && indexes.colors),
                title: this.cellTitleFromValues(
                    employee,
                    week,
                    state,
                    arrival,
                    remote,
                    current
                ),
                label: this.intervalCellLabel(resolvedInterval),
                arrival: Boolean(arrival),
                baseline: Boolean(current && !arrival && this.baselineForCell(employee, week, resolvedInterval, current)),
                remoteInitials: remote
                    ? remoteName.split(/\s+/).slice(0, 2)
                        .map(part => part[0]).join('').toUpperCase()
                    : ''
            };
        },
        cellStyle(employee, week, current) {
            const interval = current
                ? this.currentCellInterval(employee)
                : (() => {
                    const state = this.cellState(employee, week);
                    return state && state.interval;
                })();
            return this.cellStyleFromValues(interval, this.remoteEditorFor(employee, week));
        },
        applyPlanningTargetColor(targetType, targetId, presentation) {
            const color = presentation && presentation.planning_color || null;
            const source = presentation && presentation.planning_color_source || 'auto';
            let rows = [];
            let idField = '';
            if (targetType === 'object') {
                rows = this.objects;
                idField = 'building_id';
            } else if (targetType === 'object_group') {
                rows = this.objectGroups;
                idField = 'object_group_id';
            } else if (targetType === 'planned_object') {
                rows = this.workforcePlannedObjects;
                idField = 'planned_object_id';
            }
            const target = rows.find(item => (
                Number(item[idField]) === Number(targetId)
            ));
            if (target) {
                this.$set(target, 'planning_color', color);
                this.$set(target, 'planning_color_source', source);
                this.$set(target, 'automatic_color', presentation.automatic_color || color);
            }
            this.employees.forEach(employee => {
                (employee.intervals || []).forEach(interval => {
                    const intervalTarget = this.intervalPlanningTarget(interval);
                    if (
                        intervalTarget
                        && intervalTarget.type === targetType
                        && intervalTarget.id === Number(targetId)
                    ) this.$set(interval, 'target_color', color);
                });
            });
        },
        async savePlanningTargetColor(row, color) {
            if (!this.permissions.can_edit) {
                throw new Error('Нет права изменять цвет в плане.');
            }
            const targetType = row.target_type;
            const targetId = Number(row.target_id);
            const mutationId = this.createWorkforceMutationId();
            this.rememberOwnWorkforceMutation(mutationId);
            try {
                const response = await api.put(
                    `/api/employees/workforce/planning-targets/${targetType}/${targetId}/color`,
                    {planning_color: color},
                    {
                        ignoreModal: true,
                        headers: {'X-Workforce-Mutation-Id': mutationId}
                    }
                );
                const result = responseData(response);
                this.applyPlanningTargetColor(targetType, targetId, result);
                return result;
            } catch (error) {
                this.forgetOwnWorkforceMutation(mutationId);
                throw error;
            }
        },
        summaryCellTitle(row, week, weekIndex) {
            const counts = this.summaryBreakdown(row, weekIndex);
            return this.summaryTooltipTitle(row, this.weekTitle(week), counts);
        },
        escapeTooltipHtml(value) {
            return String(value == null ? '' : value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },
        summaryTooltipTitle(row, period, counts) {
            const label = this.escapeTooltipHtml(row.label);
            const dateRange = this.escapeTooltipHtml(period);
            return '<div class="text-left">' +
                `<div><strong>${label}</strong></div>` +
                (row.description
                    ? `<div class="mt-1">${this.escapeTooltipHtml(row.description)}</div>`
                    : '') +
                `<div class="mt-1">${dateRange}</div>` +
                '<div class="mt-2 pt-1 border-top">' +
                `<div>Всего: <strong>${counts.total}</strong></div>` +
                `<div>Рабочие: <strong>${counts.workers}</strong></div>` +
                `<div>ИТР: <strong>${counts.itr}</strong></div>` +
                '</div>' +
                `<div class="mt-2"><small>${row.stacked
                    ? 'Сверху — рабочие, снизу — ИТР'
                    : 'В ячейке: рабочие / ИТР'}</small></div>` +
                '</div>';
        },
        cellClasses(employee, week) {
            const state = this.cellState(employee, week);
            const type = state && state.interval && state.interval.state_type || 'missing';
            return {
                [`workforce-plan-cell--${type}`]: true,
                'workforce-plan-cell--mixed': Boolean(state && state.mixed),
                'workforce-plan-cell--month': week.is_month_start,
                'workforce-plan-cell--selected': this.cellIsVisuallySelected(employee, week),
                'workforce-plan-cell--today': week.is_current,
                'workforce-plan-cell--remote': Boolean(this.remoteEditorFor(employee, week))
            };
        },
        currentCellClasses(employee) {
            const interval = this.currentCellInterval(employee);
            const type = interval && interval.state_type || 'missing';
            return {
                [`workforce-plan-cell--${type}`]: true,
                'workforce-plan-cell--selected': this.currentStatusWeek
                    ? this.cellIsVisuallySelected(employee, this.currentStatusWeek)
                    : false,
                'workforce-plan-cell--remote': this.currentStatusWeek
                    ? Boolean(this.remoteEditorFor(employee, this.currentStatusWeek))
                    : false
            };
        },
        intervalCellLabel(interval) {
            if (!interval) return '';
            if (interval.state_type === 'requires_distribution') return '❌';
            if (interval.state_type === 'rest') return 'ОТДХ';
            if (interval.state_type === 'loupe') return '🔍';
            return interval.target_code || this.shortObjectName(interval.target_name);
        },
        currentCellLabel(employee) {
            return this.intervalCellLabel(this.currentCellInterval(employee));
        },
        cellLabel(employee, week) {
            const state = this.cellState(employee, week);
            return this.intervalCellLabel(state && state.interval);
        },
        arrivalConfirmationForWeek(employee, week) {
            if (!employee || !week) return null;
            return this.arrivalConfirmationCellIndex.get(
                selectionUtils.cellKey(employee.employee_id, week.key)
            ) || null;
        },
        arrivalMarker(employee, week) {
            return this.arrivalConfirmationForWeek(employee, week) ? '✓' : '';
        },
        cellTitle(employee, week) {
            const state = this.cellState(employee, week);
            const remote = this.remoteEditorFor(employee, week);
            const parts = [employee.display_name, this.weekTitle(week)];
            if (!state || !state.interval) {
                parts.push('План не заполнен');
            } else {
                const interval = state.interval;
                parts.push(this.stateTitle(interval));
                parts.push(`${this.formatDate(interval.starts_on)} — ${this.formatDate(planUtils.addDays(interval.ends_on, -1))}`);
                if (state.mixed) parts.push('Внутри недели есть изменение состояния');
                if (interval.auto_unassigned) parts.push('Заполнено автоматически: «Не распределён»');
                if (interval.auto_loupe) parts.push('Автопродление выбытия до ' + this.formatDate(planUtils.addDays(interval.loupe_autofill_until, -1)) + (interval.loupe_autofill_rolling ? '. Каждую новую неделю продлевается на неделю, пока не прервано.' : ''));
            }
            if (remote) {
                parts.push(`Сейчас редактирует: ${remote.actor.display_name || 'Коллега'}`);
            }
            const arrival = this.arrivalConfirmationForWeek(employee, week);
            if (arrival) {
                parts.push(
                    `✓ Фактическое прибытие подтверждено ${this.formatDate(arrival.arrival_date)}`
                );
            }
            return parts.join('\n');
        },
        stateTitle(interval) {
            if (interval.state_type === 'object') return interval.target_name || 'Объект';
            if (interval.state_type === 'object_group') {
                return interval.target_name
                    ? `Группа объектов — ${interval.target_name}`
                    : 'Группа объектов';
            }
            if (interval.state_type === 'planned_object') {
                return interval.target_name
                    ? `Плановый объект — ${interval.target_name}. Контракт не заключён.`
                    : 'Плановый объект. Контракт не заключён.';
            }
            if (interval.state_type === 'requires_distribution') return '❌ Не распределён';
            if (interval.state_type === 'rest') return 'ОТДХ — отдых';
            if (interval.state_type === 'loupe') {
                return interval.loupe_reason
                    ? `🔍 Выбыл из планирования: ${interval.loupe_reason}`
                    : '🔍 Выбыл из планирования';
            }
            return 'План не заполнен';
        },
        weekTitle(week) {
            return `${this.formatDate(week.visible_starts_on)} — ${this.formatDate(planUtils.addDays(week.visible_ends_on, -1))}`;
        },
        shortWeekRange(week) {
            return `${this.shortDate(week.visible_starts_on)}–${this.shortDate(planUtils.addDays(week.visible_ends_on, -1))}`;
        },
        shortDate(value) {
            return `${value.slice(8, 10)}.${value.slice(5, 7)}`;
        },
        monthLabelLong(value) {
            const month = value.slice(5, 7);
            const label = ({
                '01': 'Январь', '02': 'Февраль', '03': 'Март', '04': 'Апрель',
                '05': 'Май', '06': 'Июнь', '07': 'Июль', '08': 'Август',
                '09': 'Сентябрь', '10': 'Октябрь', '11': 'Ноябрь', '12': 'Декабрь'
            })[month] || '';
            return label;
        },
        monthInitial(value) {
            return this.monthLabelLong(value).slice(0, 1).toLocaleUpperCase('ru-RU');
        },
        formatDate(value) {
            const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
            return match ? `${match[3]}.${match[2]}.${match[1]}` : String(value || '');
        },
        formatArrivalTimestamp(value) {
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return 'Время не указано';
            return new Intl.DateTimeFormat('ru-RU', {
                timeZone: 'Europe/Minsk', day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            }).format(date) + ' (Минск)';
        },
        toggleArrivalDetails() {
            this.arrivalDetailsExpanded = !this.arrivalDetailsExpanded;
            try {
                window.localStorage.setItem('argus.workforce.arrivalDetailsExpanded', String(this.arrivalDetailsExpanded));
            } catch (error) { /* Keep the current state in memory when storage is unavailable. */ }
        },
        shortObjectName(value) {
            const words = String(value || 'Объект').trim().split(/\s+/);
            if (words.length > 1) {
                const initials = words.slice(0, 3).map(word => word[0]).join('');
                if (initials.length >= 2) return initials.toLocaleUpperCase('ru-RU');
            }
            return words[0].slice(0, 7);
        },
        scrollToPlanDate(date) {
            const viewport = this.$refs.viewport;
            const index = this.weeks.findIndex(week => week.visible_starts_on <= date && date < week.visible_ends_on);
            if (!viewport || index < 0 || this.zoom === 'fit') return;
            const width = parseInt(this.weekWidth, 10) || 30;
            this.setPlanScrollLeft(
                Math.max(
                    0,
                    this.pinnedColumnsWidth + index * width + width / 2 - viewport.clientWidth / 2
                )
            );
        },
        scrollToToday() {
            this.scrollToPlanDate(this.filterDate);
        },
        centerCurrentWeek() {
            if (this.routeDateIntent && this.$route.query.plan_intent === 'focus') return;
            this.$nextTick(() => {
                this.scrollToToday();
                if (
                    typeof window !== 'undefined'
                    && typeof window.requestAnimationFrame === 'function'
                ) {
                    window.requestAnimationFrame(this.scrollToToday);
                }
            });
        },
        scrollToFilterWeek() {
            const viewport = this.$refs.viewport;
            const index = this.weeks.findIndex(week => week.is_filter);
            if (!viewport || index < 0 || this.zoom === 'fit') return;
            const width = parseInt(this.weekWidth, 10) || 30;
            this.setPlanScrollLeft(
                Math.max(
                    0,
                    this.pinnedColumnsWidth + index * width - viewport.clientWidth / 2
                )
            );
        },
        scrollPlanByWeeks(amount) {
            const viewport = this.$refs.viewport;
            if (!viewport || this.zoom === 'fit') return;
            const width = parseInt(this.weekWidth, 10) || 44;
            const next = Math.max(0, viewport.scrollLeft + Number(amount || 0) * width);
            if (typeof viewport.scrollTo === 'function') {
                viewport.scrollTo({left: next, behavior: 'smooth'});
            } else {
                viewport.scrollLeft = next;
            }
            if (this.$refs.topScrollbar) this.$refs.topScrollbar.scrollLeft = next;
        },
        scrollPlanToPeople() {
            const viewport = this.$refs.viewport;
            if (!viewport) return;
            if (typeof viewport.scrollTo === 'function') {
                viewport.scrollTo({left: 0, behavior: 'smooth'});
            } else {
                viewport.scrollLeft = 0;
            }
            if (this.$refs.topScrollbar) this.$refs.topScrollbar.scrollLeft = 0;
        },
        startMobileContextPreview(event) {
            this.hideMobileContextHint(false);
            if (event && event.pointerType === 'mouse' && event.button !== 0) return;
            if (this._mobileContextPreviewTimer) {
                window.clearTimeout(this._mobileContextPreviewTimer);
            }
            this._mobileContextPreviewSuppressClick = false;
            if (
                event
                && event.currentTarget
                && typeof event.currentTarget.setPointerCapture === 'function'
            ) {
                try {
                    event.currentTarget.setPointerCapture(event.pointerId);
                } catch (error) {
                    // Старые мобильные браузеры продолжат работать без захвата указателя.
                }
            }
            this._mobileContextPreviewTimer = window.setTimeout(() => {
                this._mobileContextPreviewTimer = null;
                this._mobileContextPreviewSuppressClick = true;
                this.mobileContextPreviewing = true;
            }, 220);
        },
        finishMobileContextPreview() {
            if (this._mobileContextPreviewTimer) {
                window.clearTimeout(this._mobileContextPreviewTimer);
                this._mobileContextPreviewTimer = null;
            }
            if (!this.mobileContextPreviewing) return;
            this.mobileContextPreviewing = false;
            this._mobileContextPreviewSuppressClick = true;
            window.setTimeout(() => {
                this._mobileContextPreviewSuppressClick = false;
            }, 0);
        },
        toggleMobileContext(event) {
            if (this._mobileContextPreviewSuppressClick) {
                if (event && typeof event.preventDefault === 'function') event.preventDefault();
                return;
            }
            this.mobileContextPinned = !this.mobileContextPinned;
            this.mobileContextPreviewing = false;
        },
        showMobileContextHintIfNeeded() {
            if (!this.isMobileViewport) return;
            const storage = this.browserStorage();
            const key = 'argus:workforce-plan:mobile-context-hint:v1';
            let views = 0;
            if (storage) {
                try {
                    views = Math.max(0, Number(storage.getItem(key)) || 0);
                    if (views >= 3) return;
                    storage.setItem(key, String(views + 1));
                } catch (error) {
                    // Подсказка продолжит работать и без доступного localStorage.
                }
            }
            this.mobileContextHintVisible = true;
            if (this._mobileContextHintTimer) {
                window.clearTimeout(this._mobileContextHintTimer);
            }
            this._mobileContextHintTimer = window.setTimeout(() => {
                this._mobileContextHintTimer = null;
                this.mobileContextHintVisible = false;
            }, 8000);
        },
        hideMobileContextHint(permanently) {
            this.mobileContextHintVisible = false;
            if (this._mobileContextHintTimer) {
                window.clearTimeout(this._mobileContextHintTimer);
                this._mobileContextHintTimer = null;
            }
            if (!permanently) return;
            const storage = this.browserStorage();
            if (!storage) return;
            try {
                storage.setItem('argus:workforce-plan:mobile-context-hint:v1', '3');
            } catch (error) {
                // Ручное закрытие подсказки не должно мешать работе плана.
            }
        },
        setPlanScrollLeft(value) {
            if (this.$refs.viewport) this.$refs.viewport.scrollLeft = value;
            if (this.$refs.topScrollbar) this.$refs.topScrollbar.scrollLeft = value;
        },
        syncPlanScroll(source) {
            const from = source === 'top'
                ? this.$refs.topScrollbar
                : this.$refs.viewport;
            const to = source === 'top'
                ? this.$refs.viewport
                : this.$refs.topScrollbar;
            if (!from || !to || Math.abs(to.scrollLeft - from.scrollLeft) < 1) return;
            to.scrollLeft = from.scrollLeft;
        },
        setupRealtime() {
            if (this._workforcePlanDestroyed) return;
            socket.off('connect', this.onRealtimeConnect);
            socket.off('disconnect', this.onRealtimeDisconnect);
            socket.off('connect_error', this.onRealtimeDisconnect);
            socket.off('workforce:changed', this.onWorkforceChanged);
            socket.off('workforce:plan-presence', this.onPlanPresence);
            socket.on('connect', this.onRealtimeConnect);
            socket.on('disconnect', this.onRealtimeDisconnect);
            socket.on('connect_error', this.onRealtimeDisconnect);
            socket.on('workforce:changed', this.onWorkforceChanged);
            socket.on('workforce:plan-presence', this.onPlanPresence);
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                this.syncStatus = 'offline';
                return;
            }
            if (socket.connected) this.subscribeRealtime();
            else socket.connect();
        },
        onRealtimeConnect() {
            if (this._workforcePlanDestroyed) return;
            this.subscribeRealtime();
        },
        onRealtimeDisconnect() {
            this.syncStatus = typeof navigator !== 'undefined' && navigator.onLine === false
                ? 'offline'
                : 'reconnecting';
        },
        subscribeRealtime() {
            if (this._workforcePlanDestroyed) return;
            const token = window.localStorage.getItem('token');
            if (!socket.connected || !token) {
                this.syncStatus = 'offline';
                return;
            }
            this.syncStatus = 'connecting';
            socket.emit('workforce:subscribe', {token}, response => {
                if (this._workforcePlanDestroyed) return;
                this.syncStatus = response && response.ok ? 'connected' : 'reconnecting';
                if (response && response.ok && this.hasSelection) this.emitPlanPresence();
            });
        },
        onWorkforceChanged(event) {
            if (this.consumeOwnWorkforceMutation(event)) return;
            if (PLAN_GRID_IGNORED_REALTIME_CHANGES.has(String(
                event && event.change_type || ''
            ))) return;
            if (this.realtimeRefreshTimer) window.clearTimeout(this.realtimeRefreshTimer);
            this.realtimeRefreshTimer = window.setTimeout(() => {
                this.realtimeRefreshTimer = null;
                if (this._workforcePlanDestroyed) return;
                this.loadPlan(true, {background: true});
                const actor = event && event.actor && event.actor.display_name;
                if (actor) {
                    this.$bvToast.toast(`План обновил ${actor}`, {
                        variant: 'info', solid: true, autoHideDelay: 2200
                    });
                }
            }, 250);
        },
        emitPlanPresence(clear) {
            if (!socket.connected || this.syncStatus !== 'connected') return;
            if (clear || !this.hasSelection) {
                socket.emit('workforce:plan-editing', {
                    employee_id: null,
                    starts_on: null,
                    ends_on: null,
                    ranges: []
                });
                return;
            }
            const ranges = this.selectionRanges.map(range => ({
                employee_id: range.employee_id,
                starts_on: range.starts_on,
                ends_on: range.ends_on
            }));
            const first = ranges[0];
            socket.emit('workforce:plan-editing', {
                employee_id: first && first.employee_id || null,
                starts_on: first && first.starts_on || null,
                ends_on: first && first.ends_on || null,
                ranges
            });
        },
        async followCollaborator(person) {
            if (this.periodNavigationDisabled) return;
            const editor = activeEditorForUser(this.remoteEditors, person.user_id);
            if (!editor) return;
            const range = editor.ranges[0];
            const request = (this._collaborationJump || 0) + 1;
            this._collaborationJump = request;
            const intersectsWeek = week => week.visible_starts_on < range.ends_on && week.visible_ends_on > range.starts_on;
            if (!this.weeks.some(intersectsWeek)) {
                await this.navigatePlanPeriod(range.starts_on);
            }
            if (this._workforcePlanDestroyed || this._collaborationJump !== request) return;
            if (!this.planWindowReady) return;
            const employeeId = Number(range.employee_id);
            if (!this.employees.some(employee => Number(employee.employee_id) === employeeId)) {
                this.$bvToast.toast('Сотрудник из выделения недоступен в вашем плане.', {variant: 'info', autoHideDelay: 3000});
                return;
            }
            if (!this.filteredEmployees.some(employee => Number(employee.employee_id) === employeeId)) this.clearAllPlanFilters();
            await this.$nextTick();
            if (this._workforcePlanDestroyed || this._collaborationJump !== request) return;
            const row = this.filteredEmployees.findIndex(employee => Number(employee.employee_id) === employeeId);
            const column = this.weeks.findIndex(intersectsWeek);
            const viewport = this.$refs.viewport;
            if (!viewport || row < 0 || column < 0) return;
            const rowHeight = this.isMobileViewport ? 38 : 30;
            viewport.scrollTop = Math.max(0, this.employeeRowsContentOffset + row * rowHeight - viewport.clientHeight / 2);
            const width = parseInt(this.weekWidth, 10) || 30;
            this.setPlanScrollLeft(Math.max(0, column * width - (viewport.clientWidth - this.pinnedColumnsWidth) / 2 + width / 2));
            this.updateVirtualEmployeeRange();
            await this.$nextTick();
            if (this._workforcePlanDestroyed || this._collaborationJump !== request) return;
            revealCollaborationCell(this.$el.querySelector(`#workforce-plan-cell-${employeeId}-${this.weeks[column].key}`), person.color, false);
        },
        startPresenceHeartbeat() {
            if (this.presenceHeartbeatTimer) return;
            this.presenceHeartbeatTimer = window.setInterval(
                this.emitPlanPresence,
                7000
            );
        },
        stopPresenceHeartbeat() {
            if (!this.presenceHeartbeatTimer) return;
            window.clearInterval(this.presenceHeartbeatTimer);
            this.presenceHeartbeatTimer = null;
        },
        onPlanPresence(event) {
            if (!event || !event.presence_id) return;
            const ranges = Array.isArray(event.ranges)
                ? event.ranges
                : event.employee_id === null
                    ? []
                    : [{
                        employee_id: event.employee_id,
                        starts_on: event.starts_on,
                        ends_on: event.ends_on
                    }];
            if (!ranges.length) {
                this.$delete(this.remoteEditors, event.presence_id);
                return;
            }
            this.$set(this.remoteEditors, event.presence_id, {
                ranges: ranges.map(range => ({
                    employee_id: Number(range.employee_id),
                    starts_on: range.starts_on,
                    ends_on: range.ends_on
                })),
                actor: event.actor || {},
                color: this.presenceColor(event),
                seen_at: Date.now()
            });
        },
        cleanupRemoteEditors() {
            const deadline = Date.now() - 20000;
            Object.keys(this.remoteEditors).forEach(id => {
                if (Number(this.remoteEditors[id].seen_at) < deadline) {
                    this.$delete(this.remoteEditors, id);
                }
            });
        },
        remoteEditorFor(employee, week) {
            if (!employee || !week) return null;
            return this.remoteEditorCellIndex.get(
                selectionUtils.cellKey(employee.employee_id, week.key)
            ) || null;
        },
        remoteEditorInitials(employee, week) {
            const editor = this.remoteEditorFor(employee, week);
            const name = editor && editor.actor && editor.actor.display_name || 'К';
            return name.split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
        },
        presenceStyle(employee, week) {
            const editor = this.remoteEditorFor(employee, week);
            return editor ? {'--plan-presence-color': editor.color} : {};
        },
        presenceColor(event) {
            const palette = ['#7c3aed', '#db2777', '#059669', '#d97706', '#2563eb', '#dc2626'];
            const source = String(event.actor && event.actor.user_id || event.presence_id || '0');
            let hash = 0;
            for (let index = 0; index < source.length; index += 1) {
                hash = ((hash * 31) + source.charCodeAt(index)) >>> 0;
            }
            return palette[hash % palette.length];
        },
        errorText(error, fallback) {
            const data = error && error.response && error.response.data;
            return data && (data.msg || data.message) || fallback;
        }
    }
};
</script>

<style scoped>
.workforce-plan-page {
    min-height: 100%;
    padding: 16px;
    background: #f4f7fa;
    color: #283a4d;
}

.workforce-plan-header,
.workforce-plan-title-row,
.workforce-plan-toolbar,
.workforce-plan-legend {
    display: flex;
    align-items: center;
}

.workforce-plan-header {
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
}

.workforce-plan-header__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
}

.workforce-create-employee-slot {
    display: inline-flex;
    width: 11.5rem;
    min-height: 31px;
    flex: 0 0 11.5rem;
}

.workforce-plan-create-planned-action {
    width: 100%;
    margin: 7px 0 2px;
    padding: 6px 9px;
    border: 1px dashed #6f9ccc;
    border-radius: 6px;
    color: #245f99;
    background: #eef6ff;
    font-size: 0.78rem;
    font-weight: 700;
    text-align: left;
}
.workforce-plan-create-planned-action:hover {
    border-style: solid;
    background: #dfefff;
}

.workforce-planned-object-manager-toolbar {
    display: flex;
    margin-bottom: 8px;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}
.workforce-planned-object-manager-toolbar small {
    color: #6b7d90;
}

.workforce-planned-object-row {
    display: flex;
    min-height: 54px;
    padding: 9px 4px;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-bottom: 1px solid #e1e7ec;
}
.workforce-planned-object-row > div:first-child {
    display: flex;
    min-width: 0;
    flex-direction: column;
}
.workforce-planned-object-row small { color: #6f7f8f; }
.workforce-planned-object-week {
    padding: 10px 12px;
    border-radius: 6px;
    background: #f4f9fd;
}
.workforce-planned-object-footer { width: 100%; }

.workforce-plan-title-row {
    flex-wrap: wrap;
    gap: 8px 14px;
}

.workforce-plan-header h1 {
    margin: 0;
    font-size: 1.45rem;
    font-weight: 700;
}

.workforce-plan-header p {
    margin: 3px 0 0;
    color: #748291;
    font-size: 0.84rem;
}

.workforce-plan-sync {
    color: #718092;
    font-size: 0.74rem;
    font-weight: 600;
}

.workforce-plan-sync i { color: #f59e0b; font-size: 0.55rem; }
.workforce-plan-sync--connected i { color: #22a06b; }
.workforce-plan-sync--offline i { color: #dc3545; }

.workforce-plan-toolbar {
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
    padding: 9px;
    border: 1px solid #dce4eb;
    border-radius: 8px;
    background: #fff;
}

.workforce-plan-period-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 14px;
    margin-bottom: 8px;
}
.workforce-plan-period-range { display: flex; align-items: center; gap: 8px; min-width: 0; }
.workforce-plan-period-range > span { font-size: 0.75rem; font-weight: 600; color: #3f566b; white-space: nowrap; }
.workforce-plan-period-range .btn { white-space: nowrap; }

.workforce-plan-mobile-tools,
.workforce-plan-mobile-week-nav,
.workforce-plan-context-fab,
.workforce-plan-context-hint { display: none; }

.workforce-plan-search { width: 250px; }
.workforce-plan-year { width: 92px; }
.workforce-plan-citizenship { width: 185px; }
.workforce-plan-assignment-filter {
    display: flex;
    align-items: center;
    gap: 5px;
    padding-left: 8px;
    border-left: 1px solid #dce4eb;
}
.workforce-plan-assignment-filter > span {
    color: #6e7e8e;
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
}
.workforce-plan-assignment-select { width: 230px; }
.workforce-plan-assignment-scope-header { position: sticky; top: -5px; z-index: 1; padding: 8px; background: #fff; border-bottom: 1px solid #e0e7ee; }
.workforce-plan-assignment-scope { display: flex; padding: 3px; gap: 3px; border: 1px solid #dce5ed; border-radius: 7px; background: #f3f7fa; }
.workforce-plan-assignment-scope button { flex: 1 1 auto; border: 0; border-radius: 5px; padding: 6px 7px; background: transparent; color: #52687b; font-size: 0.73rem; white-space: nowrap; }
.workforce-plan-assignment-scope button[aria-pressed="true"] { background: #fff; color: #156aa5; box-shadow: 0 1px 3px rgba(40, 65, 90, 0.14); font-weight: 700; }
.workforce-plan-assignment-scope button:hover { color: #075d9b; background: #e9f3fb; }
.workforce-plan-assignment-select.vs--open { z-index: 1020; }
.workforce-plan-assignment-select >>> .vs__dropdown-menu { left: auto; right: 0; width: max(100%, 290px); min-width: 290px; max-width: calc(100vw - 32px); }
.workforce-plan-assignment-select >>> .vs__dropdown-option { white-space: normal; overflow-wrap: anywhere; }
.workforce-plan-assignment-select >>> .vs__dropdown-toggle {
    min-height: 31px;
    border-color: #ced4da;
    background: #fff;
}
.workforce-plan-assignment-select >>> .vs__selected,
.workforce-plan-assignment-select >>> .vs__search,
.workforce-plan-assignment-select >>> .vs__dropdown-menu {
    font-size: 0.78rem;
}
.workforce-plan-count { margin-left: auto; color: #718092; font-size: 0.78rem; }

.workforce-plan-active-filters {
    position: sticky;
    z-index: 1015;
    top: var(--shell-header-height, 56px);
    display: flex;
    width: 100%;
    height: 42px;
    margin-bottom: 10px;
    padding: 5px 8px;
    align-items: center;
    gap: 6px;
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid #efc860;
    border-radius: 8px;
    background: rgba(255, 249, 226, 0.98);
    box-shadow: 0 5px 18px rgba(87, 68, 14, 0.16);
    white-space: nowrap;
}

.workforce-plan-active-filters__label {
    color: #725400;
    font-size: 0.75rem;
    font-weight: 800;
}

.workforce-plan-active-filter,
.workforce-plan-active-filters__reset {
    display: inline-flex;
    height: 28px;
    padding: 0 8px;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    border: 1px solid transparent;
    border-radius: 14px;
    background: #fff;
    color: #fff;
    font: inherit;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
}

.workforce-plan-active-filter:hover,
.workforce-plan-active-filters__reset:hover { filter: brightness(0.96); }
.workforce-plan-active-filter--primary { border-color: #007bff; background: #007bff; }
.workforce-plan-active-filter--info { border-color: #17a2b8; background: #17a2b8; }
.workforce-plan-active-filter--secondary { border-color: #6c757d; background: #6c757d; }
.workforce-plan-active-filter--warning { border-color: #ffc107; background: #ffc107; color: #212529; }
.workforce-plan-active-filter .fa-times { opacity: 0.72; }
.workforce-plan-active-filters__reset {
    margin-left: auto;
    border-color: #c6a13d;
    background: transparent;
    color: #725400;
}

.workforce-plan-page--filtered .workforce-plan-horizontal-scroll {
    top: calc(var(--shell-header-height, 56px) + 48px);
}

.workforce-plan-quick-editor {
    position: fixed;
    z-index: 1080;
    width: min(390px, calc(100vw - 16px));
    max-height: calc(100vh - 16px);
    padding: 10px;
    overflow: auto;
    overscroll-behavior: contain;
    border: 2px solid #1976bd;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 12px 35px rgba(24, 57, 87, 0.28);
    color: #30485d;
    font-size: 0.76rem;
}

.workforce-plan-quick-editor__header {
    position: sticky;
    z-index: 3;
    top: -10px;
    display: flex;
    margin: -10px -2px 8px;
    padding: 10px 2px 8px;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    border-bottom: 1px solid #d9e3ec;
    background: #fff;
    cursor: grab;
    user-select: none;
}

.workforce-plan-quick-editor--dragging,
.workforce-plan-quick-editor--dragging .workforce-plan-quick-editor__header {
    cursor: grabbing;
}

.workforce-plan-quick-editor__header > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.workforce-plan-quick-editor__drag-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.workforce-plan-quick-editor__drag-label > i {
    color: #7f93a6;
    font-size: 0.72rem;
}

.workforce-plan-quick-editor__header strong { font-size: 0.84rem; }
.workforce-plan-quick-editor__selection-title {
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
}
.workforce-plan-quick-editor__header small { color: #718092; font-size: 0.66rem; }
.workforce-plan-quick-editor__header button {
    display: inline-flex;
    width: 26px;
    height: 26px;
    padding: 0;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #667789;
    cursor: pointer;
}
.workforce-plan-quick-editor__header button:hover { background: #edf2f6; color: #243b50; }

.workforce-plan-row-copy {
    display: flex;
    margin-bottom: 8px;
    padding: 8px;
    flex-direction: column;
    gap: 7px;
    border: 1px solid #b9d6ee;
    border-radius: 6px;
    background: #f1f7fc;
}
.workforce-plan-row-copy > .btn {
    width: 100%;
    font-weight: 800;
}
.workforce-plan-row-copy__hint {
    position: relative;
    padding: 7px 26px 7px 8px;
    border-radius: 5px;
    background: #e2effa;
    color: #315a7d;
    font-size: 0.68rem;
    line-height: 1.35;
}
.workforce-plan-row-copy__hint kbd {
    padding: 1px 4px;
    border: 1px solid #9eb7cc;
    border-radius: 3px;
    background: #fff;
    box-shadow: 0 1px 0 #9eb7cc;
    color: #284d6c;
    font: inherit;
    font-weight: 800;
}
.workforce-plan-row-copy__hint > button {
    position: absolute;
    top: 1px;
    right: 3px;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #54738e;
    font: inherit;
    font-size: 1rem;
}

.workforce-plan-quick-editor__current {
    margin-bottom: 8px;
    padding: 6px 8px;
    border-radius: 5px;
    background: #eef3f7;
    color: #52677a;
}

.workforce-plan-arrival {
    margin-bottom: 8px;
    padding: 8px;
    border: 1px solid #b9dfca;
    border-radius: 6px;
    background: #f2faf5;
}

.workforce-plan-arrival__confirmed,
.workforce-plan-arrival__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.workforce-plan-arrival__summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: #246a43;
    text-align: left;
    font: inherit;
    font-weight: 600;
}

#workforce-arrival-details > .workforce-plan-arrival__confirmed {
    margin-top: 6px;
}

.workforce-plan-arrival__confirmed > span {
    color: #246a43;
    font-weight: 700;
}

.workforce-plan-arrival__confirmed {
    flex-wrap: wrap;
}

.workforce-plan-arrival__author {
    font-weight: 400;
}

.workforce-plan-arrival__confirmed > div {
    display: flex;
    flex: 0 0 auto;
    gap: 4px;
}

.workforce-plan-arrival__open {
    display: flex;
    width: 100%;
    min-height: 32px;
    padding: 5px 8px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 1px solid #43a46d;
    border-radius: 5px;
    background: #e4f5eb;
    color: #1f6a3f;
    font: inherit;
    font-weight: 800;
    cursor: pointer;
}

.workforce-plan-arrival__open:hover {
    border-color: #268652;
    background: #d7efdf;
}

.workforce-plan-arrival__title > button {
    display: inline-flex;
    width: 24px;
    height: 24px;
    padding: 0;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #65798b;
    cursor: pointer;
}

.workforce-plan-arrival > small {
    display: block;
    margin-top: 2px;
    color: #6c7e8d;
    font-size: 0.65rem;
}

.workforce-plan-arrival__days {
    display: grid;
    margin-top: 7px;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 3px;
}

.workforce-plan-arrival__days button {
    min-width: 0;
    padding: 4px 1px;
    border: 1px solid #b8c9d6;
    border-radius: 4px;
    background: #fff;
    color: #40566a;
    font: inherit;
    cursor: pointer;
}

.workforce-plan-arrival__days button span,
.workforce-plan-arrival__days button strong {
    display: block;
    font-size: 0.58rem;
    line-height: 1.2;
}

.workforce-plan-arrival__days button.is-active {
    border-color: #238250;
    background: #d9f0e2;
    color: #165d38;
    box-shadow: inset 0 0 0 1px #238250;
}

.workforce-plan-arrival__days button:disabled {
    opacity: 0.42;
    cursor: not-allowed;
}

.workforce-plan-arrival__other-toggle {
    margin-top: 6px;
    padding: 3px 0;
    border: 0;
    background: transparent;
    color: #236ca5;
    font: inherit;
    font-size: 0.67rem;
    font-weight: 700;
    cursor: pointer;
}

.workforce-plan-arrival__other-date {
    width: 100%;
    min-height: 31px;
    margin-top: 4px;
    padding: 4px 7px;
    border: 1px solid #b8c6d2;
    border-radius: 4px;
    color: #2f4458;
    font: inherit;
}

.workforce-plan-arrival__warning,
.workforce-plan-arrival__error,
.workforce-plan-arrival__moves {
    margin-top: 7px;
    padding: 7px;
    border-radius: 5px;
}

.workforce-plan-arrival__warning {
    border: 1px solid #efd184;
    background: #fff4d6;
    color: #704f00;
}

.workforce-plan-arrival__warning p {
    margin: 4px 0 0;
}

.workforce-plan-arrival__moves {
    display: grid;
    gap: 4px;
    border: 1px solid #c8d7e3;
    background: #f5f8fa;
}

.workforce-plan-arrival__moves label {
    display: flex;
    margin: 0;
    align-items: flex-start;
    gap: 5px;
    font-weight: 600;
}

.workforce-plan-arrival__error {
    background: #fde4e2;
    color: #9f2922;
}

.workforce-plan-arrival__submit {
    width: 100%;
    margin-top: 7px;
}

.workforce-plan-command-search,
.workforce-plan-quick-editor__details label {
    display: flex;
    margin: 0;
    flex-direction: column;
    gap: 3px;
    color: #586d80;
    font-size: 0.66rem;
    font-weight: 800;
}

.workforce-plan-command-search input,
.workforce-plan-quick-editor__details input,
.workforce-plan-quick-editor__details select,
.workforce-plan-quick-editor__details textarea {
    width: 100%;
    min-height: 31px;
    padding: 4px 7px;
    border: 1px solid #b8c6d2;
    border-radius: 4px;
    background: #fff;
    color: #2f4458;
    font: inherit;
    font-size: 0.76rem;
}

.workforce-plan-command-search input:focus,
.workforce-plan-quick-editor__details input:focus,
.workforce-plan-quick-editor__details select:focus,
.workforce-plan-quick-editor__details textarea:focus {
    outline: 2px solid rgba(25, 118, 189, 0.22);
    border-color: #1976bd;
}

.workforce-plan-command-results {
    display: grid;
    max-height: 190px;
    margin-top: 4px;
    overflow-y: auto;
    border: 1px solid #b8c6d2;
    border-radius: 5px;
    background: #fff;
}

.workforce-plan-command-results button {
    display: flex;
    min-height: 34px;
    padding: 5px 8px;
    align-items: flex-start;
    flex-direction: column;
    border: 0;
    border-bottom: 1px solid #edf1f4;
    background: #fff;
    color: #30485d;
    text-align: left;
    cursor: pointer;
}
.workforce-plan-command-results button:hover,
.workforce-plan-command-results button.is-active { background: #e8f3fc; }
.workforce-plan-command-results button small { color: #718092; font-size: 0.62rem; }
.workforce-plan-command-results > small { padding: 8px; color: #718092; }

.workforce-plan-quick-states {
    display: grid;
    margin-top: 8px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 5px;
}
.workforce-plan-quick-states button {
    min-height: 31px;
    padding: 4px 5px;
    overflow: hidden;
    border: 1px solid #aebdca;
    border-radius: 4px;
    background: #f8fafc;
    color: #40576b;
    font: inherit;
    font-size: 0.68rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
}
.workforce-plan-quick-states button:hover,
.workforce-plan-quick-states button.is-active {
    border-color: #1976bd;
    background: #deeffd;
    color: #165f98;
}

.workforce-plan-selected-action,
.workforce-plan-selection-warning {
    margin-top: 8px;
    padding: 6px 8px;
    border-radius: 5px;
}
.workforce-plan-selected-action { background: #e2f3e8; color: #1d633a; }
.workforce-plan-selection-warning { background: #fff0c7; color: #765400; }

.workforce-plan-quick-editor__details-toggle {
    width: 100%;
    margin-top: 8px;
    padding: 5px 0;
    border: 0;
    background: transparent;
    color: #4e657a;
    font: inherit;
    font-size: 0.69rem;
    font-weight: 800;
    text-align: left;
    cursor: pointer;
}
.workforce-plan-quick-editor__details {
    display: grid;
    padding: 8px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 7px;
    border: 1px solid #d9e3ec;
    border-radius: 5px;
    background: #f8fafc;
}
.workforce-plan-quick-editor__details textarea,
.workforce-plan-quick-editor__details > input,
.workforce-plan-quick-editor__details > label:nth-last-child(-n + 2) {
    grid-column: 1 / -1;
}
.workforce-plan-exact-period-disabled {
    grid-column: 1 / -1;
    color: #718092;
}

.workforce-plan-quick-editor__actions {
    position: sticky;
    z-index: 2;
    bottom: -10px;
    display: flex;
    margin-top: 10px;
    padding: 8px 0 10px;
    align-items: center;
    gap: 6px;
    border-top: 1px solid #d9e3ec;
    background: #fff;
}
.workforce-plan-quick-editor__actions .btn:first-child { flex: 1 1 auto; }
.workforce-plan-quick-editor__hint {
    display: block;
    margin-top: 6px;
    color: #7b8996;
    font-size: 0.61rem;
}

.workforce-plan-horizontal-scroll {
    position: sticky;
    z-index: 10;
    top: var(--shell-header-height, 56px);
    height: 22px;
    margin-bottom: 5px;
    overflow-x: auto;
    overflow-y: hidden;
    border: 2px solid #8eb9df;
    border-radius: 11px;
    background: #dbeaf7;
    box-shadow: 0 2px 7px rgba(25, 100, 165, 0.18);
    scrollbar-color: #1476c4 #dbeaf7;
    scrollbar-width: auto;
}

.workforce-plan-horizontal-scroll:hover {
    border-color: #4b96d3;
    box-shadow: 0 3px 10px rgba(25, 100, 165, 0.26);
}

.workforce-plan-horizontal-scroll > div { height: 1px; }

.workforce-plan-horizontal-scroll::-webkit-scrollbar:horizontal,
.workforce-plan-viewport::-webkit-scrollbar:horizontal { height: 18px; }

.workforce-plan-horizontal-scroll::-webkit-scrollbar-track:horizontal,
.workforce-plan-viewport::-webkit-scrollbar-track:horizontal {
    border-radius: 9px;
    background: #dbeaf7;
    box-shadow: inset 0 1px 3px rgba(39, 79, 113, 0.2);
}

.workforce-plan-horizontal-scroll::-webkit-scrollbar-thumb:horizontal,
.workforce-plan-viewport::-webkit-scrollbar-thumb:horizontal {
    min-width: 72px;
    border: 3px solid #dbeaf7;
    border-radius: 9px;
    background-color: #1476c4;
    background-image: repeating-linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.1) 0,
        rgba(255, 255, 255, 0.1) 6px,
        rgba(255, 255, 255, 0.28) 6px,
        rgba(255, 255, 255, 0.28) 8px
    );
    box-shadow: inset 0 0 0 1px rgba(10, 75, 128, 0.35);
}

.workforce-plan-horizontal-scroll::-webkit-scrollbar-thumb:horizontal:hover,
.workforce-plan-viewport::-webkit-scrollbar-thumb:horizontal:hover {
    border-width: 2px;
    background-color: #0868b7;
}

.workforce-plan-viewport {
    scrollbar-color: #1476c4 #dbeaf7;
    scrollbar-width: auto;
}

.workforce-plan-loading,
.workforce-plan-empty {
    padding: 28px;
    text-align: center;
    color: #718092;
}

.workforce-plan-viewport {
    max-height: calc(100vh - 210px);
    overflow: auto;
    border: 1px solid #d7e0e8;
    border-radius: 8px;
    background: #fff;
}

.workforce-plan-grid {
    display: grid;
    grid-template-columns: var(--plan-columns);
    width: max-content;
    min-width: 100%;
}

.workforce-plan-month-head,
.workforce-plan-corner,
.workforce-plan-current-head,
.workforce-plan-week-head,
.workforce-plan-summary-label,
.workforce-plan-summary-cell,
.workforce-plan-employee,
.workforce-plan-cell {
    min-width: 0;
    height: 28px;
    border: 0;
    border-right: 1px solid #e4e9ee;
    border-bottom: 1px solid #e4e9ee;
    background: #fff;
}

.workforce-plan-month-head,
.workforce-plan-corner,
.workforce-plan-current-head,
.workforce-plan-week-head {
    position: sticky;
    z-index: 5;
}

.workforce-plan-corner {
    grid-row: 1 / span 2;
    grid-column: 1 / span 2;
    z-index: 8;
    top: 0;
    left: 0;
    display: flex;
    height: 54px;
    padding: 6px 78px 6px 10px;
    flex-direction: column;
    justify-content: center;
    border-right: 2px solid #8195a8;
    border-bottom: 2px solid #8195a8;
    background: #e6edf4;
    text-align: left;
}

.workforce-plan-summary-toggle {
    position: absolute;
    top: 50%;
    right: 6px;
    display: inline-flex;
    height: 26px;
    padding: 0 7px;
    align-items: center;
    gap: 4px;
    transform: translateY(-50%);
    border: 1px solid #8fa3b5;
    border-radius: 5px;
    background: #fff;
    color: #3f596f;
    font: inherit;
    font-size: 0.62rem;
    font-weight: 800;
    cursor: pointer;
}

.workforce-plan-summary-toggle:hover { background: #f6f9fb; }

.workforce-plan-current-head {
    grid-row: 1 / span 2;
    grid-column: 3;
    z-index: 7;
    top: 0;
    left: var(--plan-employee-width, 220px);
    display: flex;
    height: 54px;
    padding: 2px 1px;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    border-right: 4px solid #5f778d;
    border-bottom: 2px solid #8195a8;
    background: linear-gradient(180deg, #eaf5ff 0%, #cfe8fb 100%);
    color: #244d70;
    cursor: pointer;
    font-family: inherit;
    line-height: 1.05;
    white-space: nowrap;
    box-shadow: inset 0 0 0 2px rgba(54, 132, 197, 0.32), 0 2px 7px rgba(31, 92, 143, 0.18);
}

.workforce-plan-current-head strong { font-size: 0.7rem; }
.workforce-plan-current-head small { margin-bottom: 3px; font-size: 0.48rem; }
.workforce-plan-current-head__filter {
    display: flex;
    width: 50px;
    max-width: 50px;
    min-height: 19px;
    margin-top: 2px;
    padding: 3px 4px;
    align-items: center;
    justify-content: center;
    gap: 3px;
    border: 1px solid #0c64aa;
    border-radius: 10px;
    background: #1976bd;
    color: #fff;
    font-size: 0.52rem;
    font-weight: 800;
    line-height: 1;
    box-shadow: 0 2px 5px rgba(19, 91, 151, 0.34);
    transition: transform 120ms ease, background-color 120ms ease;
}
.workforce-plan-current-head__filter b {
    min-width: 0;
    overflow: hidden;
    font: inherit;
    text-overflow: ellipsis;
}
.workforce-plan-current-head__filter .fa-filter { font-size: 0.48rem; }
.workforce-plan-current-head--filtered {
    background: #fff0a8;
    color: #765000;
    box-shadow: inset 0 0 0 2px #e5a900, inset 0 -4px #f0ad00, 0 2px 8px rgba(144, 97, 0, 0.22);
}
.workforce-plan-current-head--filtered .workforce-plan-current-head__filter {
    border-color: #765000;
    background: #8f6400;
}
.workforce-plan-current-head:hover { filter: brightness(0.96); }
.workforce-plan-current-head:hover .workforce-plan-current-head__filter {
    transform: scale(1.04);
}
.workforce-plan-current-head:focus-visible {
    outline: 3px solid #1976bd;
    outline-offset: -3px;
}



.workforce-plan-current-filter-selection {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.workforce-plan-current-filter-title {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}
.workforce-plan-current-filter-close {
    flex: 0 0 28px;
    min-height: 28px;
    padding: 0;
    color: #52677a;
    font-size: 1.5rem;
    line-height: 1;
    text-decoration: none;
}





.workforce-plan-corner strong {
    color: #30485d;
    font-size: 0.78rem;
}

.workforce-plan-corner span {
    margin-top: 2px;
    color: #728394;
    font-size: 0.62rem;
    font-weight: 500;
}

.workforce-plan-month-head {
    grid-row: 1;
    top: 0;
    z-index: 6;
    display: flex;
    overflow: hidden;
    height: 23px;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid #b8c5d0;
    background: #e6edf4;
    color: #344a5e;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
}

.workforce-plan-month-head--current { background: #dbeaf7; color: #185e96; }

.workforce-plan-month-head--dated { flex-direction: column; gap: 0; letter-spacing: 0; }
.workforce-plan-month-head--dated .workforce-plan-month-name { font-size: 10px; line-height: 11px; }
.workforce-plan-month-year { font-size: 8px; line-height: 9px; font-weight: 500; opacity: 0.8; }

.workforce-plan-week-head {
    top: 23px;
    z-index: 6;
    overflow: hidden;
    height: 31px;
    padding: 2px 0 1px;
    border-bottom: 2px solid #8195a8;
    background: #f8fafc;
    color: #5c6f81;
    line-height: 1.05;
}

.workforce-plan-week-head strong,
.workforce-plan-week-head small { display: block; }
.workforce-plan-week-head strong { font-size: 0.7rem; }
.workforce-plan-week-head small {
    margin-top: 3px;
    color: #8492a0;
    font-size: 0.5rem;
    font-weight: 500;
}
.workforce-plan-month-head,
.workforce-plan-week-head--month { border-left: 2px solid #93a9bd; }
.workforce-plan-week-head--today { background: #f6a609; color: #1f2933; }
.workforce-plan-week-head--today small { color: #513400; }
.workforce-plan-week-head--filter { box-shadow: inset 0 -3px #7c3aed; }

.workforce-plan-summary-label,
.workforce-plan-summary-cell {
    height: 21px;
    background: #fff;
}

.workforce-plan-summary-label {
    position: sticky;
    grid-column: 1 / span 2;
    z-index: 4;
    left: 0;
    display: flex;
    overflow: hidden;
    padding: 3px 8px;
    align-items: center;
    color: #43566a;
    font-size: 0.68rem;
    line-height: 1;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    box-shadow: 2px 0 3px rgba(39, 58, 75, 0.06);
}

.workforce-plan-summary-label__text {
    min-width: 0;
    overflow: hidden;
    flex: 1 1 auto;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-plan-summary-help {
    flex: 0 0 20px;
    width: 20px;
    height: 24px;
    margin-left: 4px;
    padding: 0;
    border: 1px solid currentColor;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.55);
    color: inherit;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    cursor: help;
}
.workforce-plan-summary-help:hover,
.workforce-plan-summary-help:focus-visible {
    background: #fff;
    outline: 2px solid currentColor;
    outline-offset: 1px;
}

.workforce-plan-settings-button,
.workforce-plan-link-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 24px;
    width: 24px;
    height: 21px;
    margin: -3px 0 -3px 4px;
    padding: 0;
    border: 0;
    border-radius: 3px;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font-size: 0.6rem;
}
.workforce-plan-settings-button:hover,
.workforce-plan-settings-button:focus-visible,
.workforce-plan-link-button:hover,
.workforce-plan-link-button:focus-visible { background: rgba(255, 255, 255, 0.5); outline: 1px solid currentColor; }

.workforce-plan-link-button { color: #17603f; animation: workforce-plan-link-pulse 2.8s ease-in-out infinite; }
.workforce-plan-link-button:hover,
.workforce-plan-link-button:focus-visible,
.workforce-plan-link-button:disabled { animation: none; }
@keyframes workforce-plan-link-pulse {
    0%, 100% { opacity: 1; background: rgba(255, 255, 255, 0.6); }
    50% { opacity: 0.6; background: transparent; }
}
@media (prefers-reduced-motion: reduce) {
    .workforce-plan-link-button { animation: none; }
}

.workforce-plan-summary-cell {
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    color: #526273;
    font-size: 0.58rem;
    white-space: nowrap;
}

.workforce-plan-summary-cell strong { font-weight: 800; }
.workforce-plan-summary-count {
    display: inline-flex;
    align-items: baseline;
    justify-content: center;
    gap: 1px;
    line-height: 1;
}
.workforce-plan-summary-cell .workforce-plan-summary-count > span {
    margin: 0;
    font-size: inherit;
}
.workforce-plan-summary-count__divider {
    color: currentColor;
    opacity: 0.55;
}
.workforce-plan-summary-count__itr {
    font-weight: 900;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 1px;
}
.workforce-plan-summary--stacked {
    height: 36px;
}
.workforce-plan-summary--stacked .workforce-plan-summary-count {
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-width: 2.2em;
    font-variant-numeric: tabular-nums;
}
.workforce-plan-summary--stacked .workforce-plan-summary-count > .workforce-plan-summary-count__divider {
    width: 100%;
    height: 0;
    border-top: 1px solid currentColor;
    font-size: 0;
}
.workforce-plan-summary--stacked .workforce-plan-summary-count__itr {
    text-decoration: none;
}
.workforce-plan-summary-cell--month { border-left: 2px solid #b6c3cf; }
.workforce-plan-summary-cell--today { box-shadow: inset 2px 0 #f6a609, inset -2px 0 #f6a609; }

.workforce-plan-current-cell {
    position: sticky;
    z-index: 4;
    left: var(--plan-employee-width, 220px);
    border-right: 4px solid #5f778d;
    box-shadow: 2px 0 3px rgba(39, 58, 75, 0.08);
}

.workforce-plan-summary--object-total {
    border-top: 2px solid #8195a8;
    background: var(--plan-color-e4f0dc, #e4f0dc);
    color: var(--plan-text-e4f0dc, #355f26);
    font-weight: 800;
}

.workforce-plan-summary--requires-distribution { background: var(--plan-color-ffffff, #ffffff); color: var(--plan-text-ffffff, #9f2922); }
.workforce-plan-summary--rest { background: var(--plan-color-e2e2e2, #e2e2e2); color: var(--plan-text-e2e2e2, #4b4b4b); }
.workforce-plan-summary--loupe { background: var(--plan-color-fff0c7, #fff0c7); color: var(--plan-text-fff0c7, #86610b); }
.workforce-plan-summary--missing { background: var(--plan-color-f3f5f7, #f3f5f7); color: var(--plan-text-f3f5f7, #6e7b88); }
.workforce-plan-summary--planning-total,
.workforce-plan-summary--total {
    border-top: 2px solid #8195a8;
    background: var(--plan-color-dce7f0, #dce7f0);
    color: var(--plan-text-dce7f0, #253c50);
    font-weight: 800;
}

.workforce-plan-summary-label--section,
.workforce-plan-summary-cell--section {
    height: 28px;
    border-top: 3px solid #8195a8;
    border-bottom: 2px solid #8195a8;
    background: #eef3f7;
    color: #30485d;
    font-size: 0.7rem;
    font-weight: 800;
}

.workforce-plan-employee {
    position: sticky;
    z-index: 4;
    left: 0;
    display: flex;
    overflow: hidden;
    height: 30px;
    padding: 2px 8px;
    flex-direction: column;
    gap: 1px;
    justify-content: center;
    line-height: 1;
    text-align: left;
    box-shadow: 2px 0 3px rgba(39, 58, 75, 0.06);
}

.workforce-plan-employee strong,
.workforce-plan-employee span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-plan-employee__name {
    display: flex !important;
    min-height: 13px;
    align-items: baseline;
    flex: 0 0 13px;
    gap: 5px;
    line-height: 13px;
}
.workforce-plan-employee__name strong {
    min-width: 0;
    overflow: hidden;
    line-height: 13px;
    text-overflow: ellipsis;
}
.workforce-plan-employee__name em {
    flex: 0 0 auto;
    padding: 1px 4px;
    border-radius: 3px;
    background: #e8edf2;
    color: #52677a;
    font-size: 0.5rem;
    font-style: normal;
    font-weight: 800;
}

.workforce-plan-employee__name .workforce-plan-employee__match {
    background: #fff0c7;
    color: #7a5400;
    max-width: 96px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.workforce-plan-employee strong { font-size: 0.7rem; }
.workforce-plan-employee span { color: #677b8e; font-size: 0.57rem; font-weight: 700; }
.workforce-plan-employee .workforce-plan-employee__skills {
    min-height: 10px;
    flex: 0 0 10px;
    color: #526a80;
    line-height: 10px;
}

.workforce-plan-cell {
    position: relative;
    display: flex;
    overflow: hidden;
    height: 30px;
    padding: 0 1px;
    align-items: center;
    color: #32475b;
    font-size: 0.61rem;
    font-weight: 700;
    justify-content: center;
    line-height: 1;
    text-overflow: ellipsis;
    user-select: none;
    white-space: nowrap;
}

.workforce-plan-cell > span {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-plan-cell.workforce-plan-current-cell {
    position: sticky;
    left: var(--plan-employee-width, 220px);
}

.workforce-plan-cell:hover { cursor: crosshair; filter: brightness(0.96); }
.workforce-plan-cell--missing { background: #fff; }
.workforce-plan-cell--object { background: var(--plan-color-dff1e5, #dff1e5); color: var(--plan-text-dff1e5, #21653d); }
.workforce-plan-cell--object_group { background: var(--plan-color-e8f3ce, #e8f3ce); color: var(--plan-text-e8f3ce, #557814); }
.workforce-plan-cell--planned_object {
    border: 1px dashed #21855a;
    background: var(--plan-color-e6f5eb, #e6f5eb);
    color: var(--plan-text-e6f5eb, #17603f);
}
.workforce-plan-summary--planned-object {
    border-top-style: dashed;
    border-bottom-style: dashed;
}
.workforce-plan-cell--requires_distribution { background: var(--plan-color-ffffff, #ffffff); color: var(--plan-text-ffffff, #9f2922); }
.workforce-plan-cell--rest { background: var(--plan-color-e2e2e2, #e2e2e2); color: var(--plan-text-e2e2e2, #4b4b4b); }
.workforce-plan-cell--loupe { background: var(--plan-color-fff0c7, #fff0c7); color: var(--plan-text-fff0c7, #86610b); }
.workforce-plan-cell--mixed::after {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 0;
    height: 0;
    border-bottom: 8px solid #7651a8;
    border-left: 8px solid transparent;
    content: '';
}
.workforce-plan-cell--month {
    border-left: 2px solid #b6c3cf;
    padding-left: 0;
}
.workforce-plan-cell--today {
    z-index: 1;
    outline: 2px solid #f6a609;
    outline-offset: -2px;
}
.workforce-plan-cell--selected { box-shadow: inset 0 0 0 2px #1976bd; }
.workforce-plan-cell--remote { box-shadow: inset 0 0 0 3px var(--plan-presence-color); }
.workforce-plan-cell--selected.workforce-plan-cell--remote {
    box-shadow: inset 0 0 0 3px #1976bd, inset 0 -5px 0 var(--plan-presence-color);
}
.workforce-plan-cell small {
    position: absolute;
    right: 1px;
    top: 1px;
    padding: 0 2px;
    border-radius: 3px;
    background: var(--plan-presence-color);
    color: #fff;
    font-size: 0.48rem;
}

.workforce-plan-viewport--fit >>> .workforce-plan-cell span { display: none; }
.workforce-plan-viewport--fit .workforce-plan-summary-cell { font-size: 0.5rem; }
.workforce-plan-viewport--fit .workforce-plan-week-head small { display: none; }
.workforce-plan-viewport--fit .workforce-plan-week-head strong {
    display: block;
    font-size: 0.5rem;
}
.workforce-plan-viewport--fit .workforce-plan-month-head {
    font-size: 0.52rem;
    letter-spacing: 0;
}
.workforce-plan-viewport--fit .workforce-plan-month-head--dated .workforce-plan-month-name { font-size: 8px; line-height: 10px; }

.workforce-plan-legend {
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 8px;
    color: #667789;
    font-size: 0.7rem;
}

.workforce-plan-legend i {
    display: inline-block;
    width: 12px;
    height: 12px;
    margin-right: 3px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 2px;
    vertical-align: -2px;
}
.legend-object { background: var(--plan-color-dff1e5, #dff1e5); }
.legend-planned-object { border-style: dashed !important; border-color: #21855a !important; background: var(--plan-color-e6f5eb, #e6f5eb); }
.legend-distribution { background: var(--plan-color-ffffff, #ffffff); }
.legend-rest { background: var(--plan-color-e2e2e2, #e2e2e2); }
.legend-loupe { background: var(--plan-color-fff0c7, #fff0c7); }
.legend-missing { background: #fff; }

@media (max-width: 767.98px),
    (hover: none) and (pointer: coarse) and (max-height: 767.98px) {
    .workforce-plan-page {
        padding: 6px;
        overflow-x: hidden;
    }
    .workforce-plan-header {
        margin-bottom: 6px;
        align-items: stretch;
        flex-direction: column;
        gap: 6px;
    }
    .workforce-plan-header h1 { font-size: 1.05rem; }
    .workforce-plan-header p { display: none; }
    .workforce-plan-title-row { gap: 5px 9px; }
    .workforce-plan-sync { font-size: 0.65rem; }
    .workforce-plan-header__actions {
        margin: 0 -6px;
        padding: 0 6px 3px;
        overflow-x: auto;
        flex-wrap: nowrap;
        justify-content: flex-start;
        gap: 5px;
        scrollbar-width: none;
    }
    .workforce-plan-header__actions::-webkit-scrollbar { display: none; }
    .workforce-plan-header__actions > *,
    .workforce-plan-header__actions .btn { flex: 0 0 auto; white-space: nowrap; }
    .workforce-create-employee-slot {
        width: auto;
        min-height: 31px;
        flex-basis: auto;
    }
    .workforce-plan-toolbar {
        margin-bottom: 6px;
        padding: 6px;
        align-items: stretch;
        gap: 6px;
    }
    .workforce-plan-period-toolbar { gap: 6px; margin-bottom: 6px; }
    .workforce-plan-period-toolbar > .btn-group { flex: 1 1 auto; }
    .workforce-plan-period-range { width: 100%; justify-content: space-between; gap: 4px; }
    .workforce-plan-period-range > span { font-size: 0.68rem; }
    .workforce-plan-period-range .btn { font-size: 0.68rem; padding: 5px; min-height: 32px; }
    .workforce-plan-mobile-tools {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: space-between;
        gap: 6px;
    }
    .workforce-plan-mobile-tools > span {
        margin-right: auto;
        color: #718092;
        font-size: 0.7rem;
        white-space: nowrap;
    }
    .workforce-plan-mobile-tool {
        display: inline-flex;
        min-height: 34px;
        padding: 5px 9px;
        align-items: center;
        gap: 5px;
        border: 1px solid #9eb2c4;
        border-radius: 6px;
        background: #fff;
        color: #3f566b;
        font: inherit;
        font-size: 0.72rem;
        font-weight: 800;
    }
    .workforce-plan-mobile-tool.is-active {
        border-color: #1476c4;
        background: #e5f2fc;
        color: #1264a5;
    }
    .workforce-plan-mobile-tool b {
        display: inline-flex;
        min-width: 17px;
        height: 17px;
        padding: 0 4px;
        align-items: center;
        justify-content: center;
        border-radius: 9px;
        background: #dc3545;
        color: #fff;
        font-size: 0.58rem;
    }
    .workforce-plan-search { width: 100%; }
    .workforce-plan-citizenship,
    .workforce-plan-multi-filter,
    .workforce-plan-assignment-filter,
    .workforce-plan-zoom,
    .workforce-plan-name-switcher { display: none; }
    .workforce-plan-page--mobile-filters-open .workforce-plan-multi-filter,
    .workforce-plan-page--mobile-filters-open .workforce-plan-citizenship {
        display: block;
    }
    .workforce-plan-page--mobile-filters-open .workforce-plan-assignment-filter,
    .workforce-plan-page--mobile-filters-open .workforce-plan-zoom { display: flex; }
    .workforce-plan-page--mobile-filters-open .workforce-plan-name-switcher {
        display: inline-block;
    }
    .workforce-plan-page--mobile-filters-open .workforce-plan-multi-filter {
        flex: 1 1 100%;
        min-width: 0;
    }
    .workforce-plan-page--mobile-filters-open .workforce-plan-citizenship {
        width: calc(100% - 110px);
    }
    .workforce-plan-assignment-filter {
        width: 100%;
        padding-left: 0;
        border-left: 0;
        flex-wrap: wrap;
    }
    .workforce-plan-assignment-select { flex: 2 1 210px; width: auto; }
    .workforce-plan-zoom {
        width: 100%;
        flex-direction: column;
        align-items: stretch;
    }
    .workforce-plan-today,
    .workforce-plan-count { display: none; }
    .workforce-plan-active-filters {
        top: var(--shell-header-height, 56px);
        height: 36px;
        margin-bottom: 6px;
        padding: 4px 6px;
    }
    .workforce-plan-active-filters__label { display: none; }
    .workforce-plan-page--filtered .workforce-plan-mobile-week-nav {
        top: calc(var(--shell-header-height, 56px) + 42px);
    }
    .workforce-plan-horizontal-scroll { display: none; }
    .workforce-plan-mobile-week-nav {
        display: grid;
        position: sticky;
        z-index: 10;
        top: var(--shell-header-height, 56px);
        margin-bottom: 5px;
        grid-template-columns: 1fr auto 1fr;
        overflow: hidden;
        border: 1px solid #8eb9df;
        border-radius: 8px;
        background: rgba(235, 245, 253, 0.97);
        box-shadow: 0 2px 7px rgba(25, 100, 165, 0.16);
    }
    .workforce-plan-mobile-week-nav button {
        display: flex;
        min-height: 36px;
        padding: 4px 6px;
        align-items: center;
        justify-content: center;
        gap: 7px;
        border: 0;
        background: transparent;
        color: #23679e;
        font: inherit;
        font-size: 0.69rem;
        font-weight: 800;
    }
    .workforce-plan-mobile-week-nav button:last-child { justify-self: stretch; }
    .workforce-plan-mobile-week-nav .is-current {
        min-width: 76px;
        flex-direction: column;
        gap: 0;
        border-right: 1px solid #a8c7df;
        border-left: 1px solid #a8c7df;
        background: #fff;
        color: #174f79;
        line-height: 1;
    }
    .workforce-plan-mobile-week-nav small { font-size: 0.52rem; }
    .workforce-plan-mobile-week-nav strong { font-size: 0.72rem; }
    .workforce-plan-context-fab {
        position: fixed;
        z-index: 25;
        right: max(12px, env(safe-area-inset-right));
        bottom: calc(14px + env(safe-area-inset-bottom));
        display: flex;
        width: 52px;
        height: 52px;
        padding: 0;
        align-items: center;
        justify-content: center;
        border: 2px solid #d07a00;
        border-radius: 50%;
        background: linear-gradient(180deg, #ffc65a 0%, #f5a623 100%);
        color: #3d2600;
        box-shadow: 0 7px 20px rgba(62, 46, 23, 0.32);
        animation: workforce-plan-context-pulse 900ms ease-in-out 3;
        font-size: 1.18rem;
        touch-action: manipulation;
        user-select: none;
    }
    .workforce-plan-context-fab.is-active {
        border-color: #59369a;
        background: #7048b8;
        color: #fff;
        box-shadow: 0 7px 20px rgba(62, 34, 113, 0.38);
        animation: none;
    }
    .workforce-plan-context-fab.is-previewing {
        border-color: #fff;
        background: #422c69;
        color: #fff;
        box-shadow: 0 0 0 5px rgba(112, 72, 184, 0.24),
            0 7px 20px rgba(49, 25, 94, 0.42);
    }
    .workforce-plan-context-fab__pin {
        position: absolute;
        top: 5px;
        right: 5px;
        font-size: 0.55rem;
        transform: rotate(22deg);
    }
    .workforce-plan-context-hint {
        position: fixed;
        z-index: 24;
        right: max(12px, env(safe-area-inset-right));
        bottom: calc(76px + env(safe-area-inset-bottom));
        display: flex;
        width: min(270px, calc(100vw - 24px));
        padding: 10px 30px 10px 12px;
        flex-direction: column;
        gap: 3px;
        border-radius: 10px;
        background: #263f55;
        color: #fff;
        font-size: 0.7rem;
        line-height: 1.25;
        box-shadow: 0 8px 24px rgba(20, 39, 55, 0.34);
    }
    .workforce-plan-context-hint::after {
        position: absolute;
        right: 18px;
        bottom: -8px;
        width: 0;
        height: 0;
        border-top: 9px solid #263f55;
        border-right: 8px solid transparent;
        border-left: 8px solid transparent;
        content: '';
    }
    .workforce-plan-context-hint > button {
        position: absolute;
        top: 3px;
        right: 5px;
        width: 24px;
        height: 24px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #fff;
        font: inherit;
        font-size: 1.1rem;
        line-height: 1;
    }
    .workforce-plan-context-hint strong { font-size: 0.76rem; }
    .workforce-plan-viewport {
        height: min(68dvh, 650px);
        min-height: 360px;
        max-height: none;
        overflow: auto;
        scroll-snap-type: x proximity;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior-x: contain;
        overscroll-behavior-y: auto;
    }
    .workforce-plan-corner,
    .workforce-plan-current-head {
        left: auto;
    }
    .workforce-plan-summary-label,
    .workforce-plan-current-cell,
    .workforce-plan-cell.workforce-plan-current-cell {
        position: relative;
        z-index: 1;
        left: auto;
        box-shadow: none;
    }
    .workforce-plan-viewport--mobile-context-pinned .workforce-plan-corner {
        position: sticky;
        z-index: 18;
        left: 0;
        box-shadow: 4px 0 10px rgba(35, 67, 94, 0.18);
        transition: opacity 90ms ease;
    }
    .workforce-plan-viewport--mobile-context-pinned .workforce-plan-current-head {
        position: sticky;
        z-index: 17;
        left: var(--plan-employee-width);
        box-shadow: 5px 0 10px rgba(35, 67, 94, 0.18);
        transition: opacity 90ms ease;
    }
    .workforce-plan-viewport--mobile-context-pinned .workforce-plan-summary-label {
        position: sticky;
        z-index: 14;
        left: 0;
        box-shadow: 4px 0 8px rgba(35, 67, 94, 0.14);
        transition: opacity 90ms ease;
    }
    .workforce-plan-viewport--mobile-context-pinned .workforce-plan-summary-cell.workforce-plan-current-cell {
        position: sticky;
        z-index: 13;
        left: var(--plan-employee-width);
        box-shadow: 5px 0 8px rgba(35, 67, 94, 0.14);
        transition: opacity 90ms ease;
    }
    .workforce-plan-viewport--mobile-context-pinned >>> .workforce-plan-employee {
        position: sticky;
        z-index: 14;
        left: var(--plan-row-selector-width);
        box-shadow: 4px 0 8px rgba(35, 67, 94, 0.14);
        transition: opacity 90ms ease;
    }
    .workforce-plan-viewport--mobile-context-pinned >>> .workforce-plan-cell.workforce-plan-current-cell {
        position: sticky;
        z-index: 13;
        left: var(--plan-employee-width);
        box-shadow: 5px 0 8px rgba(35, 67, 94, 0.14);
        transition: opacity 90ms ease;
    }
    .workforce-plan-viewport--mobile-context-previewing .workforce-plan-corner,
    .workforce-plan-viewport--mobile-context-previewing .workforce-plan-current-head,
    .workforce-plan-viewport--mobile-context-previewing .workforce-plan-summary-label,
    .workforce-plan-viewport--mobile-context-previewing .workforce-plan-summary-cell.workforce-plan-current-cell,
    .workforce-plan-viewport--mobile-context-previewing >>> .workforce-plan-employee,
    .workforce-plan-viewport--mobile-context-previewing >>> .workforce-plan-cell.workforce-plan-current-cell {
        opacity: 0.62;
    }
    .workforce-plan-week-head,
    .workforce-plan-cell { scroll-snap-align: start; }
    .workforce-plan-corner { padding: 4px 50px 4px 5px; }
    .workforce-plan-corner > strong { font-size: 0.72rem; }
    .workforce-plan-corner > span { font-size: 0.56rem; }
    .workforce-plan-summary-toggle {
        right: 4px;
        height: 28px;
        padding: 0 4px;
        gap: 3px;
        font-size: 0.54rem;
    }
    .workforce-plan-current-head__filter {
        width: 38px;
        max-width: 38px;
        min-height: 18px;
        padding: 3px 2px;
        gap: 2px;
    }
    .workforce-plan-quick-editor {
        top: auto !important;
        right: 6px !important;
        bottom: calc(6px + env(safe-area-inset-bottom)) !important;
        left: 6px !important;
        width: auto;
        max-height: min(68dvh, 620px) !important;
        padding: 10px;
        border-radius: 14px 14px 8px 8px;
        box-shadow: 0 -10px 36px rgba(24, 57, 87, 0.32);
    }
    .workforce-plan-quick-editor__header { cursor: default; }
    .workforce-plan-quick-editor__drag-label > i { display: none; }
    .workforce-plan-quick-states { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .workforce-plan-quick-editor__hint { display: none; }
    .workforce-plan-legend {
        margin-top: 5px;
        padding-bottom: 2px;
        overflow-x: auto;
        flex-wrap: nowrap;
        gap: 9px;
        font-size: 0.62rem;
        white-space: nowrap;
    }
}

@keyframes workforce-plan-context-pulse {
    0%, 100% {
        filter: brightness(1);
        box-shadow: inset 0 -2px rgba(130, 73, 0, 0.2);
    }
    50% {
        filter: brightness(1.16);
        box-shadow: inset 0 0 0 3px rgba(255, 255, 255, 0.72);
    }
}

@media (prefers-reduced-motion: reduce) {
    .workforce-plan-context-fab { animation: none; }
}

@media (hover: none) and (pointer: coarse) and (max-height: 767.98px) and (orientation: landscape) {
    .workforce-plan-header {
        align-items: center;
        flex-direction: row;
    }
    .workforce-plan-title-row > h1 { font-size: 0.95rem; }
    .workforce-plan-sync { display: none; }
    .workforce-plan-header__actions { max-width: 72%; }
    .workforce-plan-toolbar { padding: 4px 6px; }
    .workforce-plan-search { width: min(240px, 34vw); }
    .workforce-plan-mobile-tools {
        width: auto;
        flex: 1 1 auto;
        order: 2;
    }
    .workforce-plan-viewport {
        height: calc(100dvh - 154px - env(safe-area-inset-bottom));
        min-height: 220px;
    }
    .workforce-plan-context-fab {
        right: max(8px, env(safe-area-inset-right));
        bottom: calc(8px + env(safe-area-inset-bottom));
    }
}
</style>

<style>
/* BootstrapVue mounts this popover outside the plan page. */
.workforce-plan-current-filter-popover {
    max-width: 360px;
}
.workforce-plan-current-filter-popover .workforce-plan-current-filter-menu {
    width: 330px;
}
.workforce-plan-current-filter-popover .workforce-plan-current-filter-menu .vs__dropdown-menu {
    width: max-content;
    min-width: 100%;
    max-width: calc(100vw - 32px);
    max-height: 280px;
    overflow-x: auto;
    font-size: 0.78rem;
}
.workforce-plan-current-filter-popover .workforce-plan-current-filter-menu .employee-filter-option {
    min-width: max-content;
    white-space: nowrap;
}
.workforce-plan-current-filter-popover .workforce-plan-current-filter-menu .employee-filter-option__label {
    white-space: nowrap;
    overflow-wrap: normal;
}
.workforce-plan-current-filter-popover .workforce-plan-current-filter-menu > small {
    display: block;
    margin-top: 8px;
    color: #718092;
    font-size: 0.68rem;
    line-height: 1.25;
}
</style>
